module.exports = [
    {
        id: "chatgpt-menu-management-overview",
        type: "guideline",
        title_i18n: {
            en: "Managing the menu through ChatGPT",
            de: "Menüverwaltung über ChatGPT",
            hu: "Menükezelés a ChatGPT-n keresztül",
            cn: "通过 ChatGPT 管理菜单"
        },
        summary_i18n: {
            en: "Once ChatGPT is connected to your restaurant, you can ask it in plain language to look up, create, change, move or delete menu items, categories and servings. Every change is previewed first and recorded in the Menu change log.",
            de: "Sobald ChatGPT mit Ihrem Restaurant verbunden ist, können Sie in normaler Sprache Produkte, Kategorien und Portionen suchen, anlegen, ändern, verschieben oder löschen lassen. Jede Änderung wird zuerst als Vorschau angezeigt und im Menu change log protokolliert.",
            hu: "Ha a ChatGPT össze van kötve az étteremmel, egyszerű nyelven kérheted tőle termékek, kategóriák és kiszerelések keresését, létrehozását, módosítását, áthelyezését vagy törlését. Minden módosítás előbb előnézetként jelenik meg, és bekerül a Menü változásnaplóba.",
            cn: "ChatGPT 连接到您的餐厅后，您可以用日常语言让它查找、创建、修改、移动或删除产品、分类和规格。每次更改都会先预览，并记录在 Menu change log 中。"
        },
        details_i18n: {
            en: "ChatGPT works on the same menu data and with the same permissions as the admin UI: it can only do what your own user may do in that restaurant. Nothing is saved before you have seen the preview and confirmed it.",
            de: "ChatGPT arbeitet mit denselben Menüdaten und denselben Berechtigungen wie die Admin-Oberfläche: Es kann nur das tun, was Ihr eigener Benutzer in diesem Restaurant tun darf. Nichts wird gespeichert, bevor Sie die Vorschau gesehen und bestätigt haben.",
            hu: "A ChatGPT ugyanazokkal a menüadatokkal és ugyanazokkal a jogosultságokkal dolgozik, mint az admin felület: csak azt teheti meg, amit a te felhasználód is megtehet az adott étteremben. Semmi nem mentődik el addig, amíg meg nem nézted az előnézetet és jóvá nem hagytad.",
            cn: "ChatGPT 使用与管理界面相同的菜单数据和相同的权限：它只能执行您本人在该餐厅中被允许的操作。在您查看预览并确认之前，不会保存任何内容。"
        },
        contentBlocks_i18n: {
            en: [
                { type: "heading", text: "What ChatGPT can do" },
                {
                    type: "bullet-list",
                    items: [
                        "Read: find menu items by name, list the items of a category, explain how an item is set up (portions and prices, recipe, add-ons, option groups, channel visibility), show the restaurant settings the menu depends on (VAT categories, courses, production lines, labels, servings) and read the Menu change log.",
                        "Create and modify menu items: names and descriptions per language, portion sizes and prices, cost, VAT, category, production lines, allergens, recipe ingredients, add-ons, option groups, availability on POS, Kiosk and Online, active or inactive.",
                        "Create and modify menu categories: names, course, VAT, production lines, availability period, add-ons and option groups offered to all items, moving under another parent category.",
                        "Create, rename and delete servings (quantity types, the units of the portions such as dl or pcs).",
                        "Delete menu items and categories. Deleted items and categories are only hidden and can be restored."
                    ]
                },
                { type: "heading", text: "Permissions" },
                {
                    type: "bullet-list",
                    items: [
                        "Creating, deleting, activating and moving menu items and categories needs the administration or products full access permission, exactly like in the admin UI.",
                        "Plain modifications (for example a new price or name) are also allowed with the stock list permission.",
                        "Servings need the administration servings add, modify or delete permission.",
                        "The permissions are checked in the restaurant that is being changed. If you manage several restaurants, tell ChatGPT which one you mean."
                    ]
                },
                { type: "heading", text: "How a change works" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT looks up the ids it needs (the item, the category, the VAT category, the serving).",
                        "2. It runs the change as a dry run and shows what would change: field, old value and new value.",
                        "3. You confirm, or ask for corrections. Only after your confirmation is the change saved.",
                        "4. The change is recorded in the Menu change log with the source ChatGPT, so you can always check who asked for what.",
                        "Changes are not pushed to POS devices and third-party channels (Wolt, Foodora) immediately; they appear with the next synchronisation."
                    ]
                },
                { type: "heading", text: "Seeing the changes in the Meals and Drinks administration" },
                {
                    type: "bullet-list",
                    items: [
                        "If you are in the Meals or Drinks administration in EatWithMe while ChatGPT works, the modifications are reflected there automatically: new, changed, moved and deleted items and categories show up without you having to reload the page.",
                        "The system lists the changes there as well: the History section of the item or category shows every change made by ChatGPT with the source ChatGPT, the time, and the old and new value of each field."
                    ]
                },
                { type: "heading", text: "Examples of requests" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Show me how the Margherita pizza is set up: portions, prices, ingredients and where it is sold.\"",
                        "\"List all items of the Desserts category with their prices.\"",
                        "\"Which VAT categories and courses does this restaurant have?\"",
                        "\"Raise the price of the 0.5 l draft beer to 1 290. Show me the change first, do not save yet.\"",
                        "\"Who changed the price of the Margherita pizza last week?\"",
                        "\"Structure my Meal ingredients.\" ChatGPT proposes a category structure for the ingredient items and, after your confirmation, creates the categories and moves the items (see the examples of creating categories and menu items)."
                    ]
                },
                { type: "heading", text: "Good to know" },
                {
                    type: "bullet-list",
                    items: [
                        "Be specific about the restaurant, the item or category and the exact new value. If something is missing or ambiguous, ChatGPT asks instead of guessing.",
                        "For many items at once, ask ChatGPT to show the whole list of changes first and confirm it in one step.",
                        "Check the result in the Menu change log or in the History section of the item."
                    ]
                }
            ],
            de: [
                { type: "heading", text: "Was ChatGPT kann" },
                {
                    type: "bullet-list",
                    items: [
                        "Lesen: Produkte nach Namen finden, die Produkte einer Kategorie auflisten, erklären, wie ein Produkt eingerichtet ist (Portionen und Preise, Rezept, Zusätze, Optionsgruppen, Sichtbarkeit in den Kanälen), die Restaurant-Einstellungen anzeigen, von denen das Menü abhängt (MwSt.-Kategorien, Gänge, Produktionslinien, Etiketten, Portionen) und das Menu change log lesen.",
                        "Produkte anlegen und ändern: Namen und Beschreibungen pro Sprache, Portionsgrößen und Preise, Kosten, MwSt., Kategorie, Produktionslinien, Allergene, Rezeptzutaten, Zusätze, Optionsgruppen, Verfügbarkeit an POS, Kiosk und Online, aktiv oder inaktiv.",
                        "Kategorien anlegen und ändern: Namen, Gang, MwSt., Produktionslinien, Verfügbarkeitszeitraum, Zusätze und Optionsgruppen für alle Produkte, Verschieben unter eine andere übergeordnete Kategorie.",
                        "Portionen (Quantity types, die Einheiten der Portionen wie dl oder Stk.) anlegen, umbenennen und löschen.",
                        "Produkte und Kategorien löschen. Gelöschte Produkte und Kategorien werden nur ausgeblendet und können wiederhergestellt werden."
                    ]
                },
                { type: "heading", text: "Berechtigungen" },
                {
                    type: "bullet-list",
                    items: [
                        "Das Anlegen, Löschen, Aktivieren und Verschieben von Produkten und Kategorien erfordert die Berechtigung Administration oder Produkte-Vollzugriff, genau wie in der Admin-Oberfläche.",
                        "Einfache Änderungen (zum Beispiel ein neuer Preis oder Name) sind auch mit der Berechtigung Lagerliste erlaubt.",
                        "Portionen erfordern die Berechtigung Administration servings add, modify oder delete.",
                        "Die Berechtigungen werden in dem Restaurant geprüft, das geändert wird. Wenn Sie mehrere Restaurants verwalten, sagen Sie ChatGPT, welches gemeint ist."
                    ]
                },
                { type: "heading", text: "So läuft eine Änderung ab" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT sucht die benötigten IDs heraus (Produkt, Kategorie, MwSt.-Kategorie, Portion).",
                        "2. Es führt die Änderung als Probelauf (dry run) aus und zeigt, was sich ändern würde: Feld, alter Wert und neuer Wert.",
                        "3. Sie bestätigen oder verlangen Korrekturen. Erst nach Ihrer Bestätigung wird die Änderung gespeichert.",
                        "4. Die Änderung wird im Menu change log mit der Quelle ChatGPT protokolliert, sodass Sie jederzeit nachvollziehen können, wer was veranlasst hat.",
                        "Änderungen werden nicht sofort an POS-Geräte und Drittkanäle (Wolt, Foodora) übertragen, sondern erscheinen bei der nächsten Synchronisierung."
                    ]
                },
                { type: "heading", text: "Änderungen in der Verwaltung von Speisen und Getränken sehen" },
                {
                    type: "bullet-list",
                    items: [
                        "Wenn Sie in EatWithMe in der Verwaltung von Speisen (Meals) oder Getränken (Drinks) sind, während ChatGPT arbeitet, werden die Änderungen dort automatisch übernommen: neue, geänderte, verschobene und gelöschte Produkte und Kategorien erscheinen, ohne dass Sie die Seite neu laden müssen.",
                        "Das System listet die Änderungen auch dort auf: Der Bereich History des Produkts oder der Kategorie zeigt jede von ChatGPT vorgenommene Änderung mit der Quelle ChatGPT, der Uhrzeit sowie dem alten und neuen Wert jedes Feldes."
                    ]
                },
                { type: "heading", text: "Beispiele für Anfragen" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Zeig mir, wie die Pizza Margherita eingerichtet ist: Portionen, Preise, Zutaten und wo sie verkauft wird.\"",
                        "\"Liste alle Produkte der Kategorie Desserts mit ihren Preisen auf.\"",
                        "\"Welche MwSt.-Kategorien und Gänge hat dieses Restaurant?\"",
                        "\"Erhöhe den Preis des Fassbiers 0,5 l auf 1 290. Zeig mir die Änderung zuerst, noch nicht speichern.\"",
                        "\"Wer hat letzte Woche den Preis der Pizza Margherita geändert?\"",
                        "\"Strukturiere meine Speisen-Zutaten.\" ChatGPT schlägt eine Kategoriestruktur für die Zutaten-Produkte vor und legt nach Ihrer Bestätigung die Kategorien an und verschiebt die Produkte (siehe die Beispiele zum Anlegen von Kategorien und Produkten)."
                    ]
                },
                { type: "heading", text: "Gut zu wissen" },
                {
                    type: "bullet-list",
                    items: [
                        "Nennen Sie genau das Restaurant, das Produkt oder die Kategorie und den neuen Wert. Fehlt etwas oder ist es mehrdeutig, fragt ChatGPT nach, statt zu raten.",
                        "Bei vielen Produkten gleichzeitig lassen Sie sich zuerst die ganze Liste der Änderungen zeigen und bestätigen sie in einem Schritt.",
                        "Prüfen Sie das Ergebnis im Menu change log oder im Bereich History des Produkts."
                    ]
                }
            ],
            hu: [
                { type: "heading", text: "Mit tud a ChatGPT" },
                {
                    type: "bullet-list",
                    items: [
                        "Olvasás: termékek keresése név alapján, egy kategória termékeinek listázása, egy termék felépítésének magyarázata (kiszerelések és árak, recept, kiegészítők, opciócsoportok, csatornánkénti láthatóság), a menü által használt éttermi beállítások megjelenítése (ÁFA kategóriák, fogások, gyártósorok, címkék, kiszerelések) és a Menü változásnapló olvasása.",
                        "Termékek létrehozása és módosítása: nevek és leírások nyelvenként, kiszerelések és árak, költség, ÁFA, kategória, gyártósorok, allergének, recept összetevők, kiegészítők, opciócsoportok, elérhetőség POS-en, Kiosk-on és Online, aktív vagy inaktív állapot.",
                        "Kategóriák létrehozása és módosítása: nevek, fogás, ÁFA, gyártósorok, elérhetőségi időszak, az összes termékhez kínált kiegészítők és opciócsoportok, áthelyezés másik szülő kategória alá.",
                        "Kiszerelések (mértékegységek, például dl vagy db) létrehozása, átnevezése és törlése.",
                        "Termékek és kategóriák törlése. A törölt termékek és kategóriák csak el vannak rejtve, és visszaállíthatók."
                    ]
                },
                { type: "heading", text: "Jogosultságok" },
                {
                    type: "bullet-list",
                    items: [
                        "Termékek és kategóriák létrehozásához, törléséhez, aktiválásához és áthelyezéséhez az adminisztráció vagy a termékek teljes hozzáférés jogosultság szükséges, pontosan úgy, mint az admin felületen.",
                        "Az egyszerű módosításokhoz (például új ár vagy név) a készletlista jogosultság is elegendő.",
                        "A kiszerelésekhez az adminisztráció kiszerelések hozzáadás, módosítás vagy törlés jogosultság kell.",
                        "A jogosultságokat abban az étteremben ellenőrzi a rendszer, amelyet módosítasz. Ha több éttermet kezelsz, mondd meg a ChatGPT-nek, melyikre gondolsz."
                    ]
                },
                { type: "heading", text: "Hogyan történik egy módosítás" },
                {
                    type: "bullet-list",
                    items: [
                        "1. A ChatGPT megkeresi a szükséges azonosítókat (termék, kategória, ÁFA kategória, kiszerelés).",
                        "2. Próbafuttatásként (dry run) elvégzi a módosítást, és megmutatja, mi változna: mező, régi érték és új érték.",
                        "3. Te jóváhagyod, vagy javítást kérsz. A módosítás csak a jóváhagyás után mentődik.",
                        "4. A módosítás ChatGPT forrással bekerül a Menü változásnaplóba, így bármikor ellenőrizhető, ki mit kért.",
                        "A módosítások nem kerülnek azonnal át a POS eszközökre és a külső csatornákra (Wolt, Foodora), hanem a következő szinkronizáláskor jelennek meg."
                    ]
                },
                { type: "heading", text: "A módosítások megjelenése az Étel és Ital adminisztrációban" },
                {
                    type: "bullet-list",
                    items: [
                        "Ha az EatWithMe-ben az Étel vagy Ital adminisztrációban vagy, miközben a ChatGPT dolgozik, a módosítások ott automatikusan megjelennek: az új, módosított, áthelyezett és törölt termékek és kategóriák az oldal újratöltése nélkül láthatóvá válnak.",
                        "A rendszer a módosításokat ott is listázza: a termék vagy kategória Előzmények része minden ChatGPT által végzett módosítást megmutat ChatGPT forrással, az időponttal, valamint minden mező régi és új értékével."
                    ]
                },
                { type: "heading", text: "Példák kérésekre" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Mutasd meg, hogyan van beállítva a Margherita pizza: kiszerelések, árak, összetevők és hol árulják.\"",
                        "\"Listázd a Desszertek kategória összes termékét az áraikkal.\"",
                        "\"Milyen ÁFA kategóriái és fogásai vannak ennek az étteremnek?\"",
                        "\"Emeld a 0,5 literes csapolt sör árát 1 290-re. Előbb mutasd meg a változást, még ne mentsd.\"",
                        "\"Ki módosította a Margherita pizza árát a múlt héten?\"",
                        "\"Strukturáld az Étel alapanyagaimat.\" A ChatGPT kategóriastruktúrát javasol az alapanyag termékeknek, és a jóváhagyásod után létrehozza a kategóriákat, és áthelyezi a termékeket (lásd a kategóriák és termékek létrehozásáról szóló példákat)."
                    ]
                },
                { type: "heading", text: "Jó tudni" },
                {
                    type: "bullet-list",
                    items: [
                        "Pontosan add meg az éttermet, a terméket vagy kategóriát és az új értéket. Ha valami hiányzik vagy félreérthető, a ChatGPT rákérdez, nem találgat.",
                        "Sok termék egyidejű módosításánál kérd, hogy a ChatGPT előbb mutassa meg a teljes változáslistát, és egy lépésben hagyd jóvá.",
                        "Az eredményt ellenőrizd a Menü változásnaplóban vagy a termék Előzmények részében."
                    ]
                }
            ],
            cn: [
                { type: "heading", text: "ChatGPT 可以做什么" },
                {
                    type: "bullet-list",
                    items: [
                        "读取：按名称查找产品、列出某个分类下的产品、说明产品的设置（规格和价格、配方、附加项、选项组、各渠道可见性）、显示菜单依赖的餐厅设置（增值税类别、上菜顺序、生产线、标签、规格），以及读取 Menu change log。",
                        "创建和修改产品：各语言的名称和描述、规格和价格、成本、增值税、分类、生产线、过敏原、配方原料、附加项、选项组、在 POS、Kiosk 和在线的可用性、启用或停用。",
                        "创建和修改分类：名称、上菜顺序、增值税、生产线、可用时间段、提供给所有产品的附加项和选项组，以及移动到其他父分类下。",
                        "创建、重命名和删除规格（Quantity type，即 dl 或 pcs 等规格的单位）。",
                        "删除产品和分类。已删除的产品和分类只是被隐藏，可以恢复。"
                    ]
                },
                { type: "heading", text: "权限" },
                {
                    type: "bullet-list",
                    items: [
                        "创建、删除、启用和移动产品及分类需要管理或产品完全访问权限，与管理界面完全一致。",
                        "普通修改（例如新价格或新名称）拥有库存列表权限也可以。",
                        "规格需要管理规格的添加、修改或删除权限。",
                        "权限在被修改的餐厅中检查。如果您管理多家餐厅，请告诉 ChatGPT 是哪一家。"
                    ]
                },
                { type: "heading", text: "更改如何进行" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT 先查找所需的编号（产品、分类、增值税类别、规格）。",
                        "2. 它以试运行（dry run）方式执行更改，并显示将发生的变化：字段、旧值和新值。",
                        "3. 您确认或要求修改。只有在您确认后才会保存更改。",
                        "4. 更改会以来源 ChatGPT 记录在 Menu change log 中，因此您随时可以核查谁提出了什么要求。",
                        "更改不会立即推送到 POS 设备和第三方渠道（Wolt、Foodora），而是在下一次同步时生效。"
                    ]
                },
                { type: "heading", text: "在菜品和饮品管理中查看更改" },
                {
                    type: "bullet-list",
                    items: [
                        "如果在 ChatGPT 操作期间，您正位于 EatWithMe 的菜品（Meals）或饮品（Drinks）管理中，更改会在那里自动反映：新建、修改、移动和删除的产品及分类无需刷新页面即可显示。",
                        "系统也会在那里列出这些更改：产品或分类的 History 部分会显示 ChatGPT 所做的每项更改，包括来源 ChatGPT、时间以及每个字段的旧值和新值。"
                    ]
                },
                { type: "heading", text: "请求示例" },
                {
                    type: "bullet-list",
                    items: [
                        "“告诉我玛格丽特披萨是如何设置的：规格、价格、原料以及在哪里销售。”",
                        "“列出甜点分类下所有产品及其价格。”",
                        "“这家餐厅有哪些增值税类别和上菜顺序？”",
                        "“把 0.5 升生啤的价格调到 1 290。先给我看更改，先不要保存。”",
                        "“上周谁修改了玛格丽特披萨的价格？”",
                        "“整理我的菜品原料结构。”ChatGPT 会为原料产品提出分类结构，并在您确认后创建分类并移动产品（参见创建分类和产品的示例）。"
                    ]
                },
                { type: "heading", text: "注意事项" },
                {
                    type: "bullet-list",
                    items: [
                        "请明确说明餐厅、产品或分类以及确切的新值。如果信息缺失或有歧义，ChatGPT 会询问而不是猜测。",
                        "一次修改很多产品时，请让 ChatGPT 先显示完整的更改列表，并一次性确认。",
                        "可在 Menu change log 或产品的 History 部分核对结果。"
                    ]
                }
            ]
        },
        createdAt: "2026-11-01T09:00:00Z",
        modifiedAt: "2026-11-01T09:00:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: ["administration.products.fullaccess"],
        tags: ["chatgpt", "menu", "ai", "permissions", "dry-run"]
    },
    {
        id: "chatgpt-menu-create-examples",
        type: "guideline",
        title_i18n: {
            en: "Creating categories and menu items with ChatGPT: examples",
            de: "Kategorien und Produkte mit ChatGPT anlegen: Beispiele",
            hu: "Kategóriák és termékek létrehozása a ChatGPT-vel: példák",
            cn: "使用 ChatGPT 创建分类和产品：示例"
        },
        summary_i18n: {
            en: "Step-by-step examples for building a menu with ChatGPT: a top level category, subcategories, meals, drinks with several portion sizes and option groups.",
            de: "Schritt-für-Schritt-Beispiele für den Aufbau eines Menüs mit ChatGPT: Hauptkategorie, Unterkategorien, Speisen, Getränke mit mehreren Portionsgrößen und Optionsgruppen.",
            hu: "Lépésről lépésre példák menü építésére a ChatGPT-vel: főkategória, alkategóriák, ételek, több kiszerelésű italok és opciócsoportok.",
            cn: "使用 ChatGPT 搭建菜单的分步示例：顶级分类、子分类、菜品、多种规格的饮品以及选项组。"
        },
        details_i18n: {
            en: "A new menu item is always sold through its portions, so it needs at least one portion with a price, and it needs a VAT category (its own or inherited from its category). A new top level category needs a course and a VAT category. Subcategories and items inherit the course, VAT and production lines from their category, so set them once on the highest category where they are valid.",
            de: "Ein neues Produkt wird immer über seine Portionen verkauft, daher braucht es mindestens eine Portion mit Preis sowie eine MwSt.-Kategorie (eigene oder von der Kategorie geerbte). Eine neue Hauptkategorie braucht einen Gang und eine MwSt.-Kategorie. Unterkategorien und Produkte erben Gang, MwSt. und Produktionslinien von ihrer Kategorie; legen Sie sie daher einmal in der höchsten Kategorie fest, in der sie gelten.",
            hu: "Egy új termék mindig a kiszerelésein keresztül kerül értékesítésre, ezért legalább egy árral rendelkező kiszerelés és egy ÁFA kategória (saját vagy a kategóriájától örökölt) szükséges hozzá. Egy új főkategóriához fogás és ÁFA kategória kell. Az alkategóriák és termékek öröklik a fogást, az ÁFA-t és a gyártósorokat a kategóriájuktól, ezért ezeket egyszer, a legmagasabb olyan kategórián kell beállítani, ahol érvényesek.",
            cn: "新产品总是通过其规格销售，因此至少需要一个带价格的规格，并需要一个增值税类别（自身设置或从分类继承）。新的顶级分类需要上菜顺序和增值税类别。子分类和产品会从其分类继承上菜顺序、增值税和生产线，因此只需在适用的最高层分类上设置一次。"
        },
        contentBlocks_i18n: {
            en: [
                { type: "heading", text: "Example 1: a category with subcategories" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"In the Main restaurant create a top level meal category Soups, course starter, VAT 27%, takeaway VAT 5%, production line Kitchen. Under it create the subcategories Hot soups and Cold soups.\"",
                        "ChatGPT looks up the VAT categories and production lines, previews the category and, after your confirmation, creates it.",
                        "The subcategories do not need their own course or VAT: they inherit them from Soups.",
                        "If you do not name a VAT category, ChatGPT lists the available ones and asks you to choose."
                    ]
                },
                { type: "heading", text: "Example 2: a meal with one portion" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"Add Goulash soup to Hot soups: one portion, price 2 490, preselected on the POS, allergens celery and gluten.\"",
                        "The portion is created with quantity 1, price 2 490 and marked as the default. The item takes VAT, course and production line from its category.",
                        "Add \"with names Gulyásleves in Hungarian and Goulash soup in English\" to store the name per language."
                    ]
                },
                { type: "heading", text: "Example 3: a drink with several portion sizes" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"Create the drink Draft beer in the category Beers with two portions: 0.3 l for 790 and 0.5 l for 1 190, the 0.5 l is the default.\"",
                        "The unit of the portions (for example l or dl) is a serving. If the restaurant has no suitable one, ChatGPT proposes creating it first.",
                        "Each portion can also have its own cost and its own visibility on POS, Kiosk and Online."
                    ]
                },
                { type: "heading", text: "Example 4: a recipe with ingredients and add-ons" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"Margherita pizza needs 0.2 mozzarella and 0.15 tomato sauce as mandatory ingredients, and offers extra cheese as an optional add-on.\"",
                        "Ingredients and add-ons are other menu items; ChatGPT looks up their ids first and shows the rows it would add (mandatory, included, optional or upsell).",
                        "The recipe is used by the stock management to calculate the consumption."
                    ]
                },
                { type: "heading", text: "Example 5: option groups shared by a whole category" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"Create an option category Toppings with the items Corn, Olives and Mushrooms, and offer it with every item of the category Pizzas, up to 3 choices.\"",
                        "Option categories are created under one top level container category named Options (Opciók in Hungarian). ChatGPT reuses it if it exists and creates it otherwise.",
                        "The option group is set once on the category Pizzas, so every pizza inherits it. Set it on a single item only if that item is different."
                    ]
                },
                { type: "heading", text: "Example 6: structuring your ingredients" },
                {
                    type: "bullet-list",
                    items: [
                        "Request: \"Structure my Meal ingredients.\" (in Hungarian: \"Strukturáld az Étel alapanyagaimat\").",
                        "ChatGPT reads the menu, finds the items that are used as ingredients and add-ons in recipes, and proposes a structure, for example an Ingredients category with the subcategories Meat, Vegetables, Dairy, Spices and Dry goods.",
                        "You first get the proposal: which categories would be created and which item would be moved where. Nothing is changed until you confirm. You can adjust it: \"Put the herbs under Spices\" or \"Do only the vegetables\".",
                        "After your confirmation the categories are created and the items are moved. The items keep their identity, so the recipes, add-ons and stock records that use them keep working; only their place in the menu changes.",
                        "A moved item takes the VAT, course and production lines of its new category unless it has its own. Set these once on the top level Ingredients category and check them in the preview."
                    ]
                },
                { type: "heading", text: "Tips" },
                {
                    type: "bullet-list",
                    items: [
                        "A top level category always needs a course and a VAT category; do not repeat them on every subcategory or item.",
                        "Always check the preview: it lists every field that will be created with its value.",
                        "Newly created items are not pushed to POS devices and third-party channels at once; they appear with the next synchronisation."
                    ]
                }
            ],
            de: [
                { type: "heading", text: "Beispiel 1: eine Kategorie mit Unterkategorien" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Lege im Restaurant Hauptlokal eine Hauptkategorie Suppen (Speisen) an, Gang Vorspeise, MwSt. 27 %, Außer-Haus-MwSt. 5 %, Produktionslinie Küche. Darunter die Unterkategorien Warme Suppen und Kalte Suppen.\"",
                        "ChatGPT sucht die MwSt.-Kategorien und Produktionslinien heraus, zeigt die Kategorie als Vorschau und legt sie nach Ihrer Bestätigung an.",
                        "Die Unterkategorien brauchen keinen eigenen Gang und keine eigene MwSt.: Sie erben sie von Suppen.",
                        "Nennen Sie keine MwSt.-Kategorie, listet ChatGPT die verfügbaren auf und bittet Sie um eine Auswahl."
                    ]
                },
                { type: "heading", text: "Beispiel 2: eine Speise mit einer Portion" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Füge Gulaschsuppe zu Warme Suppen hinzu: eine Portion, Preis 2 490, am POS vorausgewählt, Allergene Sellerie und Gluten.\"",
                        "Die Portion wird mit Menge 1 und Preis 2 490 angelegt und als Standard markiert. Das Produkt übernimmt MwSt., Gang und Produktionslinie von seiner Kategorie.",
                        "Ergänzen Sie \"mit den Namen Gulyásleves auf Ungarisch und Goulash soup auf Englisch\", um den Namen pro Sprache zu speichern."
                    ]
                },
                { type: "heading", text: "Beispiel 3: ein Getränk mit mehreren Portionsgrößen" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Lege das Getränk Fassbier in der Kategorie Biere mit zwei Portionen an: 0,3 l für 790 und 0,5 l für 1 190, die 0,5 l ist der Standard.\"",
                        "Die Einheit der Portionen (zum Beispiel l oder dl) ist eine Portion (Serving). Gibt es im Restaurant keine passende, schlägt ChatGPT vor, sie zuerst anzulegen.",
                        "Jede Portion kann außerdem eigene Kosten und eine eigene Sichtbarkeit an POS, Kiosk und Online haben."
                    ]
                },
                { type: "heading", text: "Beispiel 4: ein Rezept mit Zutaten und Zusätzen" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Die Pizza Margherita braucht 0,2 Mozzarella und 0,15 Tomatensauce als Pflichtzutaten und bietet Extra-Käse als optionalen Zusatz.\"",
                        "Zutaten und Zusätze sind andere Produkte; ChatGPT sucht zuerst deren IDs und zeigt die Zeilen, die es hinzufügen würde (mandatory, included, optional oder upsell).",
                        "Das Rezept wird von der Lagerverwaltung zur Berechnung des Verbrauchs verwendet."
                    ]
                },
                { type: "heading", text: "Beispiel 5: Optionsgruppen für eine ganze Kategorie" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Lege eine Optionskategorie Toppings mit den Produkten Mais, Oliven und Champignons an und biete sie bei jedem Produkt der Kategorie Pizzen an, bis zu 3 Auswahlen.\"",
                        "Optionskategorien werden unter einer Hauptkategorie als Container namens Options (auf Ungarisch Opciók) angelegt. ChatGPT verwendet sie weiter, falls vorhanden, und legt sie sonst an.",
                        "Die Optionsgruppe wird einmal an der Kategorie Pizzen festgelegt, sodass jede Pizza sie erbt. Legen Sie sie nur dann an einem einzelnen Produkt fest, wenn dieses abweicht."
                    ]
                },
                { type: "heading", text: "Beispiel 6: Zutaten strukturieren" },
                {
                    type: "bullet-list",
                    items: [
                        "Anfrage: \"Strukturiere meine Speisen-Zutaten.\" (auf Ungarisch: \"Strukturáld az Étel alapanyagaimat\").",
                        "ChatGPT liest das Menü, findet die Produkte, die in Rezepten als Zutaten und Zusätze verwendet werden, und schlägt eine Struktur vor, zum Beispiel eine Kategorie Zutaten mit den Unterkategorien Fleisch, Gemüse, Milchprodukte, Gewürze und Trockenwaren.",
                        "Sie erhalten zuerst den Vorschlag: welche Kategorien angelegt und welche Produkte wohin verschoben würden. Nichts wird geändert, bevor Sie bestätigen. Sie können ihn anpassen: \"Setze die Kräuter unter Gewürze\" oder \"Mache nur das Gemüse\".",
                        "Nach Ihrer Bestätigung werden die Kategorien angelegt und die Produkte verschoben. Die Produkte behalten ihre Identität, daher funktionieren Rezepte, Zusätze und Lagerdaten, die sie verwenden, weiter; nur ihre Stelle im Menü ändert sich.",
                        "Ein verschobenes Produkt übernimmt MwSt., Gang und Produktionslinien seiner neuen Kategorie, sofern es keine eigenen hat. Legen Sie diese einmal an der Hauptkategorie Zutaten fest und prüfen Sie sie in der Vorschau."
                    ]
                },
                { type: "heading", text: "Tipps" },
                {
                    type: "bullet-list",
                    items: [
                        "Eine Hauptkategorie braucht immer einen Gang und eine MwSt.-Kategorie; wiederholen Sie diese nicht bei jeder Unterkategorie oder jedem Produkt.",
                        "Prüfen Sie immer die Vorschau: Sie listet jedes Feld auf, das mit seinem Wert angelegt wird.",
                        "Neu angelegte Produkte werden nicht sofort an POS-Geräte und Drittkanäle übertragen, sondern erscheinen bei der nächsten Synchronisierung."
                    ]
                }
            ],
            hu: [
                { type: "heading", text: "1. példa: kategória alkategóriákkal" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"A Fő étteremben hozz létre egy Levesek étel főkategóriát, előétel fogással, 27% ÁFÁ-val, 5% elvitel ÁFÁ-val, Konyha gyártósorral. Alatta hozd létre a Meleg levesek és Hideg levesek alkategóriákat.\"",
                        "A ChatGPT megkeresi az ÁFA kategóriákat és gyártósorokat, előnézetben megmutatja a kategóriát, és a jóváhagyásod után létrehozza.",
                        "Az alkategóriáknak nem kell saját fogás vagy ÁFA: ezeket a Levesek kategóriától öröklik.",
                        "Ha nem nevezel meg ÁFA kategóriát, a ChatGPT kilistázza az elérhetőket, és kéri, hogy válassz."
                    ]
                },
                { type: "heading", text: "2. példa: étel egy kiszeréléssel" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"Add hozzá a Gulyásleves terméket a Meleg levesek kategóriához: egy adag, ára 2 490, a POS-en előre kiválasztva, allergének zeller és glutén.\"",
                        "A kiszerelés 1 mennyiséggel, 2 490 árral jön létre, és alapértelmezettként van megjelölve. A termék az ÁFA-t, a fogást és a gyártósort a kategóriájától veszi át.",
                        "Add hozzá: \"a nevek: Gulyásleves magyarul és Goulash soup angolul\", hogy a név nyelvenként tárolódjon."
                    ]
                },
                { type: "heading", text: "3. példa: ital több kiszereléssel" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"Hozd létre a Csapolt sör italt a Sörök kategóriában két kiszereléssel: 0,3 l 790-ért és 0,5 l 1 190-ért, az 0,5 l az alapértelmezett.\"",
                        "A kiszerelések mértékegysége (például l vagy dl) egy kiszerelés (serving). Ha az étteremben nincs megfelelő, a ChatGPT javasolja, hogy előbb hozza létre.",
                        "Minden kiszerelésnek lehet saját költsége és saját láthatósága a POS-en, Kiosk-on és Online."
                    ]
                },
                { type: "heading", text: "4. példa: recept összetevőkkel és kiegészítőkkel" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"A Margherita pizzához kötelező összetevőként kell 0,2 mozzarella és 0,15 paradicsomszósz, és opcionális kiegészítőként extra sajtot kínál.\"",
                        "Az összetevők és kiegészítők más termékek; a ChatGPT előbb megkeresi az azonosítóikat, és megmutatja a hozzáadandó sorokat (mandatory, included, optional vagy upsell).",
                        "A receptet a készletkezelés használja a fogyás kiszámításához."
                    ]
                },
                { type: "heading", text: "5. példa: egy egész kategóriára érvényes opciócsoport" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"Hozz létre egy Feltétek opciókategóriát Kukorica, Olívabogyó és Gomba termékekkel, és kínáld a Pizzák kategória minden termékéhez, legfeljebb 3 választás.\"",
                        "Az opciókategóriák egy Opciók nevű főkategória-tárolóban jönnek létre. A ChatGPT újra felhasználja, ha már létezik, egyébként létrehozza.",
                        "Az opciócsoport egyszer, a Pizzák kategórián van beállítva, így minden pizza örökli. Egyetlen terméken csak akkor állítsd be, ha az eltér a többitől."
                    ]
                },
                { type: "heading", text: "6. példa: az alapanyagok strukturálása" },
                {
                    type: "bullet-list",
                    items: [
                        "Kérés: \"Strukturáld az Étel alapanyagaimat.\"",
                        "A ChatGPT beolvassa a menüt, megkeresi azokat a termékeket, amelyeket a receptekben összetevőként és kiegészítőként használnak, és struktúrát javasol, például egy Alapanyagok kategóriát Hús, Zöldség, Tejtermékek, Füszerek és Száraz áruk alkategóriákkal.",
                        "Először a javaslatot kapod meg: mely kategóriák jönnének létre, és melyik termék hová kerülne. Semmi nem változik, amíg jóvá nem hagyod. Finomíthatod: \"A fűszernövények a Füszerek alá kerüljenek\" vagy \"Csak a zöldségeket csináld meg\".",
                        "A jóváhagyásod után a kategóriák létrejönnek, a termékek áthelyezésre kerülnek. A termékek azonosítója megmarad, ezért a receptek, kiegészítők és készletnyilvántartások, amelyek használják őket, továbbra is működnek; csak a helyük változik a menüben.",
                        "Az áthelyezett termék az új kategóriája ÁFA-ját, fogását és gyártósorait veszi át, ha nincs sajátja. Ezeket egyszer, a legfelső Alapanyagok kategórián állítsd be, és ellenőrizd az előnézetben."
                    ]
                },
                { type: "heading", text: "Tippek" },
                {
                    type: "bullet-list",
                    items: [
                        "Egy főkategóriához mindig kell fogás és ÁFA kategória; ne ismételd meg minden alkategórián vagy terméken.",
                        "Mindig nézd meg az előnézetet: felsorol minden létrehozandó mezőt az értékével.",
                        "Az újonnan létrehozott termékek nem kerülnek azonnal át a POS eszközökre és a külső csatornákra, hanem a következő szinkronizáláskor jelennek meg."
                    ]
                }
            ],
            cn: [
                { type: "heading", text: "示例 1：带子分类的分类" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“在主餐厅创建一个顶级菜品分类“汤”，上菜顺序为前菜，增值税 27%，外带增值税 5%，生产线为厨房。在其下创建子分类“热汤”和“冷汤”。”",
                        "ChatGPT 会查找增值税类别和生产线，预览该分类，并在您确认后创建。",
                        "子分类不需要自己的上菜顺序或增值税：它们从“汤”继承。",
                        "如果您没有指定增值税类别，ChatGPT 会列出可用的类别并请您选择。"
                    ]
                },
                { type: "heading", text: "示例 2：只有一个规格的菜品" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“在“热汤”中添加牛肉汤：一份，价格 2 490，在 POS 上默认选中，过敏原为芹菜和麸质。”",
                        "规格以数量 1、价格 2 490 创建，并标记为默认。产品从其分类获取增值税、上菜顺序和生产线。",
                        "可补充“名称：匈牙利语 Gulyásleves，英语 Goulash soup”，以按语言保存名称。"
                    ]
                },
                { type: "heading", text: "示例 3：有多种规格的饮品" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“在“啤酒”分类中创建饮品“生啤”，有两个规格：0.3 升 790，0.5 升 1 190，0.5 升为默认。”",
                        "规格的单位（例如 l 或 dl）是一个 Serving。如果餐厅没有合适的单位，ChatGPT 会建议先创建。",
                        "每个规格还可以有自己的成本以及在 POS、Kiosk 和在线的可见性。"
                    ]
                },
                { type: "heading", text: "示例 4：带原料和附加项的配方" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“玛格丽特披萨的必需原料为 0.2 马苏里拉和 0.15 番茄酱，并提供额外奶酪作为可选附加项。”",
                        "原料和附加项是其他产品；ChatGPT 会先查找它们的编号，并显示将要添加的行（mandatory、included、optional 或 upsell）。",
                        "库存管理使用配方来计算消耗量。"
                    ]
                },
                { type: "heading", text: "示例 5：整个分类共用的选项组" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“创建选项分类“配料”，包含玉米、橄榄和蘑菇，并提供给“披萨”分类的每个产品，最多选 3 项。”",
                        "选项分类创建在名为 Options（匈牙利语 Opciók）的顶级容器分类下。如果已存在，ChatGPT 会重复使用，否则会创建。",
                        "选项组只在“披萨”分类上设置一次，所有披萨都会继承。只有当某个产品与众不同时，才在单个产品上设置。"
                    ]
                },
                { type: "heading", text: "示例 6：整理原料结构" },
                {
                    type: "bullet-list",
                    items: [
                        "请求：“整理我的菜品原料结构。”（匠牙利语：“Strukturáld az Étel alapanyagaimat”）",
                        "ChatGPT 会读取菜单，找出在配方中作为原料和附加项使用的产品，并提出结构，例如“原料”分类下设“肉类”、“蔬菜”、“乳制品”、“香料”和“干货”子分类。",
                        "您首先会看到方案：将创建哪些分类、哪些产品将移到哪里。在您确认之前不会做任何更改。您可以调整：“把香草放到香料下”或“只整理蔬菜”。",
                        "确认后会创建分类并移动产品。产品的标识保持不变，因此使用它们的配方、附加项和库存记录仍然正常工作；变化的只是它们在菜单中的位置。",
                        "被移动的产品会采用新分类的增值税、上菜顺序和生产线，除非它有自己的设置。请在顶级“原料”分类上一次性设置这些，并在预览中检查。"
                    ]
                },
                { type: "heading", text: "提示" },
                {
                    type: "bullet-list",
                    items: [
                        "顶级分类始终需要上菜顺序和增值税类别；不要在每个子分类或产品上重复设置。",
                        "务必检查预览：它列出将创建的每个字段及其值。",
                        "新创建的产品不会立即推送到 POS 设备和第三方渠道，而是在下一次同步时生效。"
                    ]
                }
            ]
        },
        createdAt: "2026-11-01T09:05:00Z",
        modifiedAt: "2026-11-01T09:05:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: ["administration.products.fullaccess"],
        tags: ["chatgpt", "menu", "ai", "examples", "create"]
    },
    {
        id: "chatgpt-menu-modify-examples",
        type: "guideline",
        title_i18n: {
            en: "Changing and deleting menu items with ChatGPT: examples",
            de: "Produkte mit ChatGPT ändern und löschen: Beispiele",
            hu: "Termékek módosítása és törlése a ChatGPT-vel: példák",
            cn: "使用 ChatGPT 修改和删除产品：示例"
        },
        summary_i18n: {
            en: "Examples of everyday menu changes through ChatGPT: prices, names, visibility, moving, servings, deleting and restoring, and checking who changed what.",
            de: "Beispiele für alltägliche Menüänderungen über ChatGPT: Preise, Namen, Sichtbarkeit, Verschieben, Portionen, Löschen und Wiederherstellen sowie die Kontrolle, wer was geändert hat.",
            hu: "Példák mindennapi menümódosításokra a ChatGPT-n keresztül: árak, nevek, láthatóság, áthelyezés, kiszerelések, törlés és visszaállítás, valamint annak ellenőrzése, ki mit módosított.",
            cn: "通过 ChatGPT 进行日常菜单更改的示例：价格、名称、可见性、移动、规格、删除与恢复，以及核查谁修改了什么。"
        },
        details_i18n: {
            en: "Only the fields you mention are changed, everything else stays as it is. For every change ChatGPT first shows the preview with the old and new value of each field and waits for your confirmation. Deleting and bigger changes should always be confirmed explicitly.",
            de: "Es werden nur die von Ihnen genannten Felder geändert, alles andere bleibt unverändert. Bei jeder Änderung zeigt ChatGPT zuerst die Vorschau mit altem und neuem Wert jedes Feldes und wartet auf Ihre Bestätigung. Löschungen und größere Änderungen sollten immer ausdrücklich bestätigt werden.",
            hu: "Csak azok a mezők módosulnak, amelyeket megemlítesz, minden más változatlan marad. Minden módosításnál a ChatGPT előbb megmutatja az előnézetet minden mező régi és új értékével, és megvárja a jóváhagyásodat. A törlést és a nagyobb módosításokat mindig kifejezetten hagyd jóvá.",
            cn: "只会修改您提到的字段，其余内容保持不变。对于每次更改，ChatGPT 都会先显示每个字段旧值和新值的预览，并等待您确认。删除和较大的更改应始终明确确认。"
        },
        contentBlocks_i18n: {
            en: [
                { type: "heading", text: "Prices and portions" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Raise the price of the 0.5 l draft beer from 1 190 to 1 290.\" Only the price of that portion changes.",
                        "\"Increase all items of the category Pizzas by 10% and round to 10.\" ChatGPT lists every item with its old and new price and you confirm the whole list at once.",
                        "\"Add a 1 l portion for 2 190 to the draft beer and remove the 0.3 l portion.\"",
                        "\"Set the cost of the Margherita pizza to 650.\""
                    ]
                },
                { type: "heading", text: "Names, descriptions and allergens" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Rename Goulash soup to Beef goulash soup in English, keep the Hungarian name.\" The other languages are kept.",
                        "\"Add the description 'Served with fresh bread' to the Goulash soup.\"",
                        "\"The Carbonara contains gluten, egg and milk. Set these allergens.\" The allergen list is replaced by the complete list you give."
                    ]
                },
                { type: "heading", text: "Availability and channels" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Hide the Lobster from the Kiosk and Online, keep it on the POS.\"",
                        "\"Deactivate the category Summer specials.\" The items below it are not shown while the category is inactive; activate it again with \"Activate the category Summer specials\".",
                        "\"Make the Brunch category available only between 2026-12-01 and 2026-12-31.\""
                    ]
                },
                { type: "heading", text: "Moving and ordering" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Move the Tiramisu from Cakes to Desserts.\"",
                        "\"Move the category Hot soups under Starters.\"",
                        "\"Put the Goulash soup to the first position of its category.\""
                    ]
                },
                { type: "heading", text: "Servings" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Create a serving called bottle.\"",
                        "\"Rename the serving dl to deciliter, in English only.\" Every item that uses it shows the new name.",
                        "\"Delete the serving glass.\" A serving that is still used by portions of any item cannot be deleted; ChatGPT tells how many portions use it, and you can first move them to another serving."
                    ]
                },
                { type: "heading", text: "Deleting and restoring" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Delete the item Old special.\" ChatGPT shows what will be deleted and asks you to confirm. The item is only hidden and can be restored: \"Restore the item Old special.\"",
                        "\"Delete the category Winter menu.\" The subcategories and items below it disappear from the menu too; the preview tells how many. They return when the category is restored.",
                        "Servings are deleted permanently and cannot be restored."
                    ]
                },
                { type: "heading", text: "Checking the changes" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Who changed the price of the Margherita pizza last week?\" or \"What did ChatGPT change in the menu today?\"",
                        "The Menu change log (Reports > Menu change log) shows every change made through ChatGPT with the source ChatGPT, with the old and new value of every field. The History section of the item shows the changes of a single item."
                    ]
                }
            ],
            de: [
                { type: "heading", text: "Preise und Portionen" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Erhöhe den Preis des Fassbiers 0,5 l von 1 190 auf 1 290.\" Es ändert sich nur der Preis dieser Portion.",
                        "\"Erhöhe alle Produkte der Kategorie Pizzen um 10 % und runde auf 10.\" ChatGPT listet jedes Produkt mit altem und neuem Preis auf, und Sie bestätigen die ganze Liste auf einmal.",
                        "\"Füge dem Fassbier eine Portion 1 l für 2 190 hinzu und entferne die Portion 0,3 l.\"",
                        "\"Setze die Kosten der Pizza Margherita auf 650.\""
                    ]
                },
                { type: "heading", text: "Namen, Beschreibungen und Allergene" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Benenne Gulaschsuppe auf Englisch in Beef goulash soup um, den ungarischen Namen behalten.\" Die anderen Sprachen bleiben erhalten.",
                        "\"Füge der Gulaschsuppe die Beschreibung 'Mit frischem Brot serviert' hinzu.\"",
                        "\"Die Carbonara enthält Gluten, Ei und Milch. Setze diese Allergene.\" Die Allergenliste wird durch die vollständige von Ihnen genannte Liste ersetzt."
                    ]
                },
                { type: "heading", text: "Verfügbarkeit und Kanäle" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Blende den Hummer im Kiosk und Online aus, am POS lassen.\"",
                        "\"Deaktiviere die Kategorie Sommerspezialitäten.\" Die Produkte darunter werden nicht angezeigt, solange die Kategorie inaktiv ist; mit \"Aktiviere die Kategorie Sommerspezialitäten\" wird sie wieder aktiviert.",
                        "\"Mache die Kategorie Brunch nur zwischen 2026-12-01 und 2026-12-31 verfügbar.\""
                    ]
                },
                { type: "heading", text: "Verschieben und Reihenfolge" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Verschiebe das Tiramisu von Kuchen nach Desserts.\"",
                        "\"Verschiebe die Kategorie Warme Suppen unter Vorspeisen.\"",
                        "\"Setze die Gulaschsuppe an die erste Stelle ihrer Kategorie.\""
                    ]
                },
                { type: "heading", text: "Portionen" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Lege eine Portion namens Flasche an.\"",
                        "\"Benenne die Portion dl in Deziliter um, nur auf Englisch.\" Jedes Produkt, das sie verwendet, zeigt den neuen Namen.",
                        "\"Lösche die Portion Glas.\" Eine Portion, die noch von Portionen eines Produkts verwendet wird, kann nicht gelöscht werden; ChatGPT nennt die Anzahl der verwendenden Portionen, und Sie können diese zuerst einer anderen Portion zuordnen."
                    ]
                },
                { type: "heading", text: "Löschen und Wiederherstellen" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Lösche das Produkt Altes Special.\" ChatGPT zeigt, was gelöscht wird, und bittet um Bestätigung. Das Produkt wird nur ausgeblendet und kann wiederhergestellt werden: \"Stelle das Produkt Altes Special wieder her.\"",
                        "\"Lösche die Kategorie Wintermenü.\" Die Unterkategorien und Produkte darunter verschwinden ebenfalls aus dem Menü; die Vorschau nennt die Anzahl. Sie kommen zurück, wenn die Kategorie wiederhergestellt wird.",
                        "Portionen werden dauerhaft gelöscht und können nicht wiederhergestellt werden."
                    ]
                },
                { type: "heading", text: "Änderungen kontrollieren" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Wer hat letzte Woche den Preis der Pizza Margherita geändert?\" oder \"Was hat ChatGPT heute im Menü geändert?\"",
                        "Das Menu change log (Berichte > Menu change log) zeigt jede über ChatGPT vorgenommene Änderung mit der Quelle ChatGPT sowie mit altem und neuem Wert jedes Feldes. Der Bereich History des Produkts zeigt die Änderungen eines einzelnen Produkts."
                    ]
                }
            ],
            hu: [
                { type: "heading", text: "Árak és kiszerelések" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Emeld a 0,5 literes csapolt sör árát 1 190-ről 1 290-re.\" Csak az adott kiszerelés ára változik.",
                        "\"Emeld a Pizzák kategória összes termékét 10%-kal, és kerekítsd 10-re.\" A ChatGPT minden terméket kilistáz a régi és új árral, te pedig egyben hagyod jóvá az egész listát.",
                        "\"Adj a csapolt sörhöz egy 1 literes kiszerelést 2 190-ért, és töröld a 0,3 literes kiszerelést.\"",
                        "\"Állítsd a Margherita pizza költségét 650-re.\""
                    ]
                },
                { type: "heading", text: "Nevek, leírások és allergének" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Nevezd át a Gulyáslevest angolul Beef goulash soup-ra, a magyar nevet tartsd meg.\" A többi nyelv megmarad.",
                        "\"Add a Gulyásleveshez a 'Friss kenyérrel tálaljuk' leírást.\"",
                        "\"A Carbonara glutént, tojást és tejet tartalmaz. Állítsd be ezeket az allergéneket.\" Az allergénlistát a megadott teljes lista váltja fel."
                    ]
                },
                { type: "heading", text: "Elérhetőség és csatornák" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Rejtsd el a Homárt a Kiosk-on és Online, a POS-en maradjon.\"",
                        "\"Inaktiváld a Nyári különlegességek kategóriát.\" Az alatta lévő termékek nem látszanak, amíg a kategória inaktív; a \"Aktiváld a Nyári különlegességek kategóriát\" kéréssel újra bekapcsolható.",
                        "\"A Brunch kategória csak 2026-12-01 és 2026-12-31 között legyen elérhető.\""
                    ]
                },
                { type: "heading", text: "Áthelyezés és sorrend" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Helyezd át a Tiramisut a Sütemények kategóriából a Desszertek közé.\"",
                        "\"Helyezd a Meleg levesek kategóriát az Előételek alá.\"",
                        "\"Tedd a Gulyáslevest a kategóriája első helyére.\""
                    ]
                },
                { type: "heading", text: "Kiszerelések" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Hozz létre egy palack nevű kiszerelést.\"",
                        "\"Nevezd át a dl kiszerelést deciliterre, csak angolul.\" Minden termék, amely használja, az új nevet mutatja.",
                        "\"Töröld a pohár kiszerelést.\" Az a kiszerelés, amelyet még használ bármely termék valamelyik adagja, nem törölhető; a ChatGPT megmondja, hány adag használja, és ezeket előbb átteheted másik kiszerelésre."
                    ]
                },
                { type: "heading", text: "Törlés és visszaállítás" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Töröld a Régi különlegesség terméket.\" A ChatGPT megmutatja, mi törlődik, és jóváhagyást kér. A termék csak el van rejtve, és visszaállítható: \"Állítsd vissza a Régi különlegesség terméket.\"",
                        "\"Töröld a Téli menü kategóriát.\" Az alatta lévő alkategóriák és termékek is eltűnnek az étlapról; az előnézet megmondja, mennyi. A kategória visszaállításakor ezek is visszatérnek.",
                        "A kiszerelések véglegesen törlődnek, és nem állíthatók vissza."
                    ]
                },
                { type: "heading", text: "A módosítások ellenőrzése" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Ki módosította a Margherita pizza árát a múlt héten?\" vagy \"Mit módosított ma a ChatGPT a menüben?\"",
                        "A Menü változásnapló (Riportok > Menü változásnapló) minden ChatGPT-n keresztül végzett módosítást ChatGPT forrással mutat, minden mező régi és új értékével. A termék Előzmények része egyetlen termék változásait mutatja."
                    ]
                }
            ],
            cn: [
                { type: "heading", text: "价格和规格" },
                {
                    type: "bullet-list",
                    items: [
                        "“把 0.5 升生啤的价格从 1 190 调到 1 290。”只有该规格的价格会改变。",
                        "“把“披萨”分类下所有产品涨价 10%，并取整到 10。”ChatGPT 会列出每个产品的旧价格和新价格，您一次性确认整个列表。",
                        "“给生啤添加一个 1 升规格，价格 2 190，并删除 0.3 升规格。”",
                        "“把玛格丽特披萨的成本设为 650。”"
                    ]
                },
                { type: "heading", text: "名称、描述和过敏原" },
                {
                    type: "bullet-list",
                    items: [
                        "“把牛肉汤的英文名改为 Beef goulash soup，保留匈牙利语名称。”其他语言保持不变。",
                        "“给牛肉汤添加描述“配新鲜面包”。”",
                        "“卡邦尼含有麸质、鸡蛋和牛奶。请设置这些过敏原。”过敏原列表会被您提供的完整列表替换。"
                    ]
                },
                { type: "heading", text: "可用性和渠道" },
                {
                    type: "bullet-list",
                    items: [
                        "“在 Kiosk 和在线隐藏龙虾，在 POS 上保留。”",
                        "“停用“夏季特色”分类。”分类停用期间，其下的产品不会显示；可用“启用“夏季特色”分类”重新启用。",
                        "“让“早午餐”分类仅在 2026-12-01 至 2026-12-31 之间可用。”"
                    ]
                },
                { type: "heading", text: "移动和排序" },
                {
                    type: "bullet-list",
                    items: [
                        "“把提拉米苏从“蛋糕”移到“甜点”。”",
                        "“把“热汤”分类移到“前菜”下。”",
                        "“把牛肉汤放在其分类的第一位。”"
                    ]
                },
                { type: "heading", text: "规格" },
                {
                    type: "bullet-list",
                    items: [
                        "“创建一个名为“瓶”的规格。”",
                        "“把规格 dl 重命名为 deciliter，仅英文。”使用它的所有产品都会显示新名称。",
                        "“删除规格“杯”。”仍被任何产品的规格使用的 Serving 无法删除；ChatGPT 会告知有多少个规格在使用，您可以先将它们改用其他 Serving。"
                    ]
                },
                { type: "heading", text: "删除和恢复" },
                {
                    type: "bullet-list",
                    items: [
                        "“删除产品“旧特色菜”。”ChatGPT 会显示将删除的内容并请您确认。产品只是被隐藏，可以恢复：“恢复产品“旧特色菜”。”",
                        "“删除“冬季菜单”分类。”其下的子分类和产品也会从菜单中消失；预览会告知数量。恢复该分类后它们会回来。",
                        "规格会被永久删除，无法恢复。"
                    ]
                },
                { type: "heading", text: "核查更改" },
                {
                    type: "bullet-list",
                    items: [
                        "“上周谁修改了玛格丽特披萨的价格？”或“今天 ChatGPT 在菜单中修改了什么？”",
                        "Menu change log（报告 > Menu change log）会以来源 ChatGPT 显示通过 ChatGPT 所做的每项更改，以及每个字段的旧值和新值。产品的 History 部分显示单个产品的更改。"
                    ]
                }
            ]
        },
        createdAt: "2026-11-01T09:10:00Z",
        modifiedAt: "2026-11-01T09:10:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: ["administration.products.fullaccess"],
        tags: ["chatgpt", "menu", "ai", "examples", "delete", "prices"]
    },
    {
        id: "chatgpt-stock-management",
        type: "guideline",
        title_i18n: {
            en: "Recording incoming stocks, inventories and scrappings with ChatGPT",
            de: "Wareneingänge, Inventuren und Ausbuchungen mit ChatGPT erfassen",
            hu: "Bevételezés, leltár és selejtezés rögzítése a ChatGPT-vel",
            cn: "使用 ChatGPT 记录入库、盘点和报废"
        },
        summary_i18n: {
            en: "ChatGPT can now look up and record incoming stocks (goods received from a supplier), stock inventories (counts) and inventory scrappings (written off stock). The stock is updated exactly as when you save the same record in the admin UI.",
            de: "ChatGPT kann jetzt Wareneingänge (Ware von einem Lieferanten), Inventuren (Bestandszählungen) und Ausbuchungen (abgeschriebene Ware) abfragen und erfassen. Der Bestand wird genau so aktualisiert, als würden Sie denselben Datensatz in der Admin-Oberfläche speichern.",
            hu: "A ChatGPT mostantól le tudja kérdezni és rögzíteni a bevételezéseket (szállítótól érkezett áru), a leltárakat (készletszámlálás) és a selejtezéseket (leírt készlet). A készlet pontosan úgy frissül, mintha ugyanazt a tételt az admin felületen mentenéd.",
            cn: "ChatGPT 现在可以查询并记录入库（供应商送来的货物）、库存盘点（清点）和库存报废（核销库存）。库存的更新方式与您在管理界面中保存同一记录时完全相同。"
        },
        details_i18n: {
            en: "ChatGPT uses the same stock management functions and the same permissions as the admin UI: it can only do what your own user may do in that restaurant. Nothing is saved before you have seen the preview and confirmed it. Records that change the stock recalculate it from the date of the record, so saving can take a little while.",
            de: "ChatGPT verwendet dieselben Lagerverwaltungsfunktionen und dieselben Berechtigungen wie die Admin-Oberfläche: Es kann nur das tun, was Ihr eigener Benutzer in diesem Restaurant tun darf. Nichts wird gespeichert, bevor Sie die Vorschau gesehen und bestätigt haben. Datensätze, die den Bestand ändern, berechnen ihn ab dem Datum des Datensatzes neu; das Speichern kann daher etwas dauern.",
            hu: "A ChatGPT ugyanazokat a készletkezelő funkciókat és ugyanazokat a jogosultságokat használja, mint az admin felület: csak azt teheti meg, amit a te felhasználód is megtehet az adott étteremben. Semmi nem mentődik el addig, amíg meg nem nézted az előnézetet és jóvá nem hagytad. A készletet módosító tételek a tétel dátumától újraszámolják a készletet, ezért a mentés eltarthat egy ideig.",
            cn: "ChatGPT 使用与管理界面相同的库存管理功能和相同的权限：它只能执行您本人在该餐厅中被允许的操作。在您查看预览并确认之前，不会保存任何内容。会改变库存的记录会从该记录的日期起重新计算库存，因此保存可能需要一些时间。"
        },
        contentBlocks_i18n: {
            en: [
                { type: "heading", text: "What ChatGPT can do" },
                {
                    type: "bullet-list",
                    items: [
                        "Incoming stocks: list and open the deliveries (supplier, date, invoice and delivery note numbers, prices, VAT), record a new delivery, change an existing one (header, finalize a draft, add, change or remove entries) and delete one.",
                        "Inventories: list the inventories and the inventory templates, and record a new stock count that follows a template. A saved draft can be finalized.",
                        "Scrappings: list the scrappings and record a new one for wasted, spoiled or broken stock, for ingredients and also for products (for example 2 wrongly made pizzas; the ingredients are taken from the recipe).",
                        "Everything is previewed first (dry run) and saved only after your confirmation."
                    ]
                },
                { type: "heading", text: "Talk to ChatGPT while you work" },
                {
                    type: "bullet-list",
                    items: [
                        "You do not have to type: you can simply talk to ChatGPT (for example with voice mode in the ChatGPT app) while you record an incoming stock, an inventory or a scrapping.",
                        "This is particularly great during an inventory: walk through the storage with your phone, say what you count (\"tomatoes, 12 kg\", \"flour, 3 bags\") and keep both hands free, while ChatGPT keeps track of the items and asks about anything that is unclear.",
                        "The same works for a delivery (read out the delivery note while unpacking) and for a scrapping (say what you are throwing away, right at the bin).",
                        "Before anything is saved, ChatGPT reads back or shows the summary, so you can correct it by talking as well."
                    ]
                },
                { type: "heading", text: "Permissions" },
                {
                    type: "bullet-list",
                    items: [
                        "Incoming stocks: the stock management incoming stocks view, add, modify or delete permission, exactly like in the admin UI.",
                        "Inventories: the stock management inventories view, add or modify permission (a template also needs its view permission).",
                        "Scrappings: the stock management inventory scrapping view or add permission.",
                        "The permissions are checked in the restaurant that is being changed. If you manage several restaurants, tell ChatGPT which one you mean."
                    ]
                },
                { type: "heading", text: "Recording an incoming stock" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT looks up the supplier, the VAT categories and the store, and finds the items you name. Only active items counted in the stock can be received.",
                        "2. You tell it what was received: for each item the quantity (ChatGPT says what one unit is, for example 1 kg or 1 piece), the price (net or gross, per unit or in total) and the VAT category, plus the invoice or delivery note number and the date.",
                        "3. It shows the supplier, date, entries and totals as a preview. You decide whether to save it as a draft (the default, the stock does not change) or final (the stock is increased from the date of the delivery).",
                        "4. After your confirmation it is saved once.",
                        "A delivery containing invoice lines that are not yet matched to a stock item cannot be changed through ChatGPT; edit it in the admin UI, Stock management, Incoming stocks."
                    ]
                },
                { type: "heading", text: "Recording an inventory" },
                {
                    type: "bullet-list",
                    items: [
                        "ChatGPT finds the inventory template and walks you through its items in the order of the template, a few at a time, telling you the name and the unit of each. Answer with a number of units or an amount (250 g).",
                        "It then shows the counted entries and the items you did not count. Items not counted are not stored, so their stock does not change; tell ChatGPT if they are really zero.",
                        "A draft inventory (the default) does not change the stock until it is finalized; a final inventory recalculates the stock from its date. A draft can be finalized later through ChatGPT or in the admin UI.",
                        "Recipe based products and templates inherited from a master restaurant are not supported; use the admin UI for them."
                    ]
                },
                { type: "heading", text: "Recording a scrapping" },
                {
                    type: "bullet-list",
                    items: [
                        "Tell ChatGPT what was written off, how much and why (the reason is stored as the comment). ChatGPT finds the items and the store.",
                        "The preview shows the store, date and entries. After your confirmation the stock is reduced from the date of the scrapping.",
                        "For a product give the number of portions; the ingredients are deducted from the stock according to the recipe."
                    ]
                },
                { type: "heading", text: "Examples of requests" },
                {
                    type: "bullet-list",
                    items: [
                        "\"What did we receive from the supplier last week?\" / \"Show me the invoice 2026/15.\"",
                        "\"We received 10 kg of tomatoes and 5 kg of flour from the supplier today, invoice 2026/15. Show me the entries and totals first, save it as a draft.\"",
                        "\"Finalize the draft incoming stock of yesterday.\"",
                        "\"Show me the last inventory.\" / \"Let us do the kitchen inventory, start with the first template.\"",
                        "\"Finalize the draft inventory from Monday.\"",
                        "\"What did we scrap last week?\" / \"Scrap 2 kg of spoiled tomatoes, the fridge broke down.\""
                    ]
                },
                { type: "heading", text: "Good to know" },
                {
                    type: "bullet-list",
                    items: [
                        "Records that change the stock cannot be undone through ChatGPT, so check the preview carefully. An incoming stock can be changed or deleted again, an inventory or scrapping can be corrected in the admin UI.",
                        "ChatGPT always states what one unit of an item is. If something is missing or ambiguous it asks instead of guessing.",
                        "When ChatGPT reports the stock recalculation as running, it is still being completed in the background; check the result in the admin UI."
                    ]
                }
            ],
            de: [
                { type: "heading", text: "Was ChatGPT kann" },
                {
                    type: "bullet-list",
                    items: [
                        "Wareneingänge: Lieferungen auflisten und öffnen (Lieferant, Datum, Rechnungs- und Lieferscheinnummern, Preise, MwSt.), einen neuen Wareneingang erfassen, einen bestehenden ändern (Kopfdaten, Entwurf abschließen, Positionen hinzufügen, ändern oder entfernen) und einen löschen.",
                        "Inventuren: Inventuren und Inventurvorlagen auflisten und eine neue Bestandszählung nach einer Vorlage erfassen. Ein gespeicherter Entwurf kann abgeschlossen werden.",
                        "Ausbuchungen: Ausbuchungen auflisten und eine neue für verdorbene, abgelaufene oder zerbrochene Ware erfassen, für Zutaten und auch für Produkte (zum Beispiel 2 falsch zubereitete Pizzen; die Zutaten werden aus dem Rezept übernommen).",
                        "Alles wird zuerst als Vorschau (dry run) angezeigt und erst nach Ihrer Bestätigung gespeichert."
                    ]
                },
                { type: "heading", text: "Mit ChatGPT sprechen, während Sie arbeiten" },
                {
                    type: "bullet-list",
                    items: [
                        "Sie müssen nicht tippen: Sie können einfach mit ChatGPT sprechen (zum Beispiel mit dem Sprachmodus der ChatGPT-App), während Sie einen Wareneingang, eine Inventur oder eine Ausbuchung erfassen.",
                        "Besonders praktisch ist das bei einer Inventur: Gehen Sie mit dem Handy durch das Lager, sagen Sie, was Sie zählen (\"Tomaten, 12 kg\", \"Mehl, 3 Säcke\") und haben Sie beide Hände frei, während ChatGPT die Artikel verfolgt und bei Unklarheiten nachfragt.",
                        "Dasselbe funktioniert bei einer Lieferung (den Lieferschein beim Auspacken vorlesen) und bei einer Ausbuchung (sagen Sie direkt am Mülleimer, was Sie wegwerfen).",
                        "Bevor etwas gespeichert wird, liest ChatGPT die Zusammenfassung vor oder zeigt sie an, sodass Sie sie ebenfalls durch Sprechen korrigieren können."
                    ]
                },
                { type: "heading", text: "Berechtigungen" },
                {
                    type: "bullet-list",
                    items: [
                        "Wareneingänge: die Berechtigung Lagerverwaltung Wareneingänge anzeigen, hinzufügen, ändern oder löschen, genau wie in der Admin-Oberfläche.",
                        "Inventuren: die Berechtigung Lagerverwaltung Inventuren anzeigen, hinzufügen oder ändern (eine Vorlage braucht zusätzlich ihre Anzeigeberechtigung).",
                        "Ausbuchungen: die Berechtigung Lagerverwaltung Inventurausbuchung anzeigen oder hinzufügen.",
                        "Die Berechtigungen werden in dem Restaurant geprüft, das geändert wird. Wenn Sie mehrere Restaurants verwalten, sagen Sie ChatGPT, welches gemeint ist."
                    ]
                },
                { type: "heading", text: "Einen Wareneingang erfassen" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT sucht den Lieferanten, die MwSt.-Kategorien und das Lager heraus und findet die genannten Artikel. Nur aktive, im Bestand geführte Artikel können eingebucht werden.",
                        "2. Sie sagen, was geliefert wurde: pro Artikel die Menge (ChatGPT nennt die Einheit, zum Beispiel 1 kg oder 1 Stück), den Preis (netto oder brutto, pro Einheit oder gesamt) und die MwSt.-Kategorie sowie die Rechnungs- oder Lieferscheinnummer und das Datum.",
                        "3. Es zeigt Lieferant, Datum, Positionen und Summen als Vorschau. Sie entscheiden, ob es als Entwurf (Standard, der Bestand ändert sich nicht) oder endgültig gespeichert wird (der Bestand erhöht sich ab dem Lieferdatum).",
                        "4. Nach Ihrer Bestätigung wird es einmal gespeichert.",
                        "Ein Wareneingang mit Rechnungszeilen, die noch keinem Artikel zugeordnet sind, kann nicht über ChatGPT geändert werden; bearbeiten Sie ihn in der Admin-Oberfläche unter Lagerverwaltung, Wareneingänge."
                    ]
                },
                { type: "heading", text: "Eine Inventur erfassen" },
                {
                    type: "bullet-list",
                    items: [
                        "ChatGPT findet die Inventurvorlage und geht mit Ihnen die Artikel in der Reihenfolge der Vorlage durch, jeweils ein paar auf einmal, und nennt Name und Einheit. Antworten Sie mit einer Anzahl Einheiten oder einer Menge (250 g).",
                        "Danach zeigt es die gezählten Positionen und die nicht gezählten Artikel. Nicht gezählte Artikel werden nicht gespeichert, ihr Bestand ändert sich also nicht; sagen Sie ChatGPT, wenn sie wirklich null sind.",
                        "Ein Entwurf (Standard) ändert den Bestand erst, wenn er abgeschlossen wird; eine endgültige Inventur berechnet den Bestand ab ihrem Datum neu. Ein Entwurf kann später über ChatGPT oder in der Admin-Oberfläche abgeschlossen werden.",
                        "Rezeptbasierte Produkte und von einem Master-Restaurant geerbte Vorlagen werden nicht unterstützt; verwenden Sie dafür die Admin-Oberfläche."
                    ]
                },
                { type: "heading", text: "Eine Ausbuchung erfassen" },
                {
                    type: "bullet-list",
                    items: [
                        "Sagen Sie ChatGPT, was abgeschrieben wurde, wie viel und warum (der Grund wird als Kommentar gespeichert). ChatGPT findet die Artikel und das Lager.",
                        "Die Vorschau zeigt Lager, Datum und Positionen. Nach Ihrer Bestätigung wird der Bestand ab dem Datum der Ausbuchung verringert.",
                        "Bei einem Produkt geben Sie die Anzahl der Portionen an; die Zutaten werden gemäß Rezept vom Bestand abgezogen."
                    ]
                },
                { type: "heading", text: "Beispiele für Anfragen" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Was haben wir letzte Woche vom Lieferanten erhalten?\" / \"Zeig mir die Rechnung 2026/15.\"",
                        "\"Wir haben heute 10 kg Tomaten und 5 kg Mehl vom Lieferanten erhalten, Rechnung 2026/15. Zeig mir zuerst Positionen und Summen, speichere es als Entwurf.\"",
                        "\"Schließe den Wareneingangs-Entwurf von gestern ab.\"",
                        "\"Zeig mir die letzte Inventur.\" / \"Machen wir die Küchen-Inventur, beginne mit der ersten Vorlage.\"",
                        "\"Schließe den Inventur-Entwurf von Montag ab.\"",
                        "\"Was haben wir letzte Woche ausgebucht?\" / \"Buche 2 kg verdorbene Tomaten aus, der Kühlschrank ist ausgefallen.\""
                    ]
                },
                { type: "heading", text: "Gut zu wissen" },
                {
                    type: "bullet-list",
                    items: [
                        "Datensätze, die den Bestand ändern, können über ChatGPT nicht rückgängig gemacht werden; prüfen Sie die Vorschau daher sorgfältig. Ein Wareneingang kann erneut geändert oder gelöscht werden, eine Inventur oder Ausbuchung kann in der Admin-Oberfläche korrigiert werden.",
                        "ChatGPT nennt immer, was eine Einheit eines Artikels ist. Fehlt etwas oder ist es mehrdeutig, fragt es nach, statt zu raten.",
                        "Meldet ChatGPT die Bestandsneuberechnung als laufend, wird sie im Hintergrund noch abgeschlossen; prüfen Sie das Ergebnis in der Admin-Oberfläche."
                    ]
                }
            ],
            hu: [
                { type: "heading", text: "Mit tud a ChatGPT" },
                {
                    type: "bullet-list",
                    items: [
                        "Bevételezések: a szállítmányok listázása és megnyitása (szállító, dátum, számla- és szállítólevélszám, árak, ÁFA), új bevételezés rögzítése, meglévő módosítása (fejadatok, vázlat véglegesítése, tételek hozzáadása, módosítása vagy törlése) és törlése.",
                        "Leltárak: a leltárak és a leltársablonok listázása, valamint új készletszámlálás rögzítése egy sablon alapján. A mentett vázlat véglegesíthető.",
                        "Selejtezések: a selejtezések listázása és új rögzítése romlott, lejárt vagy eltört készletre, alapanyagokra és termékekre is (például 2 rosszul elkészített pizza; az alapanyagokat a recept alapján számolja).",
                        "Minden előbb előnézetként (dry run) jelenik meg, és csak a jóváhagyásod után mentődik."
                    ]
                },
                { type: "heading", text: "Beszélj a ChatGPT-vel munka közben" },
                {
                    type: "bullet-list",
                    items: [
                        "Nem kell gépelned: egyszerűen beszélhetsz a ChatGPT-vel (például a ChatGPT alkalmazás hangmódjával), miközben bevételezést, leltárt vagy selejtezést rögzítesz.",
                        "Ez különösen jó leltározás közben: járd végig a raktárat a telefonoddal, mondd el, mit számolsz (\"paradicsom, 12 kg\", \"liszt, 3 zsák\"), és mindkét kezed szabad marad, miközben a ChatGPT követi a tételeket, és rákérdez, ha valami nem egyértelmű.",
                        "Ugyanez működik szállításnál (felolvashatod a szállítólevelet kicsomagolás közben) és selejtezésnél is (mondd el, mit dobsz ki, közvetlenül a kuka mellett).",
                        "Mielőtt bármi mentődik, a ChatGPT felolvassa vagy megmutatja az összefoglalót, így beszéddel is javíthatod."
                    ]
                },
                { type: "heading", text: "Jogosultságok" },
                {
                    type: "bullet-list",
                    items: [
                        "Bevételezések: a készletkezelés bevételezések megtekintése, hozzáadása, módosítása vagy törlése jogosultság, pontosan úgy, mint az admin felületen.",
                        "Leltárak: a készletkezelés leltárak megtekintése, hozzáadása vagy módosítása jogosultság (a sablonhoz a megtekintési jogosultsága is kell).",
                        "Selejtezések: a készletkezelés leltári selejtezés megtekintése vagy hozzáadása jogosultság.",
                        "A jogosultságokat abban az étteremben ellenőrzi a rendszer, amelyiket módosítod. Ha több éttermet kezelsz, mondd meg a ChatGPT-nek, melyikre gondolsz."
                    ]
                },
                { type: "heading", text: "Bevételezés rögzítése" },
                {
                    type: "bullet-list",
                    items: [
                        "1. A ChatGPT megkeresi a szállítót, az ÁFA kategóriákat és a raktárat, és megtalálja a megnevezett tételeket. Csak aktív, a készletben számolt tételek vehetők be.",
                        "2. Te mondod meg, mi érkezett: tételenként a mennyiséget (a ChatGPT megmondja, mi az egy egység, például 1 kg vagy 1 darab), az árat (nettó vagy bruttó, egységárként vagy összesen) és az ÁFA kategóriát, továbbá a számla- vagy szállítólevélszámot és a dátumot.",
                        "3. Előnézetben megmutatja a szállítót, a dátumot, a tételeket és az összesítést. Te döntöd el, hogy vázlatként (alapértelmezett, a készlet nem változik) vagy véglegesként mentse (a készlet nő a szállítás dátumától).",
                        "4. A jóváhagyásod után egyszer menti.",
                        "Az olyan bevételezés, amelyben még nem párosított számlasorok vannak, a ChatGPT-n keresztül nem módosítható; szerkeszd az admin felületen, a Készletkezelés > Bevételezések alatt."
                    ]
                },
                { type: "heading", text: "Leltár rögzítése" },
                {
                    type: "bullet-list",
                    items: [
                        "A ChatGPT megkeresi a leltársablont, és a sablon sorrendjében végigvezet a tételeken, egyszerre néhányat, megmondva mindegyik nevét és egységét. Válaszolj egységek számával vagy mennyiséggel (250 g).",
                        "Ezután megmutatja a megszámolt tételeket és a meg nem számoltakat. A meg nem számolt tételek nem kerülnek mentésre, így a készletük nem változik; mondd meg a ChatGPT-nek, ha valójában nullák.",
                        "A vázlat leltár (alapértelmezett) addig nem módosítja a készletet, amíg nincs véglegesítve; a végleges leltár a dátumától újraszámolja a készletet. A vázlat később a ChatGPT-n keresztül vagy az admin felületen véglegesíthető.",
                        "A recept alapú termékek és a törzsétteremtől örökölt sablonok nem támogatottak; ezekhez használd az admin felületet."
                    ]
                },
                { type: "heading", text: "Selejtezés rögzítése" },
                {
                    type: "bullet-list",
                    items: [
                        "Mondd meg a ChatGPT-nek, mit írtál le, mennyit és miért (az ok megjegyzésként tárolódik). A ChatGPT megkeresi a tételeket és a raktárat.",
                        "Az előnézet megmutatja a raktárat, a dátumot és a tételeket. A jóváhagyásod után a készlet a selejtezés dátumától csökken.",
                        "Terméknél az adagok számát add meg; az alapanyagokat a recept alapján vonja le a készletből."
                    ]
                },
                { type: "heading", text: "Példák kérésekre" },
                {
                    type: "bullet-list",
                    items: [
                        "\"Mit kaptunk a szállítótól a múlt héten?\" / \"Mutasd a 2026/15 számlát.\"",
                        "\"Ma kaptunk 10 kg paradicsomot és 5 kg lisztet a szállítótól, számla 2026/15. Előbb mutasd a tételeket és az összesítést, vázlatként mentsd.\"",
                        "\"Véglegesítsd a tegnapi vázlat bevételezést.\"",
                        "\"Mutasd az utolsó leltárat.\" / \"Csináljuk meg a konyhai leltárt, kezdd az első sablonnal.\"",
                        "\"Véglegesítsd a hétfői vázlat leltárat.\"",
                        "\"Mit selejteztünk a múlt héten?\" / \"Selejtezz 2 kg romlott paradicsomot, elromlott a hűtő.\""
                    ]
                },
                { type: "heading", text: "Jó tudni" },
                {
                    type: "bullet-list",
                    items: [
                        "A készletet módosító tételeket a ChatGPT-n keresztül nem lehet visszavonni, ezért gondosan nézd át az előnézetet. A bevételezés újra módosítható vagy törölhető, a leltár és a selejtezés az admin felületen javítható.",
                        "A ChatGPT mindig megmondja, mi egy tétel egy egysége. Ha valami hiányzik vagy félreérthető, kérdez ahelyett, hogy találgatna.",
                        "Ha a ChatGPT a készlet újraszámolását futónak jelzi, az a háttérben még befejeződik; az eredményt ellenőrizd az admin felületen."
                    ]
                }
            ],
            cn: [
                { type: "heading", text: "ChatGPT 可以做什么" },
                {
                    type: "bullet-list",
                    items: [
                        "入库：列出并打开送货单（供应商、日期、发票和送货单号、价格、增值税），记录新的入库，修改现有入库（表头、确认草稿、添加、修改或删除明细）以及删除入库。",
                        "盘点：列出盘点和盘点模板，并按模板记录新的库存清点。已保存的草稿可以确认。",
                        "报废：列出报废记录，并为变质、过期或损坏的库存记录新的报废，可用于原料，也可用于产品（例如 2 份做错的披萨；原料按配方计算）。",
                        "所有操作都会先预览（dry run），在您确认后才会保存。"
                    ]
                },
                { type: "heading", text: "边工作边与 ChatGPT 对话" },
                {
                    type: "bullet-list",
                    items: [
                        "您无需打字：在记录入库、盘点或报废时，可以直接与 ChatGPT 对话（例如使用 ChatGPT 应用的语音模式）。",
                        "这在盘点时尤其方便：拿着手机走过仓库，说出您清点的内容（“番茄，12 公斤”“面粉，3 袋”），双手保持空闲，ChatGPT 会跟踪各个产品，并在有不清楚的地方时询问您。",
                        "送货时同样适用（拆箱时读出送货单），报废时也一样（在垃圾桶旁边说出要丢弃的东西）。",
                        "在保存任何内容之前，ChatGPT 会朗读或显示摘要，您也可以通过对话来更正。"
                    ]
                },
                { type: "heading", text: "权限" },
                {
                    type: "bullet-list",
                    items: [
                        "入库：库存管理的入库查看、添加、修改或删除权限，与管理界面完全相同。",
                        "盘点：库存管理的盘点查看、添加或修改权限（模板还需要其查看权限）。",
                        "报废：库存管理的盘点报废查看或添加权限。",
                        "权限会在被修改的餐厅中检查。如果您管理多家餐厅，请告诉 ChatGPT 指的是哪一家。"
                    ]
                },
                { type: "heading", text: "记录入库" },
                {
                    type: "bullet-list",
                    items: [
                        "1. ChatGPT 查找供应商、增值税类别和仓库，并找到您提到的产品。只有启用且按库存计数的产品才能入库。",
                        "2. 您说明收到了什么：每个产品的数量（ChatGPT 会说明一个单位是什么，例如 1 公斤或 1 件）、价格（含税或不含税，单价或总价）和增值税类别，以及发票或送货单号和日期。",
                        "3. 它会以预览显示供应商、日期、明细和合计。由您决定保存为草稿（默认，库存不变）还是最终（库存从送货日期起增加）。",
                        "4. 在您确认后保存一次。",
                        "包含尚未匹配到库存产品的发票行的入库无法通过 ChatGPT 修改；请在管理界面“库存管理 > 入库”中编辑。"
                    ]
                },
                { type: "heading", text: "记录盘点" },
                {
                    type: "bullet-list",
                    items: [
                        "ChatGPT 找到盘点模板，并按模板的顺序一次几项地带您逐项清点，说明每项的名称和单位。请回答单位数量或数量（250 克）。",
                        "然后它会显示已清点的明细和未清点的产品。未清点的产品不会保存，因此其库存不变；如果它们确实为零，请告诉 ChatGPT。",
                        "草稿盘点（默认）在确认之前不会改变库存；最终盘点会从其日期起重新计算库存。草稿之后可通过 ChatGPT 或在管理界面中确认。",
                        "基于配方的产品和从总店继承的模板不受支持；请在管理界面中处理。"
                    ]
                },
                { type: "heading", text: "记录报废" },
                {
                    type: "bullet-list",
                    items: [
                        "告诉 ChatGPT 核销了什么、数量多少以及原因（原因保存为备注）。ChatGPT 会查找产品和仓库。",
                        "预览会显示仓库、日期和明细。在您确认后，库存从报废日期起减少。",
                        "对于产品，请给出份数；原料会按配方从库存中扣除。"
                    ]
                },
                { type: "heading", text: "请求示例" },
                {
                    type: "bullet-list",
                    items: [
                        "“上周我们从供应商那里收到了什么？”/“显示发票 2026/15。”",
                        "“今天我们从供应商收到 10 公斤番茄和 5 公斤面粉，发票 2026/15。先显示明细和合计，保存为草稿。”",
                        "“确认昨天的入库草稿。”",
                        "“显示最近一次盘点。”/“我们来做厨房盘点，从第一个模板开始。”",
                        "“确认周一的盘点草稿。”",
                        "“上周我们报废了什么？”/“报废 2 公斤变质的番茄，冰箱坏了。”"
                    ]
                },
                { type: "heading", text: "注意事项" },
                {
                    type: "bullet-list",
                    items: [
                        "会改变库存的记录无法通过 ChatGPT 撤销，因此请仔细检查预览。入库可以再次修改或删除，盘点和报废可在管理界面中更正。",
                        "ChatGPT 总会说明产品的一个单位是什么。如果信息缺失或有歧义，它会询问而不是猜测。",
                        "如果 ChatGPT 报告库存重新计算正在运行，则它仍在后台完成；请在管理界面中核对结果。"
                    ]
                }
            ]
        },
        createdAt: "2026-11-01T09:15:00Z",
        modifiedAt: "2026-11-01T09:15:00Z",
        module: "stockmanagement",
        journey: "inventory_admin",
        roles: ["admin", "manager"],
        permissions: [
            "stockmanagement.incomingstocks.view",
            "stockmanagement.incomingstocks.add",
            "stockmanagement.incomingstocks.modify",
            "stockmanagement.incomingstocks.delete",
            "stockmanagement.inventories.view",
            "stockmanagement.inventories.add",
            "stockmanagement.inventories.modify",
            "stockmanagement.inventoryscrapping.view",
            "stockmanagement.inventoryscrapping.add"
        ],
        tags: ["chatgpt", "stock", "incoming-stock", "inventory", "scrapping", "ai", "dry-run"]
    },
    {
        id: "menu-change-log-feature",
        type: "whats-new",
        title_i18n: {
            en: "Menu change log",
            de: "Menü-Änderungsprotokoll",
            hu: "Menü változásnapló",
            cn: "菜单变更日志"
        },
        summary_i18n: {
            en: "Every change of products, categories and servings is now recorded: who changed it, when, and what the old and new values were. Open it under Reports > Menu change log, or in the History section of the product and category editor.",
            de: "Jede Änderung an Produkten, Kategorien und Portionen wird jetzt protokolliert: wer sie wann vorgenommen hat und wie die alten und neuen Werte lauteten. Aufrufbar unter Berichte > Menu change log oder im Bereich History des Produkt- und Kategorie-Editors.",
            hu: "Mostantól minden termék-, kategória- és kiszerelés-módosítás naplózva van: ki, mikor és mit módosított, mi volt a régi és mi az új érték. Megnyitható a Riportok > Menü változásnapló menüpontban, valamint a termék és kategória szerkesztő Előzmények részében.",
            cn: "现在会记录产品、分类和规格的每一次更改：谁在何时修改了什么，以及旧值和新值。可在“报告 > Menu change log”中查看，也可在产品和分类编辑器的“History”部分查看。"
        },
        details_i18n: {
            en: "The change log records the creation, modification, deletion, restoring, activating, deactivating, moving and reordering of products (Meal, Drink), categories (Meal category, Drink category) and servings (quantity types). Changes made in the admin UI and changes made through ChatGPT are both logged; the Source column shows Admin or ChatGPT. Entries older than one year are deleted automatically.",
            de: "Das Änderungsprotokoll erfasst das Erstellen, Ändern, Löschen, Wiederherstellen, Aktivieren, Deaktivieren, Verschieben und Neuordnen von Produkten (Meal, Drink), Kategorien (Meal category, Drink category) und Portionen (Quantity types). Änderungen in der Admin-Oberfläche und Änderungen über ChatGPT werden beide protokolliert; die Spalte Source zeigt Admin oder ChatGPT. Einträge, die älter als ein Jahr sind, werden automatisch gelöscht.",
            hu: "A változásnapló rögzíti a termékek (Étel, Ital), a kategóriák (Étel kategória, Ital kategória) és a kiszerelések létrehozását, módosítását, törlését, visszaállítását, aktiválását, inaktiválását, áthelyezését és átrendezését. Az admin felületen és a ChatGPT-n keresztül végzett módosítások is naplózásra kerülnek; a Forrás oszlop mutatja, hogy Admin vagy ChatGPT végezte. Az egy évnél régebbi bejegyzések automatikusan törlődnek.",
            cn: "变更日志会记录产品（Meal、Drink）、分类（Meal category、Drink category）和规格（Quantity type）的创建、修改、删除、恢复、启用、停用、移动和重新排序。通过管理界面和通过 ChatGPT 所做的更改都会被记录；Source 列显示 Admin 或 ChatGPT。超过一年的记录会自动删除。"
        },
        contentBlocks_i18n: {
            en: [
                { type: "heading", text: "Where to find it" },
                {
                    type: "bullet-list",
                    items: [
                        "Reports > Menu change log: the complete log of the restaurant. It needs the Reports access and the administration, menu change log or products full access permission.",
                        "In the product or category editor, the History section lists only the changes of the edited product or category. It is shown for existing items only, not for new ones."
                    ]
                },
                { type: "heading", text: "Filters on the Menu change log page" },
                {
                    type: "bullet-list",
                    items: [
                        "From / To: the time period. By default the last 30 days are shown.",
                        "Modified by: select a single user (surname and name, or e-mail address). Everyone shows all users.",
                        "Entity type: All, All products, All categories, Meal, Drink, Meal category, Drink category or Serving.",
                        "Modification type: Created, Modified, Deleted, Restored, Activated, Deactivated, Moved or Reordered.",
                        "Name contains: part of the name of the product or category. It also finds items that were renamed or deleted later.",
                        "Search applies the filters; Reset clears them and returns to the last 30 days."
                    ]
                },
                { type: "heading", text: "Reading the list" },
                {
                    type: "bullet-list",
                    items: [
                        "Columns: Time, Modified by, Source, Entity, Modification type and Changes. The Changes column shows a short summary of the first changed fields.",
                        "Click a row to open it. The Field, Old value and New value table lists every changed field, for example name, price, VAT category, production lines, availability on POS, Kiosk and Online, labels, allergens or add-ons. References are shown by name, not by number.",
                        "The list shows 50 entries per page; use Previous and Next at the bottom to page through the results. The History section of the editor shows 5 entries per page and has its own From, To, Modified by and Modification type filters."
                    ]
                },
                { type: "heading", text: "ChatGPT" },
                {
                    type: "text",
                    text: "ChatGPT can read the same log (tool getMenuChangeLog) with the same permissions and filters, for example to answer who changed the price of a product or what changed in the menu last week. Menu changes made by ChatGPT are logged with the source ChatGPT."
                }
            ],
            de: [
                { type: "heading", text: "Wo finde ich es" },
                {
                    type: "bullet-list",
                    items: [
                        "Berichte > Menu change log: das vollständige Protokoll des Restaurants. Erforderlich sind der Zugriff auf Berichte und die Berechtigung Administration, Menu change log oder Produkte-Vollzugriff.",
                        "Im Produkt- oder Kategorie-Editor listet der Bereich History nur die Änderungen des bearbeiteten Produkts oder der Kategorie auf. Er wird nur für bestehende Einträge angezeigt, nicht für neue."
                    ]
                },
                { type: "heading", text: "Filter auf der Seite Menu change log" },
                {
                    type: "bullet-list",
                    items: [
                        "From / To: der Zeitraum. Standardmäßig werden die letzten 30 Tage angezeigt.",
                        "Modified by: ein einzelner Benutzer (Nachname und Name oder E-Mail-Adresse). Everyone zeigt alle Benutzer.",
                        "Entity type: All, All products, All categories, Meal, Drink, Meal category, Drink category oder Serving.",
                        "Modification type: Created, Modified, Deleted, Restored, Activated, Deactivated, Moved oder Reordered.",
                        "Name contains: Teil des Namens des Produkts oder der Kategorie. Es werden auch später umbenannte oder gelöschte Einträge gefunden.",
                        "Search wendet die Filter an; Reset setzt sie zurück auf die letzten 30 Tage."
                    ]
                },
                { type: "heading", text: "Die Liste lesen" },
                {
                    type: "bullet-list",
                    items: [
                        "Spalten: Time, Modified by, Source, Entity, Modification type und Changes. Die Spalte Changes zeigt eine kurze Zusammenfassung der ersten geänderten Felder.",
                        "Klicken Sie auf eine Zeile, um sie zu öffnen. Die Tabelle Field, Old value und New value listet jedes geänderte Feld auf, zum Beispiel Name, Preis, MwSt.-Kategorie, Produktionslinien, Verfügbarkeit an POS, Kiosk und Online, Etiketten, Allergene oder Zusätze. Verweise werden mit Namen statt mit Nummern angezeigt.",
                        "Die Liste zeigt 50 Einträge pro Seite; mit Previous und Next am unteren Rand blättern Sie durch die Ergebnisse. Der Bereich History im Editor zeigt 5 Einträge pro Seite und hat eigene Filter für From, To, Modified by und Modification type."
                    ]
                },
                { type: "heading", text: "ChatGPT" },
                {
                    type: "text",
                    text: "ChatGPT kann dasselbe Protokoll (Tool getMenuChangeLog) mit denselben Berechtigungen und Filtern lesen, zum Beispiel um zu beantworten, wer den Preis eines Produkts geändert hat oder was sich letzte Woche im Menü geändert hat. Menüänderungen durch ChatGPT werden mit der Quelle ChatGPT protokolliert."
                }
            ],
            hu: [
                { type: "heading", text: "Hol található" },
                {
                    type: "bullet-list",
                    items: [
                        "Riportok > Menü változásnapló: az étterem teljes változásnaplója. Hozzá a Riportok hozzáférés és az adminisztráció, a menü változásnapló vagy a termékek teljes hozzáférés jogosultság szükséges.",
                        "A termék vagy kategória szerkesztőben az Előzmények rész csak a szerkesztett termék vagy kategória változásait listázza. Csak meglévő elemnél jelenik meg, újnál nem."
                    ]
                },
                { type: "heading", text: "Szűrők a Menü változásnapló oldalon" },
                {
                    type: "bullet-list",
                    items: [
                        "Ettől / Eddig: az időszak. Alapértelmezetten az elmúlt 30 nap látszik.",
                        "Módosító: egy felhasználó kiválasztása (vezetéknév és keresztnév, vagy e-mail cím). A Mindenki az összes felhasználót mutatja.",
                        "Elem típusa: Mind, Összes termék, Összes kategória, Étel, Ital, Étel kategória, Ital kategória vagy Kiszerelés.",
                        "Módosítás típusa: Létrehozva, Módosítva, Törölve, Visszaállítva, Aktiválva, Inaktiválva, Áthelyezve vagy Átrendezve.",
                        "A név tartalmazza: a termék vagy kategória nevének egy része. Azokat az elemeket is megtalálja, amelyeket később átneveztek vagy töröltek.",
                        "A Keresés gomb alkalmazza a szűrőket; a Törlés gomb törli őket, és visszaáll az elmúlt 30 napra."
                    ]
                },
                { type: "heading", text: "A lista értelmezése" },
                {
                    type: "bullet-list",
                    items: [
                        "Oszlopok: Időpont, Módosító, Forrás, Elem, Módosítás típusa és Változások. A Változások oszlop az első módosított mezőket foglalja össze röviden.",
                        "Egy sorra kattintva kinyílik: a Mező, Régi érték és Új érték táblázat minden módosított mezőt felsorol, például név, ár, ÁFA kategória, gyártósorok, elérhetőség POS-en, Kiosk-on és Online, címkék, allergének vagy kiegészítők. A hivatkozások névvel jelennek meg, nem azonosítóval.",
                        "A lista oldalanként 50 bejegyzést mutat; az alul lévő Előző és Következő gombokkal lehet lapozni. A szerkesztő Előzmények része oldalanként 5 bejegyzést mutat, és saját Ettől, Eddig, Módosító és Módosítás típusa szűrői vannak."
                    ]
                },
                { type: "heading", text: "ChatGPT" },
                {
                    type: "text",
                    text: "A ChatGPT ugyanezt a naplót (getMenuChangeLog eszköz) ugyanazokkal a jogosultságokkal és szűrőkkel olvassa, például hogy megválaszolja, ki módosította egy termék árát, vagy mi változott a menüben az elmúlt héten. A ChatGPT által végzett menümódosítások ChatGPT forrással kerülnek a naplóba."
                }
            ],
            cn: [
                { type: "heading", text: "在哪里查看" },
                {
                    type: "bullet-list",
                    items: [
                        "报告 > Menu change log：餐厅的完整变更日志。需要报告访问权限，以及管理、菜单变更日志或产品完全访问权限。",
                        "在产品或分类编辑器中，History 部分仅列出所编辑产品或分类的更改。该部分只对已有项目显示，新建项目不显示。"
                    ]
                },
                { type: "heading", text: "Menu change log 页面的筛选项" },
                {
                    type: "bullet-list",
                    items: [
                        "From / To：时间段。默认显示最近30天。",
                        "Modified by：选择单个用户（姓和名，或电子邮件地址）。Everyone 表示所有用户。",
                        "Entity type：All、All products、All categories、Meal、Drink、Meal category、Drink category 或 Serving。",
                        "Modification type：Created、Modified、Deleted、Restored、Activated、Deactivated、Moved 或 Reordered。",
                        "Name contains：产品或分类名称的一部分。也能找到之后被重命名或删除的项目。",
                        "Search 应用筛选；Reset 清除筛选并恢复为最近30天。"
                    ]
                },
                { type: "heading", text: "如何阅读列表" },
                {
                    type: "bullet-list",
                    items: [
                        "列：Time、Modified by、Source、Entity、Modification type 和 Changes。Changes 列简要显示前几个被修改的字段。",
                        "点击某一行可展开。Field、Old value 和 New value 表格列出每个被修改的字段，例如名称、价格、增值税类别、生产线、POS、Kiosk 和在线可用性、标签、过敏原或附加项。引用以名称而不是编号显示。",
                        "列表每页显示50条记录；使用底部的 Previous 和 Next 翻页。编辑器中的 History 部分每页显示5条记录，并有自己的 From、To、Modified by 和 Modification type 筛选。"
                    ]
                },
                { type: "heading", text: "ChatGPT" },
                {
                    type: "text",
                    text: "ChatGPT 可以使用相同的权限和筛选读取同一份日志（工具 getMenuChangeLog），例如回答谁修改了某个产品的价格，或上周菜单有哪些变化。ChatGPT 所做的菜单更改会以来源 ChatGPT 记入日志。"
                }
            ]
        },
        createdAt: "2026-11-01T08:00:00Z",
        modifiedAt: "2026-11-01T08:00:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: ["administration.menuchangelog", "administration.products.fullaccess"],
        tags: ["menu", "tracking", "reporting", "filters", "permissions"]
    },
    {
        id: "master-inventory-template-feature",
        type: "whats-new",
        title_i18n: {
            en: "Master inventory templates",
            de: "Master-Lager-Vorlagen",
            hu: "Master leltár sablonok",
            cn: "主库存模板"
        },
        summary_i18n: {
            en: "For restaurants linked to a master instance, the Templates list can also include master inventory templates imported from the master restaurant. This is especially useful for franchise groups, where a central template can define the standard inventory rules across all locations.",
            de: "Für Restaurants, die mit einer Master-Instanz verknüpft sind, kann die Vorlagenliste auch Master-Lager-Vorlagen enthalten, die aus dem Master-Restaurant importiert wurden. Das ist besonders nützlich für Franchise-Gruppen, bei denen ein zentrales Template die Standard-Lagerregeln für alle Standorte festlegt.",
            hu: "Azoknál az éttermeknél, amelyek egy master példányhoz vannak kapcsolva, a Sablonok lista tartalmazhat master leltár sablonokat is, amelyeket a master étteremből importáltak. Ez különösen hasznos franchise hálózatoknál, ahol a központi sablon határozza meg a szabványos leltárszabályokat az összes helyszínen.",
            cn: "对于连接到主实例的餐厅来说，“模板”列表还可能包含从主餐厅导入的主库存模板。这对连锁门店尤其有用，因为中心模板可以为所有门店定义统一的库存规则。"
        },
        details_i18n: {
            en: "In the Mód > Sablonok flow, the template list can include saved inventory templates from the connected master restaurant. These are treated as master templates and can be reused in the same template workflow without leaving the stock management screen. For a franchise network, this lets the head office define the standard inventory setup centrally so all locations work with the same rules, item structure, and stock template.",
            de: "Im Ablauf Mód > Sablonok kann die Vorlagenliste gespeicherte Lager-Vorlagen aus dem verbundenen Master-Restaurant enthalten. Diese werden als Master-Vorlagen behandelt und können im gleichen Vorlagenfluss wiederverwendet werden, ohne den Bestandsmanagement-Bildschirm zu verlassen. In einem Franchise-Netzwerk kann das Hauptbüro dadurch zentral die Standard-Lagerstruktur festlegen, sodass alle Filialen mit denselben Regeln, derselben Artikelstruktur und derselben Lager-Vorlage arbeiten.",
            hu: "A Mód > Sablonok folyamában a sablonlista tartalmazhat mentett leltár sablonokat a kapcsolt master étteremből. Ezeket master sablonként kezelik, és ugyanabban a sablonfolyamatban újra felhasználhatók, anélkül, hogy elhagynánk a leltárkezelő képernyőt. Franchise hálózatoknál ez lehetővé teszi, hogy a központ központosítottan határozza meg a standard leltárbeállításokat, így minden helyszín ugyanazokat a szabályokat és leltár sablonokat használja.",
            cn: "在“模式 > 模板”流程中，模板列表可以包含来自已连接主餐厅的已保存库存模板。这些会按主模板处理，并可在同一模板流程中重复使用，而无需离开库存管理界面。对于连锁网络来说，这使总部可以集中定义标准库存设置，从而让所有门店使用相同的规则、项目结构和库存模板。"
        },
        createdAt: "2026-10-25T08:00:00Z",
        modifiedAt: "2026-10-25T08:00:00Z",
        module: "stockmanagement",
        journey: "inventory_admin",
        roles: ["admin", "manager"],
        permissions: ["stockmanagement.inventorytemplates.view"],
        tags: ["lelt\u00e1r", "sablon", "master", "lelt\u00e1rkezel\u00e9s", "szinkron"]
    },
  {
    id: "inventory-template-feature",
    type: "whats-new",
    title_i18n: {
      en: "Inventory templates",
      de: "Lager-Vorlagen",
      hu: "Leltár sablonok",
      cn: "库存模板"
    },
    summary_i18n: {
      en: "Use the Mode selector to switch between Inventories and Templates, then save or load a template with Save as template and the Templates field.",
      de: "Im Feld Modus kann zwischen Bestände und Vorlagen gewechselt werden; mit „Als Vorlage speichern“ und dem Feld Vorlagen lassen sich Vorlagen speichern oder laden.",
      hu: "A Mód mezőben a Leltár és a Sablonok között lehet váltani, majd a Mentés sablonként gombbal és a Sablonok mezőben lehet sablont menteni vagy betölteni.",
      cn: "可在“模式”中切换“库存”和“模板”，然后通过“另存为模板”和“模板”字段保存或加载模板。"
    },
    details_i18n: {
      en: "In the inventory list, the Mode selector switches between Inventories and Templates. In the editor, Save as template saves the current stock list. When creating a new inventory, the Templates field lets you select an earlier saved template and load its items.",
      de: "Im Lagerbereich kann im Feld Modus zwischen Bestände und Vorlagen gewechselt werden. Im Editor speichert „Als Vorlage speichern“ die aktuelle Liste. Beim Erstellen eines neuen Lagers kann im Feld Vorlagen eine frühere Vorlage ausgewählt und deren Einträge geladen werden.",
      hu: "A leltár listában a Mód mezőben a Leltár és a Sablonok lehetőségek közül lehet választani. A leltár szerkesztőben a Mentés sablonként gombbal menthető el az aktuális leltár. Új leltár létrehozásakor a Sablonok mezőben kiválasztható a korábbi sablon, és a rendszer betölti a mentett tételeket.",
      cn: "在库存列表中，可在“模式”字段中切换“库存”和“模板”。在编辑器中，使用“另存为模板”保存当前库存。创建新库存时，可在“模板”字段中选择以前保存的模板并加载其项目。"
    },
    contentBlocks_i18n: {
      hu: [
        {
          type: "embed",
          title: "Leltár sablonok bemutató videó",
          caption: "Bemutató videó a leltár sablonok használatáról.",
          url: "https://www.youtube.com/embed/yIsQE7Ao_BQ"
        },
        {
          type: "link",
          text: "Megnyitás a YouTube-on",
          url: "https://youtu.be/yIsQE7Ao_BQ"
        }
      ]
    },
    createdAt: "2026-10-24T08:00:00Z",
    modifiedAt: "2026-10-24T08:00:00Z",
    module: "stockmanagement",
    journey: "inventory_admin",
    roles: ["admin", "manager"],
    permissions: ["stockmanagement.inventorytemplates.view"],
      tags: ["lelt\u00e1r", "sablon", "lelt\u00e1roz\u00e1s", "\u00fajrafelhaszn\u00e1lhat\u00f3", "munkafolyamat"]
  },
  {
    id: "inventory-excel-export-import-feature",
    type: "whats-new",
    title_i18n: {
      en: "Excel export and import for stock updates",
      de: "Excel-Export und -Import für Bestandsaktualisierungen",
      hu: "Excel export és import leltárfrissítéshez",
      cn: "用于库存更新的Excel导出与导入"
    },
    summary_i18n: {
      en: "In the normal inventory view, use the Excel buttons to generate the stock file or upload the edited file back.",
      de: "In der normalen Lageransicht dienen die Excel-Schaltflächen dazu, die Lagerdatei zu erstellen oder die bearbeitete Datei wieder hochzuladen.",
      hu: "A normál leltár nézetben látszanak az Excel gombok: Feltöltés Excelből és Kitöltöm excelben. Ezekkel lehet a leltárfájlt elkészíteni, vagy a módosított fájlt visszatölteni.",
      cn: "在普通库存视图中，可使用Excel按钮生成库存文件或重新上传已编辑的文件。"
    },
    details_i18n: {
      en: "The normal inventory view shows the Excel buttons for the current stock file and for uploading the edited file back. The file contains the current product list and quantity columns, so stock values can be changed in Excel and then imported again. Empty rows are skipped, so the update stays fast and predictable.",
      de: "In der normalen Lageransicht werden die Excel-Schaltflächen für die aktuelle Lagerdatei und für das erneute Hochladen der bearbeiteten Datei angezeigt. Die Datei enthält die Produktliste und Mengenangaben, sodass Bestandswerte in Excel geändert und danach wieder importiert werden können. Leere Zeilen werden übersprungen, damit die Aktualisierung schnell und zuverlässig bleibt.",
      hu: "A normál leltár nézetben látszanak az Excel gombok: Feltöltés Excelből és Kitöltöm excelben. A fájl a termékek listáját és a darabszám oszlopokat tartalmazza, ezért a mennyiségek Excelben módosíthatók, majd újra importálhatók. Az üres sorokat a rendszer kihagyja, így a leltárfrissítés gyors és kiszámítható marad.",
      cn: "在普通库存视图中，会显示Excel按钮来生成当前库存文件并重新上传已编辑的文件。该文件包含产品列表和数量列，因此可以在Excel中修改库存数值后再次导入。空白行会被跳过，因此更新过程更快、更稳定。"
    },
    createdAt: "2026-10-24T08:00:00Z",
    modifiedAt: "2026-10-24T08:00:00Z",
    module: "stockmanagement",
    journey: "inventory_admin",
    roles: ["admin", "manager"],
    permissions: ["stockmanagement.view"],
      tags: ["lelt\u00e1r", "excel", "import", "export", "t\u00f6meges-friss\u00edt\u00e9s"]
  },
  {
    id: "inventory-template-permissions-feature",
    type: "whats-new",
    title_i18n: {
      en: "Inventory template permissions",
      de: "Berechtigungen für Lager-Vorlagen",
      hu: "Leltár sablonok jogkörök",
      cn: "库存模板权限"
    },
    summary_i18n: {
      en: "Template management is protected by separate permissions: view, add, modify, and delete. Users without the right access cannot open or use the template flow.",
      de: "Das Vorlagenmanagement ist durch separate Berechtigungen geschützt: Anzeigen, Hinzufügen, Ändern und Löschen. Nutzer ohne die passende Berechtigung können den Vorlagenfluss nicht öffnen oder nutzen.",
      hu: "A sablonkezelés külön jogkörökhöz kötött: nézet, hozzáadás, módosítás és törlés. Akinek nincs meg a megfelelő jogosultsága, az nem tudja megnyitni vagy használni a sablonfolyamot.",
      cn: "模板管理受独立权限控制：查看、添加、修改和删除。没有相应权限的用户无法打开或使用模板流程。"
    },
    details_i18n: {
      en: "The new rights belong to stockmanagement.inventorytemplates.*. Users with view can open the Mód selector and see the Templates option, then load an existing template. Add gives access to Save as template. Modify and delete cover editing or removing saved templates. If a user lacks view access, the template mode is blocked and the template actions are hidden.",
      de: "Die neuen Rechte gehören zu stockmanagement.inventorytemplates.*. Nutzer mit Ansicht können den Modus-Selektor öffnen und die Option Vorlagen sehen, um eine vorhandene Vorlage zu laden. „Hinzufügen“ aktiviert „Als Vorlage speichern“. „Ändern“ und „Löschen“ decken das Bearbeiten oder Entfernen gespeicherter Vorlagen ab. Wenn ein Nutzer keine Ansichtsberechtigung hat, wird der Vorlagenmodus blockiert und die Vorlagenaktionen ausgeblendet.",
      hu: "Az új jogok a stockmanagement.inventorytemplates.* csoportba tartoznak. A nézet joggal rendelkező felhasználó megnyithatja a Mód választót, és látja a Sablonok opciót, majd betölthet egy mentett sablont. A hozzáadás jog engedélyezi a Mentés sablonként gombet. A módosítás és törlés jog a mentett sablonok szerkesztésére vagy eltávolítására vonatkozik. Ha a felhasználónak nincs nézet jog, a sablon mód le van tiltva, és a sablon műveletek el lettek rejtve.",
      cn: "新的权限属于 stockmanagement.inventorytemplates.*。拥有查看权限的用户可以打开“模式”选择器并看到“模板”选项，然后加载已有模板。添加权限启用“另存为模板”。修改和删除权限用于编辑或移除已保存模板。如果用户没有查看权限，模板模式会被阻止，模板操作也会被隐藏。"
    },
    createdAt: "2026-10-24T08:00:00Z",
    modifiedAt: "2026-10-24T08:00:00Z",
    module: "stockmanagement",
    journey: "inventory_admin",
    roles: ["admin", "manager"],
    permissions: ["stockmanagement.inventorytemplates.view"],
      tags: ["lelt\u00e1r", "sablon", "jogosults\u00e1gok", "rbac", "hozz\u00e1f\u00e9r\u00e9s-kezel\u00e9s"]
  },
  {
    id: "invoice-logo-insertion-feature",
    type: "whats-new",
    title_i18n: {
      en: "Invoice logo helper",
      de: "Rechnungslogo-Helfer",
      hu: "Számla logó beillesztése",
      cn: "发票Logo助手"
    },
    summary_i18n: {
      en: "Goal: make invoice branding easier by letting you preview and insert the restaurant logo into the invoice header.",
      de: "Ziel: die Rechnungsbranding einfacher machen, indem das Restaurantlogo vor dem Einfügen im Kopfbereich der Rechnung vorab angesehen und eingefügt werden kann.",
      hu: "Cél: egyszerűbbé tenni a számla arculatát azzal, hogy a logót előtte meg lehet tekinteni, és a számla fejlécébe lehet beszúrni.",
      cn: "目标：让发票品牌化更简单，可以先预览餐厅Logo，并将其插入发票页眉。"
    },
    details_i18n: {
      en: "From the restaurant settings, the admin can use the current restaurant logo or upload a new one, then preview how it looks before placing it in the invoice header. The system can also adjust the white margins and make the logo cleaner and more balanced on the document. Once it looks right, the logo is inserted into the invoice header.",
      de: "Im Restaurant-Setup kann der Administrator das aktuelle Restaurantlogo verwenden oder ein neues hochladen und zunächst ansehen, wie es im Kopfbereich der Rechnung aussieht. Das System kann auch die weißen Ränder anpassen und das Logo sauberer und ausgewogener auf dem Dokument wirken lassen. Wenn alles passend aussieht, wird das Logo in den Kopfbereich der Rechnung eingefügt.",
      hu: "Az étterem beállításai között az adminisztrátor használhatja a meglévő logót, vagy feltölthet egy újat, és előtte megnézheti, hogyan fog kinézni a számla fejlécében. A rendszer a fehér margókat is igazítani tudja, így a logó tisztább és kiegyensúlyozottabb lesz a dokumentumban. Ha megfelelőnek tűnik, a logó a számla fejlécébe kerül beillesztésre.",
      cn: "在餐厅设置中，管理员可以使用当前餐厅Logo，或者上传新的Logo，并先预览它在发票页眉中的效果。系统也可以调整白边并让Logo在文档中更整洁、更平衡。一切合适后，Logo就会插入到发票页眉中。"
    },
    createdAt: "2026-10-24T08:00:00Z",
    modifiedAt: "2026-10-24T08:00:00Z",
    module: "administration",
    journey: "administration",
    roles: ["admin"],
    permissions: [],
      tags: ["sz\u00e1mla", "log\u00f3", "arculat", "el\u0151n\u00e9zet"]
  },
  {
    id: "utalvany-payment-method-support-feature",
    type: "whats-new",
    title_i18n: {
      en: "Utalvány (voucher) payment method support",
      de: "Zahlungsart Utalvány unterstützt",
      hu: "Utalvány fizetési mód támogatása",
      cn: "支持 Utalvány 支付方式"
    },
    summary_i18n: {
      en: "Utalvány (voucher) is now supported as a payment type. Enable it from Administration > Cash registers by adding Utalvány (voucher) to the cash register payment methods.",
      de: "Utalvány wird jetzt als Zahlungsmethode unterstützt. Aktivieren Sie es über Administration > Kassen, indem Sie Utalvány zu den Zahlungsmethoden der Kasse hinzufügen.",
      hu: "Az Utalvány most már támogatott fizetési mód. Engedélyezéséhez nyissa meg az Adminisztráció > Pénztárgépek menüpontot, és adja hozzá az Utalványt a pénztár fizetési módjaihoz.",
      cn: "Utalvány 现已支持作为支付方式。要启用它，请前往 Administration > Cash registers，并将 Utalvány 添加到收银机支付方式中。"
    },
    details_i18n: {
      en: "The payment option is available globally, but each register must be configured explicitly. Go to Administration / Cash registers, open the relevant cash register setting, and add Utalvány (voucher) as a payment method. Once enabled there, the payment can be used in the POS flow.",
      de: "Die Zahlungsoption ist grundsätzlich verfügbar, muss aber für jede Kasse separat konfiguriert werden. Gehen Sie zu Administration / Cash registers, öffnen Sie die entsprechende Kassen-Einstellung und fügen Sie Utalvány als Zahlungsmethode hinzu. Danach kann sie im POS-Ablauf verwendet werden.",
      hu: "A fizetési lehetőség globálisan elérhető, de minden pénztárhoz külön kell konfigurálni. Nyissa meg az Adminisztráció / Pénztárgépek menüpontot, a megfelelő pénztár beállítását, és adja hozzá az Utalványt fizetési módként. Ezt követően a fizetési mód használható lesz a fizetéskor is.",
      cn: "该支付选项已全局可用，但每台收银机都需要单独配置。前往 Administration / Cash registers，打开对应收银机设置，并将 Utalvány 添加为支付方式。启用后，即可在 POS 流程中使用。"
    },
    createdAt: "2026-10-28T09:00:00Z",
    modifiedAt: "2026-10-28T09:00:00Z",
    module: "administration",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
    tags: ["utalvány", "fizetés", "pénztár", "cash-register", "beállítások"]
  },
  {
    id: "guidelines-whats-new-entry",
    type: "whats-new",
    title_i18n: {
      en: "Guidelines & What's new",
      de: "Richtlinien und Neuigkeiten",
        hu: "Funkciók és újdonságok",
      cn: "指南与更新"
    },
    summary_i18n: {
      en: "A new admin panel shows product updates and operational guidance, with role-based visibility, multilingual text, search, and tag filters.",
      de: "Ein neues Admin-Panel zeigt Produkt-Updates und Betriebsrichtlinien an, mit rollenbasierter Sichtbarkeit, Mehrsprachigkeit, Suche und Tag-Filtern.",
      hu: "Az új admin panel a termékfrissítéseket és működési irányelveket mutatja, szerepkör alapú láthatósággal, többnyelvű szöveggel, kereséssel és címkeszűréssel.",
      cn: "新的管理面板展示产品更新和操作指南，具备基于角色的可见性、多语言文本、搜索和标签筛选功能。"
    },
    details_i18n: {
      en: "Users can browse feature entries relevant to their role, filter by date and freshness, and search by tag or text without seeing unrelated entries.",
      de: "Benutzer können nur die für ihre Rolle relevanten Einträge ansehen, nach Datum und Aktualität filtern und nach Tag oder Text suchen, ohne irrelevante Einträge zu sehen.",
      hu: "A felhasználók csak a szerepkörhöz tartozó bejegyzéseket nézhetik meg, szűrhetnek dátum és frissesség szerint, valamint címke vagy szöveg alapján kereshetnek a nem releváns elemek nélkül.",
      cn: "用户可以查看与其角色相关的功能条目，按日期和新鲜度筛选，并按标签或文本搜索，而不会看到无关的条目。"
    },
    createdAt: "2026-10-24T11:37:01Z",
    modifiedAt: "2026-10-24T11:37:01Z",
    module: "administration",
    journey: "administration",
    roles: ["admin"],
    permissions: [],
      tags: ["admin", "friss\u00edt\u00e9sek", "szerepk\u00f6r-alap\u00fa", "dokument\u00e1ci\u00f3", "sz\u0171r\u0151k"]
  },
  {
    id: "inventory-ordering-grouping-feature",
    type: "whats-new",
    title_i18n: {
      en: "Inventory ordering and grouping",
      de: "Bestandsreihenfolge und Gruppierung",
      hu: "Leltár rendezés és csoportosítás",
      cn: "库存排序与分组"
    },
    summary_i18n: {
      en: "The inventory list now supports clearer ordering and grouping of stock entries.",
      de: "Die Bestandsliste unterstützt jetzt eine klarere Reihenfolge und Gruppierung von Lagerpositionen.",
      hu: "A leltárlista most már világosabb sorrendet és csoportosítást támogat a készlet tételekhez.",
      cn: "库存列表现在支持更清晰的排序和分组显示。"
    },
    details_i18n: {
      en: "This makes large inventories easier to review, especially when related stock records should be displayed together or in a more structured order.",
      de: "Dadurch lassen sich größere Bestände leichter prüfen, insbesondere wenn zusammengehörige Lagerdatensätze gemeinsam oder in einer klareren Reihenfolge angezeigt werden sollen.",
      hu: "Ez megkönnyíti a nagy leltárak áttekintését, különösen akkor, ha a kapcsolódó készletrekordokat együtt vagy strukturáltabb sorrendben kell megjeleníteni.",
      cn: "这让大型库存更容易审核，尤其是在相关库存记录应一起显示或按更清晰的顺序展示时。"
    },
    createdAt: "2026-10-23T12:12:55Z",
    modifiedAt: "2026-10-23T12:12:55Z",
    module: "stockmanagement",
    journey: "inventory_admin",
    roles: ["admin", "manager"],
    permissions: ["stockmanagement.view"],
      tags: ["lelt\u00e1r", "csoportos\u00edt\u00e1s", "rendez\u00e9s", "lelt\u00e1rkezel\u00e9s"]
  },
  {
    id: "return-money-change-flow-feature",
    type: "whats-new",
    title_i18n: {
      en: "Return-money / change handling",
      de: "Rückgeld- und Wechselgeldabwicklung",
      hu: "Visszajáró / pénzváltás kezelése",
      cn: "找零与退币处理"
    },
    summary_i18n: {
      en: "The cashier flow includes improved return-money and change handling.",
      de: "Der Kassenablauf enthält jetzt eine verbesserte Rückgeld- und Wechselgeldabwicklung.",
      hu: "A kassza folyamat javítja a visszajáró és a pénzváltás kezelését.",
      cn: "收银流程中改进了找零和退币处理。"
    },
    details_i18n: {
      en: "This directly affects the checkout process when giving change or processing money returns during payment.",
      de: "Dies wirkt sich direkt auf den Checkout-Prozess aus, wenn Wechselgeld ausgegeben oder Rückzahlungen während der Zahlung verarbeitet werden.",
      hu: "Ez közvetlenül befolyásolja a pénztár folyamatát, amikor visszajárót adnak vagy fizetés közben visszafizetést kezelnek.",
      cn: "这会直接影响结账流程，尤其是在支付时找零或处理退币时。"
    },
    createdAt: "2026-10-18T16:03:31Z",
    modifiedAt: "2026-10-18T16:03:31Z",
    module: "sales",
    journey: "orders",
    roles: ["admin", "manager", "cashier"],
    permissions: [],
      tags: ["pult", "fizet\u00e9s", "p\u00e9nzt\u00e1ros", "visszaj\u00e1r\u00f3", "visszat\u00e9r\u00edt\u00e9s"]
  },
  {
    id: "hellopay-integration-settings-feature",
    type: "whats-new",
    title_i18n: {
      en: "HelloPay integration settings",
      de: "HelloPay-Integrations-Einstellungen",
      hu: "HelloPay integrációs beállítások",
      cn: "HelloPay 集成设置"
    },
    summary_i18n: {
      en: "Restaurant payment settings now include a HelloPay user code field.",
      de: "Die Restaurant-Zahlungseinstellungen enthalten jetzt ein HelloPay-Benutzercode-Feld.",
      hu: "A HelloPay felhasználói azonosító határozza meg, hogy a borravaló hova kerül. Ha nincs beállítva, akkor ugyanoda megy, mint a látogatási összeg. A beállításhoz kérjék a support segítségét.",
      cn: "餐厅支付设置现在包含 HelloPay 用户代码字段。"
    },
    details_i18n: {
      en: "This is part of the actual payment setup flow and affects how the restaurant connects to the payment provider.",
      de: "Dies ist Teil des tatsächlichen Zahlungseinrichtungsprozesses und beeinflusst, wie das Restaurant sich an den Zahlungsanbieter anschließt.",
      hu: "A HelloPay felhasználói azonosító meghatározza a borravaló célhelyét. Ha nincs beállítva, akkor a borravaló ugyanoda kerül, mint a látogatási összeg. A beállításhoz kérjék a supportet.",
      cn: "这是实际支付设置流程的一部分，并影响餐厅如何连接到支付服务商。"
    },
    createdAt: "2026-10-17T08:27:17Z",
    modifiedAt: "2026-10-17T08:27:17Z",
    module: "administration",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
      tags: ["fizet\u00e9s", "hello-pay", "be\u00e1ll\u00edt\u00e1sok", "integr\u00e1ci\u00f3"]
  },
  {
    id: "chatgpt-eatwithme-plugin-feature",
    type: "whats-new",
    title_i18n: {
      en: "ChatGPT EatWithMe plugin",
      de: "ChatGPT EatWithMe-Plugin",
      hu: "ChatGPT EatWithMe plugin",
      cn: "ChatGPT EatWithMe 插件"
    },
    summary_i18n: {
      en: "Install the EatWithMe ChatGPT analytics module and use it in the daily workflow to generate AI-assisted analyses of products, sales, and traffic.",
      de: "Installieren Sie das EatWithMe-ChatGPT-Analysemodul und nutzen Sie es im täglichen Arbeitsablauf für KI-gestützte Analysen zu Produkten, Verkäufen und Verkehrsströmen.",
      hu: "Telepítsd az EatWithMe ChatGPT elemző modult, és használd a mindennapi munkafolyamatban a termékekről, eladásokról és forgalomról készülő AI által támogatott elemzésekhez.",
      cn: "安装 EatWithMe ChatGPT 分析模块，并在日常工作流中使用它，对产品、销售和客流进行 AI 辅助分析。"
    },
    details_i18n: {
      en: "This analytics module helps restaurant staff review products, sales performance, and traffic patterns with AI-assisted guidance in the EatWithMe environment. The installation is simple, and the workflow is designed to fit the daily operating rhythm. The embedded video demonstrates the workflow and its practical use cases. For the next two months, the usage is free for everyone, and after that it may become a paid module depending on the final product decision.",
      de: "Dieses Analysemodul hilft Restaurantmitarbeitern dabei, Produkte, Verkaufsleistung und Verkehrsströme mit KI-gestützten Empfehlungen im EatWithMe-Umfeld zu bewerten. Die Installation ist einfach, und der Arbeitsablauf ist auf den täglichen Betrieb abgestimmt. Das eingebettete Video zeigt den Ablauf und die praktischen Anwendungsfälle. In den nächsten zwei Monaten ist die Nutzung für alle kostenlos; danach kann das Modul je nach finaler Entscheidung kostenpflichtig werden.",
      hu: "Ez az elemző modul segít az éttermi kollégáknak a termékekről, az értékesítési adatokról és a forgalmi mintázatokról készülő AI támogatású elemzések értékelésében az EatWithMe környezetben. A telepítés egyszerű, a munkafolyamat pedig a mindennapi működéshez igazodik. A beágyazott videó bemutatja a munkafolyamatet és a gyakorlati használati eseteket. A következő két hónapban a használat mindenkinek ingyenes, ezután lehet, hogy a modul fizetőssé válik a végső döntés függvényében.",
      cn: "该分析模块可帮助餐厅工作人员在 EatWithMe 环境中通过 AI 辅助分析评估产品、销售表现和客流模式。安装简单，工作流设计与日常运营节奏相匹配。嵌入视频展示了工作流及其实际使用场景。接下来的两个月内，所有人都可以免费使用；之后，根据最终产品决策，该模块可能会变为付费功能。"
    },
    contentBlocks_i18n: {
      en: [
        {
          type: "embed",
          title: "ChatGPT EatWithMe plugin demo",
          caption: "Video walkthrough for the ChatGPT EatWithMe plugin.",
          url: "https://www.youtube.com/embed/nBhXT-HFQU8"
        },
        {
          type: "link",
          text: "Open video on YouTube",
          url: "https://youtu.be/nBhXT-HFQU8"
        }
      ],
      de: [
        {
          type: "embed",
          title: "Demo zum ChatGPT EatWithMe-Plugin",
          caption: "Video-Übersicht zum ChatGPT EatWithMe-Plugin.",
          url: "https://www.youtube.com/embed/nBhXT-HFQU8"
        },
        {
          type: "link",
          text: "Video auf YouTube öffnen",
          url: "https://youtu.be/nBhXT-HFQU8"
        }
      ],
      hu: [
        {
          type: "embed",
          title: "ChatGPT EatWithMe plugin bemutató",
          caption: "Videó bemutatja a ChatGPT EatWithMe plugin használatát.",
          url: "https://www.youtube.com/embed/nBhXT-HFQU8"
        },
        {
          type: "link",
          text: "Videó megnyitása a YouTube-on",
          url: "https://youtu.be/nBhXT-HFQU8"
        }
      ],
      cn: [
        {
          type: "embed",
          title: "ChatGPT EatWithMe 插件演示",
          caption: "展示 ChatGPT EatWithMe 插件使用流程的视频。",
          url: "https://www.youtube.com/embed/nBhXT-HFQU8"
        },
        {
          type: "link",
          text: "在 YouTube 中打开视频",
          url: "https://youtu.be/nBhXT-HFQU8"
        }
      ]
    },
    createdAt: "2026-10-26T08:00:00Z",
    modifiedAt: "2026-10-26T08:00:00Z",
    module: "integrations",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
    tags: ["chatgpt", "eatwithme", "plugin", "ai", "integr\u00e1ci\u00f3", "seg\u00edts\u00e9g", "munkafolyamat"]
  },
  {
    id: "fiscat-b1-cashier-integration-feature",
    type: "whats-new",
    title_i18n: {
      en: "Fiscat B1 fiscalization compliance",
      de: "Fiscat-B1-Fiskalisierung und Compliance",
      hu: "Fiscat B1 adó- és pénztár megfelelés",
      cn: "Fiscat B1 税务合规与电子收银"
    },
    summary_i18n: {
      en: "Fiscat B1 is the first new authority-approved EPG / electronic cash-register solution that follows the newer Hungarian fiscalization regulations.",
      de: "Fiscat B1 ist die erste neu genehmigte EPG-/elektronische Kassenlösung, die den neuen ungarischen Fiskalisierungsregelungen entspricht.",
      hu: "A Fiscat B1 az első új, engedélyszámú EPG / elektronikus pénztárgép megoldás, amely megfelel az új magyar adó- és fiskális előírásoknak.",
      cn: "Fiscat B1 是首个符合新匈牙利税务监管要求、并已获主管机关批准的 EPG / 电子收银解决方案。"
    },
    details_i18n: {
      en: "This update brings the restaurant POS into the new Hungarian fiscalization framework, covering electronic cash-register compliance, receipt generation, and transaction reporting under the updated authority rules. In practical terms, it is the technical layer that helps the business operate legally under the new EPG / tax requirements.",
      de: "Diese Aktualisierung bringt das Restaurant-POS in das neue ungarische Fiskalisierungs-Framework, inkl. EPG-/Belegpflicht, Kassendokumentation und Transaktionsmeldungen nach den aktualisierten Behördenvorgaben. Praktisch ist dies die technische Grundlage, damit das Geschäft den neuen EPG- und Steueranforderungen entspricht.",
      hu: "Ez a frissítés az étterem POS-rendszerét az új magyar fiskális szabályozás keretébe helyezi, beleértve az elektronikus pénztárgép megfelelést, a nyugta generálást és a tranzakciók hatósági jelzését az új előírások szerint. Gyakorlati szinten ez az a technikai réteg, amely segít a vállalkozásnak jogszerűen működni az új EPG / adóügyi követelmények mellett.",
      cn: "此更新使餐厅 POS 系统符合新的匈牙利税务合规框架，包括电子收银合规、发票/收据生成，以及基于最新主管机关要求的交易上报。实际而言，这是一层技术基础，帮助企业在新的 EPG / 税务要求下合法运营。"
    },
    createdAt: "2026-10-02T12:00:00Z",
    modifiedAt: "2026-10-02T12:00:00Z",
    module: "administration",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
      tags: ["fiscat", "b1", "p\u00e9nzt\u00e1ros", "fizet\u00e9s", "integr\u00e1ci\u00f3", "be\u00e1ll\u00edt\u00e1sok", "megfelel\u00e9s", "szab\u00e1lyoz\u00e1s"]
  },
  {
    id: "label-printing-configuration-feature",
    type: "whats-new",
    title_i18n: {
      en: "Label printing and restaurant settings",
      de: "Etikettendruck und Restaurant-Einstellungen",
      hu: "Címke nyomtatás és étterem beállítások",
      cn: "标签打印与餐厅设置"
    },
    summary_i18n: {
      en: "Label printing is now available directly from the delivery and pickup lists, not only from the kitchen screen.",
      de: "Der Etikettendruck ist jetzt direkt aus der Liefer- und Abhol-Liste verfügbar, nicht nur vom Küchenscreen.",
      hu: "A címkenyomtatás most már közvetlenül a házhozszállítási és az elviteles listából is elérhető, nem csak a konyhai képernyőről.",
      cn: "标签打印现在可直接从配送和自提列表中使用，而不仅限于厨房屏幕。"
    },
    details_i18n: {
      en: "Previously, label printing was only available from the kitchen screen. Now the same action is available directly from the delivery and pickup views, so staff can print labels when they are already working on those orders and without switching screens.",
      de: "Bisher war der Etikettendruck nur vom Küchenscreen aus verfügbar. Jetzt ist dieselbe Aktion direkt aus der Liefer- und Abholansicht erreichbar, sodass Mitarbeiter Labels ausdrucken können, ohne zwischen den Bildschirmen zu wechseln.",
      hu: "Korábban a címkenyomtatás csak a konyhai képernyőről volt elérhető. Most ugyanaz a funkció közvetlenül a házhozszállítási és az elviteles listából is elérhető, így a kollégák közvetlenül a rendeléshez kapcsolódó nézetben tudnak címkét nyomtatni anélkül, hogy másik képernyőre kellene váltaniuk.",
      cn: "此前，标签打印仅可从厨房屏幕中使用。现在同样的操作可直接从配送和自提视图中使用，因此员工无需切换屏幕即可在处理相关订单时直接打印标签。"
    },
    createdAt: "2026-10-02T10:00:00Z",
    modifiedAt: "2026-10-02T10:00:00Z",
    module: "administration",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
      tags: ["c\u00edmke", "nyomtat\u00e1s", "be\u00e1ll\u00edt\u00e1sok", "ford\u00edt\u00e1s", "adminisztr\u00e1ci\u00f3"]
  },
  {
    id: "kiosk-upselling-suggestions-feature",
    type: "whats-new",
    title_i18n: {
      en: "Kiosk upselling suggestions",
      de: "Upselling-Vorschläge am Kiosk",
      hu: "Kiosk ajánlási javaslatok",
      cn: "自助点餐机增值推荐"
    },
    summary_i18n: {
      en: "The kiosk now shows upselling suggestions and category visibility options so relevant add-ons can be promoted at the right moment.",
      de: "Der Kiosk zeigt jetzt Upselling-Vorschläge und Optionen zur Kategorien-Sichtbarkeit an, damit passende Zusatzprodukte zum richtigen Zeitpunkt beworben werden können.",
      hu: "A kiosk most már ajánlási javaslatokat és kategória láthatósági beállításokat jelenít meg, így a megfelelő kiegészítő termékek a megfelelő időben népszerűsíthetők.",
      cn: "自助点餐机现在会展示增值推荐和分类可见性选项，因此相关附加产品可在正确的时机推广。"
    },
    details_i18n: {
      en: "The kiosk UI was updated with better product recommendation logic and category filtering, allowing add-ons and recommended items to appear more naturally during ordering. This improves conversion without cluttering the menu.",
      de: "Die Kiosk-Oberfläche wurde mit einer verbesserten Produktempfehlungslogik und Kategoriefilterung aktualisiert, sodass Zusatzprodukte und empfohlene Artikel während der Bestellung natürlicher erscheinen. Dadurch steigt die Konversionsrate, ohne die Speisekarte zu überladen.",
      hu: "A kiosk felületet frissítették a jobb termékajánlási logika és kategória szűrés segítségével, így a kiegészítő és ajánlott tételek természetesebben jelennek meg a rendelés során. Ez javítja az átalakítási arányt anélkül, hogy túlzsúfolttá tenné a menüt.",
      cn: "自助点餐机界面已更新为更好的产品推荐逻辑和分类筛选，让附加产品和推荐商品在下单过程中更自然地展示。这提高了转化率，同时不会让菜单显得过于拥挤。"
    },
    createdAt: "2026-10-13T09:00:00Z",
    modifiedAt: "2026-10-13T09:00:00Z",
    module: "sales",
    journey: "orders",
    roles: ["admin", "manager", "cashier"],
    permissions: [],
      tags: ["kiosk", "upselling", "\u00e9rt\u00e9kes\u00edt\u00e9s", "munkafolyamat", "aj\u00e1nl\u00e1sok"]
  },
  {
    id: "new-email-look-feature",
    type: "whats-new",
    title_i18n: {
      en: "Refreshed email styling",
      de: "Aktualisiertes E-Mail-Design",
      hu: "Frissített e-mail stílus",
      cn: "更新后的邮件样式"
    },
    summary_i18n: {
      en: "Email templates were redesigned with a cleaner, modern look for account, order, and password-related notifications.",
      de: "Die E-Mail-Templates wurden mit einem saubereren, moderneren Design für Konten-, Bestell- und Passwortbenachrichtigungen überarbeitet.",
      hu: "Az e-mail sablonokat frissítették egy tisztább, modernabb megjelenéssel a fiókhoz, rendeléshez és jelszóhoz kapcsolódó értesítésekhez.",
      cn: "邮件模板进行了重新设计，采用更干净、更现代的风格，用于账户、订单和密码相关通知。"
    },
    details_i18n: {
      en: "This refresh updates the render of activation, confirmation, password reset, and customer notifications across multiple languages. It gives the whole communication flow a more consistent and polished look while keeping the same business information and actions intact.",
      de: "Diese Aktualisierung verbessert das Rendering von Aktivierungs-, Bestätigungs-, Passwort-Reset- und Kundenbenachrichtigungen in mehreren Sprachen. Dadurch erhält der gesamte Kommunikationsfluss ein konsistenteres und professionelleres Aussehen, ohne die geschäftlich relevanten Informationen oder Aktionen zu verändern.",
      hu: "Ez a frissítés a regisztráció, megerősítés, jelszó-visszaállítás és ügyfélértesítések megjelenését modernizálja több nyelven. A teljes kommunikációs folyamat konzisztensebb és professzionálisabb megjelenést kap anélkül, hogy a vállalati információk vagy az akciók elvesznének.",
      cn: "此更新刷新了激活、确认、密码重置和客户通知等邮件的多语言渲染效果，使整个通信流程更统一、更精致，同时保留原有业务信息和操作内容不变。"
    },
    createdAt: "2026-10-07T11:30:00Z",
    modifiedAt: "2026-10-07T11:30:00Z",
    module: "administration",
    journey: "administration",
    roles: ["admin", "manager"],
    permissions: [],
      tags: ["email", "arculat", "sablonok", "kommunik\u00e1ci\u00f3", "diz\u00e1jn"]
    },
    {
        id: "restaurant-floor-map-and-bill-split-feature",
        type: "whats-new",
        title_i18n: {
            en: "Table map and bill splitting",
            de: "Tischplan und Rechnungsaufteilung",
            hu: "Asztaltérkép és számlabontás",
            cn: "桌位地图与分账"
        },
        summary_i18n: {
            en: "Track table status and order flow on a single overview while splitting bills and following active orders from the same dashboard.",
            de: "Verfolge den Tischstatus und den Bestellfluss in einer Übersicht und teile Rechnungen auf, während du die aktiven Bestellungen im selben Dashboard verfolgst.",
            hu: "Egyetlen áttekintésben követheted az asztalok állapotát és a rendelések menetét, miközben a számlákat is bontani lehet a rendszerben.",
            cn: "在同一控制台内跟踪桌位状态和订单流程，同时完成分账并查看活跃订单。"
        },
        details_i18n: {
            en: "The table overview shows each table state, timing, and order progress with color coding and icons. It is designed to help service staff work faster and with less confusion, especially during busy shifts or when multiple guests share a table.",
            de: "Die Tischübersicht zeigt den Status, Zeitpunkte und den Bestellfortschritt mit Farbcode und Symbolen. So können Servicemitarbeiter schneller und mit weniger Verwirrung arbeiten, besonders in stressigen Schichten oder bei gemeinsam benutzten Tischen.",
            hu: "Az asztal áttekintésben színkódok és ikonok segítségével látszik az asztalok állapota, időpontja és a rendelés folyamata. Ez a munkafolyamat gyorsabb és kevésbé összetett végrehajtását támogatja, különösen sűrű műszakokban vagy több vendég közös asztalánál.",
            cn: "桌位总览使用颜色编码和图标展示各桌状态、时间和订单进度，帮助服务人员在忙碌时段或多人共桌时更快、更清晰地协作。"
        },
        createdAt: "2026-09-01T08:00:00Z",
        modifiedAt: "2026-09-01T08:00:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["asztalok", "\u00e1ttekint\u00e9s", "rendel\u00e9sek", "sz\u00e1mlabont\u00e1s", "kiszolg\u00e1l\u00e1s"]
    },
    {
        id: "table-booking-feature",
        type: "whats-new",
        title_i18n: {
            en: "Table booking",
            de: "Tischreservierung",
            hu: "Asztalfoglalás",
            cn: "桌位预订"
        },
        summary_i18n: {
            en: "Add reservations with time and guest count, and let the system suggest the best table based on your priority rules.",
            de: "Erstelle Reservierungen mit Uhrzeit und Gästeanzahl; das System schlägt anhand deiner Prioritäten den passenden Tisch vor.",
            hu: "Adj meg foglalásokat időponttal és vendégszámmal, a rendszer a beállított prioritások alapján javasolja a legjobb asztalt.",
            cn: "可按时间和人数添加预订，并让系统根据优先级规则推荐最合适的桌位。"
        },
        details_i18n: {
            en: "The reservation flow includes start time, expected guest count, and priority-based table matching. Where needed, the staff can override the system suggestion and assign the best available table manually.",
            de: "Der Reservierungsprozess umfasst Startzeit, erwartete Gästezahl und tischweise Prioritätslogik. Wenn nötig, kann das Personal die Systemvorschläge überschreiben und einen passenden Tisch manuell zuweisen.",
            hu: "A foglalási folyamat tartalmazza az időpontot, a vendégek számát és a prioritás alapú asztalválasztást. Szükség esetén a személyzet felülírhatja a javaslatot, és manuálisan is kioszthat egy megfelelő asztalt.",
            cn: "预订流程包括开始时间、预计人数和基于优先级的桌位匹配。必要时，工作人员也可以覆盖系统建议并手动分配合适桌位。"
        },
        createdAt: "2026-09-01T08:05:00Z",
        modifiedAt: "2026-09-01T08:05:00Z",
        module: "sales",
        journey: "reservations",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["foglal\u00e1s", "foglal\u00e1s", "asztalok", "kiszolg\u00e1l\u00e1s", "tervez\u00e9s"]
    },
    {
        id: "self-service-kiosk-feature",
        type: "whats-new",
        title_i18n: {
            en: "Self-service kiosk",
            de: "Selbstbedienungs-Kiosk",
            hu: "Önkiszolgáló kiosk",
            cn: "自助点餐机"
        },
        summary_i18n: {
            en: "Guests can order and pay independently on the kiosk, while the kitchen and service team still receive the order flow in the same system.",
            de: "Gäste können am Kiosk eigenständig bestellen und zahlen, während Küche und Service dieselben Bestellungen im selben System erhalten.",
            hu: "A vendégek a kioskon saját maguk tudnak rendelni és fizetni, miközben a konyha és a kiszolgálás ugyanabban a rendszerben kapja meg a rendeléseket.",
            cn: "顾客可在自助点餐机上独立下单和付款，同时厨房和服务团队仍可在同一系统中接收订单流程。"
        },
        details_i18n: {
            en: "The kiosk is part of the restaurant’s digital sales flow. It supports ordering, payment, and guest interaction with minimal staff effort while still keeping the kitchen screens and service operations synchronized.",
            de: "Der Kiosk ist Teil des digitalen Verkaufsflusses des Restaurants. Er unterstützt Bestellungen, Bezahlung und Gästeinteraktion mit minimalem Personalaufwand und hält dabei Küche und Servicebetrieb synchronisiert.",
            hu: "A kiosk az étterem digitális értékesítési folyamatának része. Támogatja a rendelésfogadást, fizetést és a vendéginterakciót minimális személyzeti ráfordítással, miközben a konyhai és kiszolgálási folyamatok szinkronban maradnak.",
            cn: "自助点餐机是餐厅数字销售流程的一部分，支持下单、支付和顾客互动，减少人工操作，同时保持厨房和服务流程同步。"
        },
        createdAt: "2026-09-01T08:10:00Z",
        modifiedAt: "2026-09-01T08:10:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["kiosk", "\u00f6nkiszolg\u00e1l\u00f3", "fizet\u00e9s", "rendez\u00e9s", "vend\u00e9g\u00e9lm\u00e9ny"]
    },
    {
        id: "home-delivery-and-takeaway-feature",
        type: "whats-new",
        title_i18n: {
            en: "Home delivery and pickup ordering",
            de: "Lieferung und Abholung",
            hu: "Házhozszállítás és elvitel",
            cn: "外卖配送与自提"
        },
        summary_i18n: {
            en: "Manage home delivery and takeaway orders in the same system, including dispatch, timing, and customer communication.",
            de: "Verwalte Liefer- und Abholbestellungen im selben System, inklusive Auslieferung, Zeitplanung und Kundenkommunikation.",
            hu: "A házhozszállítási és az elviteles rendelések ugyanabban a rendszerben kezelhetők, beleértve a kiszállítást, időzítést és az ügyfélkommunikációt.",
            cn: "在同一系统中管理外卖配送和自提订单，包括配送、时间安排和客户沟通。"
        },
        details_i18n: {
            en: "This core workflow keeps food delivery and pickup orders in one operational process. It brings together order acceptance, kitchen preparation, and the final handoff or delivery, helping the team work without duplication or missed steps.",
            de: "Dieser Kernworkflow hält Liefer- und Abholbestellungen in einem operativen Prozess zusammen. Er verbindet Bestellannahme, Küchenvorbereitung und Übergabe bzw. Zustellung, sodass das Team ohne Duplikation und ohne verpasste Schritte arbeiten kann.",
            hu: "Ez a fő munkafolyamat a házhozszállítási és az elviteles rendelések közös működési körbe tartozását biztosítja. A rendelésfelvétel, a konyhai előkészítés és a végső átadás vagy kiszállítás ugyanabban a folyamatban történik, így a csapat kevesebb duplikációval és kevesebb kihagyott lépéssel dolgozik.",
            cn: "这一核心工作流将外卖配送和自提订单统一到同一运营流程中，串联订单接收、厨房准备与最终交付或配送，减少重复劳动和漏步骤。"
        },
        createdAt: "2026-09-01T08:15:00Z",
        modifiedAt: "2026-09-01T08:15:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "elvitel", "rendez\u00e9s", "kisz\u00e1ll\u00edt\u00e1s", "m\u0171veletek"]
    },
    {
        id: "kitchen-display-system-feature",
        type: "whats-new",
        title_i18n: {
            en: "Kitchen display system",
            de: "Küchenanzeigesystem",
            hu: "Konyhai kijelző (KDS)",
            cn: "厨房显示系统"
        },
        summary_i18n: {
            en: "The kitchen display keeps orders organized by production line and supports the handoff to service or pickup.",
            de: "Das Küchenanzeigesystem organisiert Bestellungen nach Produktionslinien und unterstützt die Übergabe an Service oder Abholung.",
            hu: "A konyhai kijelző a rendelések szervezését támogatja a különböző készítési pályák szerint, és segíti a szolgáltatás és az elvitel közötti átadást.",
            cn: "厨房显示系统按生产线组织订单，并支持向服务台或自提端的交接。"
        },
        details_i18n: {
            en: "Kitchen staff can see the exact tasks and status of each order. With multiple production lines, tasks can be separated by station, which reduces confusion and speeds up the preparation process.",
            de: "Das Küchenpersonal sieht die genauen Aufgaben und den Status jeder Bestellung. Bei mehreren Produktionslinien können Aufgaben nach Stationen getrennt werden, wodurch Verwirrung reduziert und der Vorbereitungsprozess beschleunigt wird.",
            hu: "A konyhai dolgozók látják a rendelés pontos feladatait és állapotát. Több pályás működésnél a feladatok pályánként elkülöníthetők, így csökken a zavar és gyorsul az elkészítés.",
            cn: "厨房工作人员可查看每个订单的具体任务和状态。在多生产线模式下，可按工位分离任务，从而减少混乱并加快出餐速度。"
        },
        createdAt: "2026-09-01T08:20:00Z",
        modifiedAt: "2026-09-01T08:20:00Z",
        module: "kitchen",
        journey: "production",
        roles: ["admin", "manager", "kitchen"],
        permissions: [],
        tags: ["konyha", "gy\u00e1rt\u00e1s", "kds", "munkafolyamat", "elk\u00e9sz\u00edt\u00e9s"]
    },
    {
        id: "cash-register-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Cash register integration",
            de: "Kassenintegrationen",
            hu: "Pénztárgép-integráció",
            cn: "收银机集成"
        },
        summary_i18n: {
            en: "Connect supported cash registers and payment terminals to the EatWithMe order flow so billing and payment data stay synchronized.",
            de: "Verbinde unterstützte Kassen und Zahlungsterminals mit dem EatWithMe Bestellfluss, damit Abrechnung und Zahlungsdaten synchron bleiben.",
            hu: "A támogatott pénztárgépek és fizetési terminálok csatlakoztathatók az EatWithMe rendelési folyamathoz, hogy a számlázás és a fizetési adatok szinkronban maradjanak.",
            cn: "将支持的收银机和支付终端接入 EatWithMe 订单流程，使结账和支付数据保持同步。"
        },
        details_i18n: {
            en: "This is one of the foundation features for operational efficiency. It reduces double entry and keeps the order, payment, and invoice data consistent with external hardware, including supported fiscal devices and card terminals.",
            de: "Dies ist eine der grundlegenden Funktionen für die Betriebseffizienz. Sie reduziert doppelte Eingaben und hält Bestell-, Zahlungs- und Rechnungsdaten mit externer Hardware konsistent, inklusive unterstützter fiskalischer Geräte und Kartenterminals.",
            hu: "Ez az egyik alapvető funkció a működési hatékonyság növeléséhez. Csökkenti a kézi adatbevitel szükségességét és biztosítja, hogy a rendelés, a fizetés és a számla adatai szinkronban maradjanak a külső hardverrel, beleértve a támogatott fiskális eszközöket és bankkártya terminálokat is.",
            cn: "这是提升运营效率的基础功能之一。它减少重复录入，并确保订单、支付和发票数据与外部硬件保持一致，包括支持的财税设备和刷卡终端。"
        },
        createdAt: "2026-09-01T08:25:00Z",
        modifiedAt: "2026-09-01T08:25:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["fizet\u00e9s", "p\u00e9nzt\u00e1rg\u00e9p", "termin\u00e1l", "integr\u00e1ci\u00f3", "fisk\u00e1lis"]
    },
    {
        id: "inventory-and-recipe-management-feature",
        type: "whats-new",
        title_i18n: {
            en: "Recipe and stock management",
            de: "Rezept- und Bestandsmanagement",
            hu: "Recept- és készletkezelés",
            cn: "配方与库存管理"
        },
        summary_i18n: {
            en: "Track ingredients, consumption, and stock levels from the recipe and inventory modules to support efficient purchasing and operations.",
            de: "Verfolge Zutaten, Verbrauch und Lagerstände über Rezept- und Bestandsmodule, um Einkauf und Betrieb effizient zu steuern.",
            hu: "A recept- és készletmodulokból nyomon követhető a nyersanyag-felhasználás és a készletállapot, ami az ellátás és a működés hatékonyabb irányítását segíti.",
            cn: "通过配方和库存模块跟踪食材、消耗和库存水平，以支持更高效的采购和运营管理。"
        },
        details_i18n: {
            en: "This is the business backbone for stock control. It lets the team calculate ingredient usage from recipes, monitor levels, and adjust purchasing or preparation based on current stock and recent consumption.",
            de: "Dies bildet das geschäftliche Rückgrat für die Bestandskontrolle. Es ermöglicht der Mannschaft, den Zutatenverbrauch aus Rezepten zu berechnen, Bestände zu überwachen und Einkauf oder Vorbereitung anhand aktueller Lagerstände und jüngstem Verbrauch anzupassen.",
            hu: "Ez az üzleti háttér a készletfelügyelethez. Lehetővé teszi, hogy a csapat a receptek alapján számolja a nyersanyagfelhasználást, figyelemmel kísérje a készletállapotot, és a jelenlegi készlet és a korábbi felhasználás alapján igazítsa a beszerzést vagy az előkészítést.",
            cn: "这是库存控制的业务基础。它可让团队根据配方计算食材消耗、监控库存，并基于当前库存和近期消耗情况调整采购或备餐。"
        },
        createdAt: "2026-09-01T08:30:00Z",
        modifiedAt: "2026-09-01T08:30:00Z",
        module: "stockmanagement",
        journey: "inventory_admin",
        roles: ["admin", "manager"],
        permissions: ["stockmanagement.view"],
        tags: ["lelt\u00e1r", "receptek", "k\u00e9szlet", "beszerz\u00e9s", "m\u0171veletek"]
    },
    {
        id: "ntak-cloud-safety-feature",
        type: "whats-new",
        title_i18n: {
            en: "NTAK, cloud operation and support",
            de: "NTAK, Cloud-Betrieb und Support",
            hu: "NTAK, felhőalapú működés és támogatás",
            cn: "NTAK、云端运营与支持"
        },
        summary_i18n: {
            en: "The core service includes NTAK data reporting, cloud hosting, regular backups, user and permission management, and support for urgent issues.",
            de: "Der Kernservice umfasst NTAK-Datenbereitstellung, Cloud-Betrieb, regelmäßige Backups, Benutzer- und Berechtigungsverwaltung sowie Support für dringende Fälle.",
            hu: "Az alapvető szolgáltatás tartalmazza az NTAK-adatszolgáltatást, a felhőalapú működést, a rendszeres mentéseket, a felhasználó- és jogosultságkezelést, valamint a sürgős esetekre nyújtott támogatást.",
            cn: "核心服务包含 NTAK 数据上报、云端托管、定期备份、用户和权限管理，以及对紧急问题的支持。"
        },
        details_i18n: {
            en: "This is the operational base of the product. It helps restaurants stay compliant and resilient while giving them the tools to manage users, access rights, and business-critical support in a centralized system.",
            de: "Dies ist die operative Basis des Produkts. Sie hilft Restaurants, compliant und belastbar zu bleiben und bietet zugleich zentrale Werkzeuge für Benutzerverwaltung, Zugriffsrechte und unternehmenskritischen Support.",
            hu: "Ez a termék működési alapja. Segít az éttermeknek megfelelni a szabályoknak és stabilan működni, miközben központi eszközöket biztosít a felhasználók, a jogosultságok és a kritikus üzleti támogatás kezeléséhez.",
            cn: "这是产品的运营基础。它帮助餐厅保持合规和稳定，并提供统一的用户、权限和关键业务支持管理工具。"
        },
        createdAt: "2026-09-01T08:35:00Z",
        modifiedAt: "2026-09-01T08:35:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["ntak", "felh\u0151", "ment\u00e9s", "t\u00e1mogat\u00e1s", "biztons\u00e1g", "alapok"]
    },
    {
        id: "guest-feedback-and-loyalty-feature",
        type: "whats-new",
        title_i18n: {
            en: "Guest feedback and loyalty",
            de: "Gästefeedback und Treueprogramm",
            hu: "Vendégvisszajelzés és hűség",
            cn: "顾客反馈与忠诚计划"
        },
        summary_i18n: {
            en: "Collect guest feedback with QR codes and support a loyalty point system to reward returning customers.",
            de: "Sammle Gästefeedback per QR-Code und unterstütze ein Treuepunktsystem zur Belohnung wiederkehrender Kunden.",
            hu: "QR-kódokon keresztül gyűjthető a vendégvisszajelzés, és támogatott a hűségpontrendszer a visszatérő vendégek jutalmazásához.",
            cn: "通过二维码收集顾客反馈，并支持忠诚积分系统，以奖励回头客。"
        },
        details_i18n: {
            en: "These tools help the restaurant improve service quality and keep customers engaged over time. QR feedback makes it easy to capture issues and experiences, while loyalty points support repeat visits and retention.",
            de: "Diese Tools helfen dem Restaurant, die Servicequalität zu verbessern und Kunden langfristig zu binden. Das QR-Feedback erleichtert die Erfassung von Problemen und Erfahrungen, während Treuepunkte Wiederbesuche und langfristige Bindung fördern.",
            hu: "Ezek az eszközök segítik az éttermet a szolgáltatás minőségének fejlesztésében és a vendégek hosszú távú elköteleződésében. A QR-kódos visszajelzés könnyen rögzíti a problémákat és az élményeket, a hűségpontok pedig a visszatérő látogatásokat és a megtartást támogatják.",
            cn: "这些工具帮助餐厅提升服务质量并长期留住顾客。二维码反馈可轻松采集问题和体验，而忠诚积分则支持回头客复购与留存。"
        },
        createdAt: "2026-09-01T08:40:00Z",
        modifiedAt: "2026-09-01T08:40:00Z",
        module: "loyalty",
        journey: "guest-experience",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["visszajelz\u00e9s", "h\u0171s\u00e9g", "qr", "megtart\u00e1s", "vend\u00e9g\u00e9lm\u00e9ny"]
    },
    {
        id: "counter-order-management-feature",
        type: "whats-new",
        title_i18n: {
            en: "Counter-based order management",
            de: "Bestellverwaltung am Tresen",
            hu: "Pultos rendeléskezelés",
            cn: "柜台订单管理"
        },
        summary_i18n: {
            en: "Capture orders placed at the counter and manage them in the same system as table service and delivery.",
            de: "Erfasse Bestellungen am Tresen und verwalte sie im selben System wie Tischservice und Lieferung.",
            hu: "A pultnál leadott rendelések ugyanabban a rendszerben kezelhetők, mint az asztalos és házhozszállításos megrendelések.",
            cn: "记录柜台下单，并与桌位服务和配送使用同一系统管理。"
        },
        details_i18n: {
            en: "This workflow keeps counter sales aligned with the rest of the restaurant operation. The same order lifecycle, kitchen handoff, and payment flow can be used across front counter and table service, reducing mistakes and duplicate entry.",
            de: "Dieser Workflow hält die Tresenverkäufe im Einklang mit dem übrigen Restaurantbetrieb. Der gleiche Bestellzyklus, die Küchenübergabe und der Zahlungsfluss können für Tresen und Tischservice gemeinsam genutzt werden, wodurch Fehler und Mehrfacheingaben reduziert werden.",
            hu: "Ez a munkafolyamat a pultos értékesítést a többi éttermi működési egységgel összhangba hozza. Ugyanaz a rendelési életciklus, konyhai átadás és fizetési folyamat alkalmazható a pultos és az asztalos kiszolgálásnál is, így csökken a hibalehetőség és a duplikált adatbevitel.",
            cn: "该流程让柜台销售与餐厅其他运营保持一致，同一订单生命周期、厨房交接和支付流程可以同时用于柜台和桌位服务，减少错误和重复录入。"
        },
        createdAt: "2026-09-01T08:45:00Z",
        modifiedAt: "2026-09-01T08:45:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["pult", "pult", "rendez\u00e9s", "kiszolg\u00e1l\u00e1s", "munkafolyamat"]
    },
    {
        id: "digital-menu-and-price-tag-feature",
        type: "whats-new",
        title_i18n: {
            en: "Digital menu and price tags",
            de: "Digitale Speisekarte und Preisschilder",
            hu: "Digitális étel- és árcímkék",
            cn: "数字菜单和价格标签"
        },
        summary_i18n: {
            en: "Update menu and price labels from the system and manage the content remotely or from mobile devices.",
            de: "Aktualisiere Menü- und Preisschilder aus dem System und verwalte den Inhalt fern oder per Mobilgerät.",
            hu: "A menü és az árjelölések a rendszerből frissíthetők, és a tartalom távolról vagy mobil eszközről is kezelhető.",
            cn: "可从系统中更新菜单和价格标签，并可从远程或移动设备管理内容。"
        },
        details_i18n: {
            en: "This feature is designed for modern restaurants that need fast, centralized updates to their menu content and signage. Adjustments can be done without manual reprinting of printed display materials and help keep the guest-facing presentation current.",
            de: "Diese Funktion ist für moderne Restaurants gedacht, die schnelle, zentrale Aktualisierungen für Menüinhalte und Beschilderung benötigen. Änderungen lassen sich ohne manuelles Nachdrucken von Displays umsetzen und helfen, die Außenwirkung aktuell zu halten.",
            hu: "Ez a funkció azokat az éttermeket támogatja, ahol gyors és központosított menü- és jelölésfrissítésre van szükség. A módosítások manuális újranyomtatás nélkül is elvégezhetők, így a vendégfelület mindig naprakész marad.",
            cn: "该功能适用于需要快速集中更新菜单内容和展示信息的现代餐厅。调整可无需手动重新打印展示材料，帮助保持对外展示内容始终保持最新。"
        },
        createdAt: "2026-09-01T08:50:00Z",
        modifiedAt: "2026-09-01T08:50:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["men\u00fc", "c\u00edmk\u00e9k", "\u00e1rk\u00e9pz\u00e9s", "digit\u00e1lis", "arculat"]
    },
    {
        id: "guest-call-system-feature",
        type: "whats-new",
        title_i18n: {
            en: "Guest call system",
            de: "Gästebestell- und Aufrufsystem",
            hu: "Vendéghívó rendszer",
            cn: "顾客呼叫系统"
        },
        summary_i18n: {
            en: "Alert guests when their order is ready or when service is available, without the staff needing to walk to the table.",
            de: "Benachrichtige Gäste, wenn ihre Bestellung fertig ist oder der Service bereitsteht, ohne dass das Personal zum Tisch laufen muss.",
            hu: "Értesíti a vendéget, ha a rendelés elkészült vagy a kiszolgálás elérhető, anélkül, hogy a személyzetnek oda kellene mennie az asztalhoz.",
            cn: "当订单准备好或服务可用时提醒顾客，无需员工到桌边。"
        },
        details_i18n: {
            en: "The call system is a service tool that improves both the speed and clarity of operations. It allows the team to signal the guest at the correct moment and keeps the service flow more organized during busy periods.",
            de: "Das Aufrufsystem ist ein Service-Tool, das sowohl Geschwindigkeit als auch Klarheit der Abläufe verbessert. Es ermöglicht dem Team, Gäste zum richtigen Zeitpunkt zu benachrichtigen und den Servicefluss in verkehrten Zeiten besser zu organisieren.",
            hu: "A vendéghívó rendszer egy szolgáltatási eszköz, amely javítja az operációk sebességét és átláthatóságát. Lehetővé teszi, hogy a csapat a megfelelő időben jelezze a vendégnek, és a forgalmas időszakokban szervezettebbé tegye a kiszolgálást.",
            cn: "呼叫系统是服务工具，可提高运营速度和透明度。它使团队能够在正确的时机通知顾客，并在忙碌时段保持更有序的服务流程。"
        },
        createdAt: "2026-09-01T08:55:00Z",
        modifiedAt: "2026-09-01T08:55:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["vend\u00e9gh\u00edv\u00e1s", "\u00e9rtes\u00edt\u00e9sek", "kiszolg\u00e1l\u00e1s", "m\u0171veletek", "vend\u00e9g\u00e9lm\u00e9ny"]
    },
    {
        id: "mobile-waiter-feature",
        type: "whats-new",
        title_i18n: {
            en: "Mobile waiter workflow",
            de: "Mobil-Serviceregelung",
            hu: "Mobilpincér munkafolyamat",
            cn: "移动服务员工作流"
        },
        summary_i18n: {
            en: "Waiters can take or update orders directly from their phone or tablet at the table.",
            de: "Servicekräfte können Bestellungen direkt vom Handy oder Tablet am Tisch aufnehmen oder aktualisieren.",
            hu: "A felszolgálók telefonról vagy tabletről, közvetlenül az asztalnál tudják felvenni vagy frissíteni a rendelést.",
            cn: "服务员可直接在桌边使用手机或平板接收或更新订单。"
        },
        details_i18n: {
            en: "The mobile waiter module gives staff a flexible way to manage service at the table without relying on a fixed terminal. It is ideal for restaurants with multi-device operations and a need for fast service.",
            de: "Das Mobil-Servicemodul gibt dem Personal eine flexible Möglichkeit, den Service am Tisch ohne festen Terminal zu verwalten. Es ist ideal für Restaurants mit mehreren Geräten und hohem Servicebedarf.",
            hu: "A mobilpincér modul rugalmas megoldást nyújt a kiszolgálás kezelésére az asztalnál, állandó terminál nélkül. Különösen ideális több eszközös működésnél és gyors kiszolgálási igény esetén.",
            cn: "移动服务员模块为员工提供在桌边进行服务管理的灵活方式，无需依赖固定终端。对于多设备运营和快速服务需求的餐厅尤其适用。"
        },
        createdAt: "2026-09-01T09:00:00Z",
        modifiedAt: "2026-09-01T09:00:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["pinc\u00e9r", "mobil", "asztali kiszolg\u00e1l\u00e1s", "eszk\u00f6z", "munkafolyamat"]
    },
    {
        id: "on-site-qr-ordering-feature",
        type: "whats-new",
        title_i18n: {
            en: "On-site QR ordering",
            de: "Vor-Ort-QR-Bestellung",
            hu: "Helyszíni QR-kódos rendelés",
            cn: "现场二维码点餐"
        },
        summary_i18n: {
            en: "Guests can order directly from their own phones using a QR code at the table or in the venue.",
            de: "Gäste können direkt von ihrem Smartphone aus per QR-Code am Tisch oder im Lokal bestellen.",
            hu: "A vendégek saját telefonjukról közvetlenül rendelhetnek QR-kód segítségével az asztalnál vagy az étteremben.",
            cn: "顾客可通过桌面或现场二维码直接用手机下单。"
        },
        details_i18n: {
            en: "This service allows simple ordering without waiting for a waiter to take the request. It is especially useful for table service, quick lunches, and venues with a high volume of guest traffic.",
            de: "Dieser Service ermöglicht einfaches Bestellen ohne Wartezeit auf einen Kellner. Er ist besonders für Tischservice, Schnellmittagessen und hochfrequentierte Lokale geeignet.",
            hu: "Ez a szolgáltatás egyszerű rendelést tesz lehetővé anélkül, hogy a vendégnek meg kellene várnia a felszolgálót. Különösen hasznos asztalos kiszolgálásnál, gyors ebédidőben és nagy forgalmú helyeken.",
            cn: "该服务让顾客无需等待服务员即可直接下单，尤其适用于桌位服务、快餐时段和高客流餐厅。"
        },
        createdAt: "2026-09-01T09:05:00Z",
        modifiedAt: "2026-09-01T09:05:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["qr", "rendez\u00e9s", "vend\u00e9g", "asztali kiszolg\u00e1l\u00e1s", "contactless"]
    },
    {
        id: "product-variations-feature",
        type: "whats-new",
        title_i18n: {
            en: "Product variations handling",
            de: "Produktvariantenverwaltung",
            hu: "Termékvariációk kezelése",
            cn: "产品变体管理"
        },
        summary_i18n: {
            en: "Manage product variations and more complex guest requests while keeping stock and recipe data in sync.",
            de: "Verwalte Produktvarianten und komplexere Gästeanforderungen und halte Bestands- und Rezeptdaten synchron.",
            hu: "A termékvariációk és a bonyolultabb vendégigények kezelése során a készlet- és receptadatok egyben maradnak.",
            cn: "在处理产品变体和更复杂顾客需求时，保持库存和配方数据同步。"
        },
        details_i18n: {
            en: "The product management layer supports variations, options, and size-related differences. This helps the business represent the real menu structure and keep inventory and stock visibility aligned.",
            de: "Die Produktverwaltung unterstützt Varianten, Optionen und größenbezogene Unterschiede. Dadurch kann das reale Menüspektrum abgebildet und Bestands-/Lagerübersicht korrekt gehalten werden.",
            hu: "A termékkezelő rendszer támogatja a variációkat, opciókat és mérettől függő különbségeket. Ez lehetővé teszi, hogy a valós menüstruktúra pontosan leképezhető legyen, a készlet és a készletláthatóság pedig szinkronban maradjon.",
            cn: "产品管理层支持变体、选项和尺寸差异。这有助于真实反映菜单结构，并保持库存和可见性同步。"
        },
        createdAt: "2026-09-01T09:10:00Z",
        modifiedAt: "2026-09-01T09:10:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["product", "variations", "receptek", "options", "men\u00fc"]
    },
    {
        id: "front-counter-block-printing-feature",
        type: "whats-new",
        title_i18n: {
            en: "Front counter block printing",
            de: "Blockdruck am Tresen",
            hu: "Pultos blokknyomtatás",
            cn: "柜台块打印"
        },
        summary_i18n: {
            en: "Print order blocks for counter operations so the kitchen and the service team can handle orders clearly and in sequence.",
            de: "Drucke Bestellblöcke für Tresenoperationen, damit Küche und Service die Bestellungen klar und in Reihenfolge bearbeiten können.",
            hu: "Blokknyomtatás a pultos műveletekhez, hogy a konyha és a kiszolgálás a rendelések kezelését világosan és sorrendben végezze.",
            cn: "为柜台运营打印订单块，使厨房和服务团队能够清晰按顺序处理订单。"
        },
        details_i18n: {
            en: "This feature supports front-of-house order processing and helps staff manage the flow without mismatch between the order entry point and the output station.",
            de: "Diese Funktion unterstützt die Bestellverarbeitung im Front-of-House und hilft dem Personal, den Ablauf ohne Abweichung zwischen Bestelleingang und Ausgabe zu steuern.",
            hu: "Ez a funkció támogatja a pult előtti rendelések feldolgozását, és segít a csapatnak az áramlás kezelésében anélkül, hogy a rendelésfelvétel és a kimeneti pont közötti eltérés felmerülne.",
            cn: "该功能支持前台订单处理，并帮助员工在订单录入点与输出点之间保持流程一致。"
        },
        createdAt: "2026-09-01T09:15:00Z",
        modifiedAt: "2026-09-01T09:15:00Z",
        module: "kitchen",
        journey: "production",
        roles: ["admin", "manager", "kitchen"],
        permissions: [],
        tags: ["nyomtat\u00e1s", "pult", "konyha", "blocks", "munkafolyamat"]
    },
    {
        id: "courier-management-feature",
        type: "whats-new",
        title_i18n: {
            en: "Courier management and delivery app",
            de: "Fahrerverwaltung und Liefer-App",
            hu: "Futárkezelés és futárapplikáció",
            cn: "配送员管理和配送应用"
        },
        summary_i18n: {
            en: "Assign delivery jobs to couriers manually or let them accept jobs directly in the courier app, including job completion and pickup workflow.",
            de: "Weise Lieferaufträge manuell Fahrern zu oder lasse sie Jobs direkt in der Liefer-App annehmen, inklusive Abschluss und Abholung.",
            hu: "A kiszállítási feladatok manuálisan is kioszthatók a futároknak, vagy a futár is közvetlenül elfogadhatja őket az applikációban, beleértve a befejezést és a felvétel folyamatát.",
            cn: "可手动分配配送任务，也可让配送员直接在配送应用中接单，包括完成任务和取货流程。"
        },
        details_i18n: {
            en: "This powerfully simplifies the last-mile flow. Dispatchers can assign deliveries, couriers can accept and complete them, and the system keeps status updates clear for the kitchen, the staff, and the guests.",
            de: "Dies vereinfacht den Last-Mile-Prozess erheblich. Disponenten können Lieferungen zuweisen, Fahrer sie annehmen und abschließen, und das System hält Statusaktualisierungen für Küche, Personal und Gäste klar.",
            hu: "Ez jelentősen egyszerűsíti az utolsó mérföldes folyamatot. A diszpécser kioszthatja a rendeléseket, a futár elfogadhatja és befejezheti azokat, a rendszer pedig követhető státuszfrissítéseket biztosít a konyha, a személyzet és a vendég számára.",
            cn: "这极大简化了最后一公里配送流程。调度员可分配配送任务，配送员可接单和完成配送，系统保持厨房、员工和顾客之间的状态同步清晰。"
        },
        createdAt: "2026-09-01T09:20:00Z",
        modifiedAt: "2026-09-01T09:20:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "courier", "kisz\u00e1ll\u00edt\u00e1s", "app", "routing"]
    },
    {
        id: "courier-tracking-feature",
        type: "whats-new",
        title_i18n: {
            en: "Courier tracking",
            de: "Fahrerverfolgung",
            hu: "Futárkövetés",
            cn: "配送员跟踪"
        },
        summary_i18n: {
            en: "Track couriers live on a map and monitor each delivery in progress with timestamps and distance data.",
            de: "Verfolge Fahrer live auf einer Karte und überwache jede laufende Lieferung mit Zeitstempeln und Entfernungsdaten.",
            hu: "Élőben követheted a futárok helyzetét a térképen, és nyomon követheted a szállítás állapotát időbélyegek és távolságadatok alapján.",
            cn: "在地图上实时跟踪配送员，并通过时间戳和距离数据监控每次配送进度。"
        },
        details_i18n: {
            en: "The map view shows the route, the current location, and estimated delivery progress. This helps the business provide better service, reduce waiting time, and improve communication with the guest.",
            de: "Die Kartenansicht zeigt Route, aktuellen Standort und geschätzten Lieferfortschritt. Dadurch kann das Geschäft besseren Service liefern, Wartezeiten reduzieren und die Kommunikation mit dem Gast verbessern.",
            hu: "A térkép nézetben látszik az útvonal, a jelenlegi helyzet és a becsült szállítási előrehaladás. Ez segít a szolgáltatás minőségének javításában, a várakozási idő csökkentésében és az ügyfélkommunikáció jobb kialakításában.",
            cn: "地图视图展示路线、当前位置和预计配送进度，这有助于提高服务质量、减少等待时间，并改善与顾客的沟通。"
        },
        createdAt: "2026-09-01T09:25:00Z",
        modifiedAt: "2026-09-01T09:25:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["tracking", "map", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "live-status", "logistics"]
    },
    {
        id: "phone-number-identification-feature",
        type: "whats-new",
        title_i18n: {
            en: "Phone number identification",
            de: "Telefonnummern-Erkennung",
            hu: "Hívószám-azonosítás",
            cn: "电话号码识别"
        },
        summary_i18n: {
            en: "When a call comes in, the customer name, address, and previous orders can appear immediately based on the phone number.",
            de: "Bei einem eingehenden Anruf kann der Kunde anhand der Telefonnummer sofort mit Namen, Adresse und bisherigen Bestellungen identifiziert werden.",
            hu: "Bejövő híváskor a telefonszám alapján azonnal megjelenhet a vevő neve, címe és korábbi rendelései.",
            cn: "来电时，可根据电话号码立即显示客户姓名、地址和历史订单。"
        },
        details_i18n: {
            en: "This makes call-based ordering faster and more accurate. The staff can use existing customer information to improve responsiveness and reduce friction during order intake.",
            de: "Dies macht telefonische Bestellungen schneller und genauer. Das Personal kann vorhandene Kundendaten nutzen, um die Reaktionszeit zu verbessern und den Bestellprozess zu vereinfachen.",
            hu: "Ez gyorsabbá és pontosabbá teszi a telefonos rendelésfelvételt. A kollégák a meglévő ügyféladatokra támaszkodva hatékonyabban és gördülékenyebben tudják kezelni a rendelést.",
            cn: "这使电话下单更快、更准确。员工可利用现有客户信息提高响应速度并减少下单过程中的摩擦。"
        },
        createdAt: "2026-09-01T09:30:00Z",
        modifiedAt: "2026-09-01T09:30:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["call-center", "customer-data", "phone", "order-taking", "crm"]
    },
    {
        id: "weight-scale-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Scale integration for weighted products",
            de: "Waagenintegration für gewogene Produkte",
            hu: "Mérlegintegráció súlyra értékesített termékekhez",
            cn: "称重产品的秤集成"
        },
        summary_i18n: {
            en: "Use a connected scale to capture the exact weight and calculate the correct price for each weighted item.",
            de: "Nutze eine verbundene Waage, um das genaue Gewicht zu erfassen und den korrekten Preis für das gewogene Produkt zu berechnen.",
            hu: "Csatlakoztatott mérleg segítségével pontosan rögzítheted a súlyt, és a rendszer kiszámolja a megfelelő árat a súly alapján értékesített termékhez.",
            cn: "使用连接的秤来获取精确重量，并计算每个称重商品的正确价格。"
        },
        details_i18n: {
            en: "This is especially useful in places where ingredients or beverages are sold by weight. The scale integration reduces manual entry and improves the accuracy of the final billing.",
            de: "Dies ist besonders nützlich für Betriebe, in denen Zutaten oder Getränke nach Gewicht verkauft werden. Die Waagenintegration reduziert manuelle Eingaben und verbessert die Genauigkeit der abschließenden Abrechnung.",
            hu: "Ez különösen hasznos olyan helyeken, ahol az alapanyagok vagy italok tömeg alapján kerülnek értékesítésre. A mérlegintegráció csökkenti a kézi bevitel szükségességét és növeli a végső számlázás pontosságát.",
            cn: "这对于按重量出售食材或饮品的场景尤其有用。秤集成减少手动录入，并提高最终结账的准确性。"
        },
        createdAt: "2026-09-01T09:35:00Z",
        modifiedAt: "2026-09-01T09:35:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["scale", "weight", "hardware", "\u00e1rk\u00e9pz\u00e9s", "lelt\u00e1r"]
    },
    {
        id: "credit-card-terminal-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Card terminal integration",
            de: "Kartenterminal-Integration",
            hu: "Bankkártyaterminál integráció",
            cn: "银行卡终端集成"
        },
        summary_i18n: {
            en: "Connect supported card terminals and payment providers so payment and receipt processing happens in one flow.",
            de: "Verbinde unterstützte Kartenterminals und Zahlungsanbieter, damit Zahlung und Belegverarbeitung in einem Ablauf erfolgen.",
            hu: "Csatlakoztasd a támogatott bankkártya terminálokat és fizetési szolgáltatókat, hogy a fizetés és a nyugta kezelése egy közös folyamatban történjen.",
            cn: "连接支持的银行卡终端和支付服务商，让支付和收据处理在同一流程中完成。"
        },
        details_i18n: {
            en: "This feature is the bridge between the restaurant POS and the card payment provider. It enables the system to pass the amount, complete the transaction, and print or save the fiscal receipt in the correct order.",
            de: "Diese Funktion ist die Brücke zwischen dem Restaurant-POS und dem Kartenzahlungsanbieter. Sie ermöglicht es dem System, den Betrag zu übermitteln, die Transaktion abzuschließen und den fiskalischen Beleg in der richtigen Reihenfolge zu drucken oder zu speichern.",
            hu: "Ez a funkció a restaurant POS és a bankkártya fizetési szolgáltató közötti kapcsolat. Lehetővé teszi a rendszer számára, hogy továbbítsa az összeget, lefuttassa a tranzakciót, majd a megfelelő sorrendben nyomtassa vagy mentse a fiskális nyugtát.",
            cn: "该功能连接餐厅 POS 和银行卡支付服务商，使系统能够传递金额，完成交易，并按正确顺序打印或保存财政收据。"
        },
        createdAt: "2026-09-01T09:40:00Z",
        modifiedAt: "2026-09-01T09:40:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["fizet\u00e9s", "termin\u00e1l", "hardware", "card", "integr\u00e1ci\u00f3"]
    },
    {
        id: "tip-on-second-screen-feature",
        type: "whats-new",
        title_i18n: {
            en: "Tip selection on second display",
            de: "Trinkgeldwahl auf zweiter Anzeige",
            hu: "Borravalóadás másodkijelzőn",
            cn: "第二屏上选择小费"
        },
        summary_i18n: {
            en: "When paying, guests can choose a tip directly on a second display facing them.",
            de: "Beim Bezahlen kann der Gast direkt auf einer zweiten, zum Gast gerichteten Anzeige das Trinkgeld wählen.",
            hu: "A fizetéskor a vendég a felé fordított másodkijelzőn közvetlenül kiválaszthatja a borravalót.",
            cn: "支付时，顾客可直接在朝向他们的第二屏幕上选择小费。"
        },
        details_i18n: {
            en: "This reduces friction at the payment point and encourages a smoother checkout experience while preserving the operational flow for staff and the kitchen.",
            de: "Dies reduziert Reibung am Zahlungspunkt und sorgt für eine flüssigere Kasse, ohne die Betriebsabläufe für Personal und Küche zu beeinträchtigen.",
            hu: "Ez csökkenti a fizetési ponton a frusztrációt és gördülékenyebb fizetési élményt biztosít, miközben a személyzet és a konyha működése is változatlan marad.",
            cn: "这减少了支付环节的摩擦，并带来更顺畅的结账体验，同时保留员工和厨房的运营流程。"
        },
        createdAt: "2026-09-01T09:45:00Z",
        modifiedAt: "2026-09-01T09:45:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["tip", "fizet\u00e9s", "second-screen", "vend\u00e9g\u00e9lm\u00e9ny"]
    },
    {
        id: "automatic-stock-import-feature",
        type: "whats-new",
        title_i18n: {
            en: "Automatic receiving and stock import",
            de: "Automatische Wareneingangsbuchung und Bestandsimport",
            hu: "Automatikus bevételezés és készletimport",
            cn: "自动收货与库存导入"
        },
        summary_i18n: {
            en: "Import supplier invoices and pre-process the products so stock updates become faster and less error-prone.",
            de: "Importiere Lieferantenrechnungen und verarbeite Produkte vor, damit Bestandsaktualisierungen schneller und weniger fehleranfällig werden.",
            hu: "Szállítói számlák importálása és a termékek előfeldolgozása gyorsabbá és hibaszegényebbé teszi a készletfrissítést.",
            cn: "导入供应商发票并预处理产品，使库存更新更快且更少出错。"
        },
        details_i18n: {
            en: "This supports efficient purchasing and inventory control by reducing manual data entry and enabling the system to prepare or automatically import the products listed on the invoice.",
            de: "Dies unterstützt effiziente Beschaffung und Bestandskontrolle, indem manuelle Dateneingaben reduziert und das System die auf der Rechnung aufgeführten Produkte vorbereiten oder automatisiert importieren kann.",
            hu: "Ez a beszerzés és a készletkezelés hatékonyságát támogatja az adatok kézi bevitelének csökkentésével, valamint azzal, hogy a rendszer előkészíti vagy automatikusan importálja a számlán szereplő termékeket.",
            cn: "这有助于提高采购和库存控制效率，减少手动录入，并允许系统准备或自动导入发票中的产品。"
        },
        createdAt: "2026-09-01T09:50:00Z",
        modifiedAt: "2026-09-01T09:50:00Z",
        module: "stockmanagement",
        journey: "inventory_admin",
        roles: ["admin", "manager"],
        permissions: ["stockmanagement.view"],
        tags: ["receiving", "sz\u00e1mla", "import", "k\u00e9szlet", "automation"]
    },
    {
        id: "franchise-multi-branch-feature",
        type: "whats-new",
        title_i18n: {
            en: "Franchise and multi-location management",
            de: "Franchise- und Mehrstandort-Management",
            hu: "Franchise és több üzlet kezelése",
            cn: "连锁和多门店管理"
        },
        summary_i18n: {
            en: "Operate multiple restaurant units from one central system while sharing products, recipes, and performance data.",
            de: "Betreibe mehrere Restaurantstandorte aus einem zentralen System heraus und teile Produkte, Rezepte und Leistungsdaten.",
            hu: "Több vendéglátóegység közösen használhatja a rendszer ugyanazon központi működését, miközben a termékek, receptek és teljesítményadatok közösen kezelhetők.",
            cn: "通过一个中央系统运营多个餐厅门店，并共享产品、配方和绩效数据。"
        },
        details_i18n: {
            en: "This is designed for restaurant groups that need consistent data access across locations. Centralized reporting and product management help compare performance and keep operations coherent across all branches.",
            de: "Dies ist für Restaurantgruppen gedacht, die konsistenten Datenzugriff über mehrere Standorte hinweg benötigen. Zentrale Berichte und Produktverwaltung helfen, die Leistung zu vergleichen und die Abläufe über alle Filialen hinweg konsistent zu halten.",
            hu: "Ez a több helyszínen működő éttermi csoportok számára készült. A központi riportok és termékkezelés segítségével összehasonlítható a teljesítmény, és a működés egységes marad az összes üzletben.",
            cn: "该功能适用于需要跨门店保持一致数据的餐饮集团。集中报表和产品管理有助于比较各门店绩效，并保持运营的一致性。"
        },
        createdAt: "2026-09-01T09:55:00Z",
        modifiedAt: "2026-09-01T09:55:00Z",
        module: "stockmanagement",
        journey: "inventory_admin",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["franchise", "multi-location", "central-data", "comparison", "m\u0171veletek"]
    },
    {
        id: "business-statistics-and-profit-feature",
        type: "whats-new",
        title_i18n: {
            en: "Traffic statistics and profit analysis",
            de: "Verkehrsstatistiken und Gewinnanalyse",
            hu: "Forgalmi statisztikák és haszonelemzés",
            cn: "流量统计和利润分析"
        },
        summary_i18n: {
            en: "Build custom reports and export the results automatically to understand sales, operating performance, and product profitability.",
            de: "Erstelle maßgeschneiderte Berichte und sende sie automatisch, um Umsatz, Betriebsleistung und Produktprofitabilität zu verstehen.",
            hu: "Saját igényekre szabott kimutatásokat készíthetsz, és automatikusan elküldheted azokat a forgalom, működési teljesítmény és termékhaszon elemzéséhez.",
            cn: "创建自定义报表并自动发送结果，以分析销售额、运营表现和产品利润。"
        },
        details_i18n: {
            en: "The business intelligence layer makes it easier to understand revenue, product contribution, and store-level performance over time. It is especially useful for management review, planning, and operational decisions.",
            de: "Die BI-Schicht erleichtert das Verständnis von Umsatz, Produktbeiträgen und Standort-Leistung über die Zeit. Sie ist vor allem für Managementreviews, Planung und operative Entscheidungen nützlich.",
            hu: "A BI modul megkönnyíti az értékesítés, a termékek hozzájárulása és az üzlet szintű teljesítmény időbeli értékelését. Különösen hasznos a vezetői felülvizsgálat, tervezés és döntéshozatal során.",
            cn: "BI 模块使管理层更容易理解收入、产品贡献和门店层面的绩效变化，尤其适用于管理审查、规划和运营决策。"
        },
        createdAt: "2026-09-01T10:00:00Z",
        modifiedAt: "2026-09-01T10:00:00Z",
        module: "reports",
        journey: "insights",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["stats", "bi", "profitability", "jelent\u00e9s", "automation"]
    },
    {
        id: "user-access-management-feature",
        type: "whats-new",
        title_i18n: {
            en: "User and permission management",
            de: "Benutzer- und Berechtigungsverwaltung",
            hu: "Felhasználó- és jogosultságkezelés",
            cn: "用户和权限管理"
        },
        summary_i18n: {
            en: "Manage multiple users, roles, and permissions so the system stays organized and secure.",
            de: "Verwalte mehrere Benutzer, Rollen und Berechtigungen, damit das System organisiert und sicher bleibt.",
            hu: "Több felhasználó, szerep és jogosultság kezelése biztosítja, hogy a rendszer jól szervezett és biztonságos maradjon.",
            cn: "管理多个用户、角色和权限，使系统保持有序和安全。"
        },
        details_i18n: {
            en: "This keeps responsibilities separated and ensures staff can access only the tools relevant to their work. It is a critical operational and compliance feature for multi-person teams.",
            de: "Damit werden Verantwortlichkeiten getrennt und sichergestellt, dass Mitarbeiter nur auf die für ihre Arbeit relevanten Werkzeuge zugreifen. Es ist ein kritisches Betriebs- und Compliance-Feature für Teams mit mehreren Personen.",
            hu: "Ez elkülöníti a feladatok felelősségét és biztosítja, hogy a dolgozók csak a saját munkájukhoz szükséges eszközökhöz férhessenek hozzá. Kritikus működési és megfelelési funkció többfős csapatoknál.",
            cn: "这使职责得到明确分离，并确保员工仅访问与其工作相关的工具。对于多人团队来说，这是关键的运营和合规功能。"
        },
        createdAt: "2026-09-01T10:05:00Z",
        modifiedAt: "2026-09-01T10:05:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["felhaszn\u00e1l\u00f3k", "jogosults\u00e1gok", "roles", "biztons\u00e1g", "hozz\u00e1f\u00e9r\u00e9s-kezel\u00e9s"]
    },
    {
        id: "choice-on-site-ordering-feature",
        type: "whats-new",
        title_i18n: {
            en: "Choice on-site ordering",
            de: "Choice Vor-Ort-Bestellung",
            hu: "Choice helyszíni rendelés",
            cn: "Choice 现场订餐"
        },
        summary_i18n: {
            en: "Guests can place orders directly through the Choice ordering interface using a QR code or a dedicated restaurant channel.",
            de: "Gäste können Bestellungen direkt über die Choice-Bestelloberfläche per QR-Code oder eigenem Restaurantkanal aufgeben.",
            hu: "A vendégek a Choice rendelési felületén közvetlenül leadhatnak rendelést QR-kód vagy saját éttermi csatorna segítségével.",
            cn: "顾客可通过 Choice 订餐界面直接下单，使用 QR 码或专用餐厅渠道。"
        },
        details_i18n: {
            en: "This channel supports local ordering with a guest-facing digital interface for takeaway or home delivery. The order is passed into the EatWithMe workflow so the restaurant can process it without extra manual duplication.",
            de: "Dieser Kanal unterstützt lokale Bestellungen über eine für Gäste gedachte digitale Oberfläche für Abholung oder Lieferung. Die Bestellung wird in den EatWithMe-Workflow übergeben, damit das Restaurant sie ohne zusätzliche manuelle Duplizierung verarbeiten kann.",
            hu: "Ez a csatorna helyszíni vagy közvetlen rendeléshez biztosít digitális vendégfelületet elvitelhez és házhozszállításhoz. A rendelések az EatWithMe folyamathoz kapcsolódnak, így az étterem nélkülözheti a kézi duplikációt.",
            cn: "该渠道支持本地订餐，面向顾客提供外卖或配送的数字界面。订单会传入 EatWithMe 工作流，餐厅无需额外手动重复处理。"
        },
        createdAt: "2026-09-01T10:10:00Z",
        modifiedAt: "2026-09-01T10:10:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager", "cashier"],
        permissions: [],
        tags: ["choice", "rendel\u00e9sek", "qr", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "elvitel"]
    },
    {
        id: "foodora-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Foodora integration",
            de: "Foodora-Integration",
            hu: "Foodora integráció",
            cn: "Foodora 集成"
        },
        summary_i18n: {
            en: "Receive Foodora orders directly in the EatWithMe system, including kitchen blocks and payment data.",
            de: "Empfange Foodora-Bestellungen direkt im EatWithMe-System, inklusive Küchenblöcken und Zahlungsdaten.",
            hu: "A Foodora rendelések közvetlenül az EatWithMe rendszerben érkeznek, beleértve a konyhai blokkokat és a fizetési adatokat.",
            cn: "将 Foodora 订单直接接入 EatWithMe 系统，包括厨房订单块和支付数据。"
        },
        details_i18n: {
            en: "This integration reduces parallel administration and keeps the order handling flow consistent across online and in-house channels. The system handles the data transfer so the kitchen and staff can work from one source of truth.",
            de: "Diese Integration reduziert doppelte Administrationsarbeit und hält den Bestellfluss über Online- und Lokalkanäle konsistent. Das System übernimmt die Datenübertragung, sodass Küche und Personal aus einer zentralen Quelle arbeiten.",
            hu: "Ez az integráció csökkenti a párhuzamos adminisztrációt, és a rendeléskezelés folyamatát egységesebbé teszi az online és helyszíni csatornák között. A rendszer kezeli az adatátvitelt, így a konyha és a személyzet egy közös forrásból dolgozik.",
            cn: "该集成减少并行管理工作，并使在线与堂食渠道的订单处理流程保持一致。系统负责数据传输，让厨房和员工基于同一数据源工作。"
        },
        createdAt: "2026-09-01T10:15:00Z",
        modifiedAt: "2026-09-01T10:15:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["foodora", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "integr\u00e1ci\u00f3", "rendel\u00e9sek", "third-party"]
    },
    {
        id: "wolt-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Wolt integration",
            de: "Wolt-Integration",
            hu: "Wolt integráció",
            cn: "Wolt 集成"
        },
        summary_i18n: {
            en: "Process Wolt orders in the same operational flow as your in-house restaurant orders, including kitchen data and payment information.",
            de: "Verarbeite Wolt-Bestellungen im selben operativen Fluss wie interne Restaurantbestellungen, inklusive Küchen- und Zahlungsdaten.",
            hu: "A Wolt rendelések ugyanabban a működési folyamatban kezelhetők, mint a teljesen belső éttermi rendelések, beleértve a konyhai és fizetési adatokat is.",
            cn: "在与餐厅内单相同的运营流程中处理 Wolt 订单，包括厨房数据和支付信息。"
        },
        details_i18n: {
            en: "The connector reduces duplicate administration while preserving the correct kitchen blocks and payment details for each order. This keeps both the guest experience and the restaurant operations aligned.",
            de: "Der Connector reduziert doppelte Administrationsarbeit und bewahrt gleichzeitig die richtigen Küchenblöcke und Zahlungsdetails für jede Bestellung. Damit bleiben Gästeerlebnis und Restaurantbetrieb konsistent.",
            hu: "Az integráció csökkenti a párhuzamos adminisztrációt, miközben megőrzi az egyes rendelésekhez tartozó konyhai blokkokat és fizetési adatokat. Ez a vendégélményt és az éttermi működést is összhangba hozza.",
            cn: "该连接器减少重复管理，同时保留每笔订单正确的厨房订单块和支付详情，从而保证顾客体验与餐厅运营一致。"
        },
        createdAt: "2026-09-01T10:20:00Z",
        modifiedAt: "2026-09-01T10:20:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["wolt", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "integr\u00e1ci\u00f3", "rendel\u00e9sek", "third-party"]
    },
    {
        id: "falatozz-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Falatozz integration",
            de: "Falatozz-Integration",
            hu: "Falatozz integráció",
            cn: "Falatozz 集成"
        },
        summary_i18n: {
            en: "Manage Falatozz orders in the same EatWithMe order flow, including items, kitchen processing, and payment data.",
            de: "Verwalte Falatozz-Bestellungen im selben EatWithMe-Bestellfluss, inklusive Artikel, Küchenbearbeitung und Zahlungsdaten.",
            hu: "A Falatozz rendelések ugyanabban az EatWithMe rendelési folyamathoz kapcsolódnak, beleértve a tételeket, a konyhai feldolgozást és a fizetési adatokat.",
            cn: "在同一 EatWithMe 订单流中处理 Falatozz 订单，包括商品、厨房流程和支付数据。"
        },
        details_i18n: {
            en: "The integration removes duplicate administrative work and ensures the kitchen receives the order details in a structured, usable format. This helps keep service quality consistent across all online channels.",
            de: "Die Integration entfernt doppelte Verwaltungsarbeit und stellt sicher, dass die Küche die Bestelldetails in einem strukturierten, nutzbaren Format erhält. So bleibt die Servicequalität über alle Online-Kanäle hinweg konsistent.",
            hu: "Az integráció megszünteti a párhuzamos adminisztrációt, és biztosítja, hogy a konyha strukturált, használható formában kapja meg a rendelés adatait. Ez segít a szolgáltatás minőségének konzisztenciájában minden online csatorna esetén.",
            cn: "该集成消除重复管理工作，并确保厨房收到结构化、可用的订单信息。这有助于在所有在线渠道保持一致的服务质量。"
        },
        createdAt: "2026-09-01T10:25:00Z",
        modifiedAt: "2026-09-01T10:25:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["falatozz", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "integr\u00e1ci\u00f3", "rendel\u00e9sek", "third-party"]
    },
    {
        id: "wolt-drive-integration-feature",
        type: "whats-new",
        title_i18n: {
            en: "Wolt Drive integration",
            de: "Wolt-Drive-Integration",
            hu: "Wolt Drive integráció",
            cn: "Wolt Drive 集成"
        },
        summary_i18n: {
            en: "If needed, dispatch a driver through Wolt to cover delivery jobs when your regular courier is unavailable.",
            de: "Wenn nötig, kannst du einen Fahrer über Wolt für Lieferungen einsetzen, wenn dein regulärer Fahrer nicht verfügbar ist.",
            hu: "Szükség esetén Wolt futárt is igénybe vehetsz a kiszállítások lebonyolítására, ha a rendszeres futár nem áll rendelkezésre.",
            cn: "如有需要，可通过 Wolt 调度司机来处理配送任务，当常规配送员不可用时。"
        },
        details_i18n: {
            en: "This keeps the delivery operation resilient. The restaurant has a fallback option for last-minute delivery requests, helping maintain service continuity even when internal capacity is strained.",
            de: "Damit bleibt der Lieferbetrieb resilient. Das Restaurant hat eine Ersatzoption für Last-Minute-Lieferaufträge und kann so die Servicekontinuität aufrechterhalten, selbst wenn interne Kapazitäten ausgelastet sind.",
            hu: "Ez a kiszállítási folyamat rugalmasságát biztosítja. Az étteremnek van egy tartalék lehetősége az utolsó pillanatban érkező kiszállítási igényekre, így a szolgáltatás folyamatos marad még akkor is, ha a belső kapacitás szűk.",
            cn: "这使配送运营更具弹性。餐厅可在临时配送需求下有备选方案，即使内部能力紧张也能保持服务连续性。"
        },
        createdAt: "2026-09-01T10:30:00Z",
        modifiedAt: "2026-09-01T10:30:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["wolt", "drive", "kisz\u00e1ll\u00edt\u00e1s", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "fallback"]
    },
    {
        id: "menu-planning-feature",
        type: "whats-new",
        title_i18n: {
            en: "Menu planning",
            de: "Menüplanung",
            hu: "Menüztetés",
            cn: "菜单规划"
        },
        summary_i18n: {
            en: "Record orders that guests place in advance for several days or a week so purchasing and preparation can be planned more accurately.",
            de: "Erfasse Bestellungen, die Gäste für mehrere Tage oder eine Woche im Voraus platzieren, damit Einkauf und Vorbereitung genauer geplant werden können.",
            hu: "Rögzítsd azokat a rendeléseket, amelyeket a vendégek akár több napra vagy egy hétre előre leadnak, így a beszerzés és az előkészítés pontosabban tervezhető.",
            cn: "记录顾客提前几天或一周下达的订单，以便更准确地规划采购和准备工作。"
        },
        details_i18n: {
            en: "This operational feature supports forecasting and inventory planning for recurring guest orders. It helps reduce waste, align manpower with demand, and improve the pickup or delivery experience.",
            de: "Diese operative Funktion unterstützt Prognosen und Bestandsplanung bei wiederkehrenden Bestellungen. Sie hilft, Verschwendung zu reduzieren, Personal an die Nachfrage anzupassen und die Abhol- oder Liefererfahrung zu verbessern.",
            hu: "Ez az operatív funkció támogatja a visszatérő vendégrendelések előrejelzését és készlettervezését. Segít a pazarlás csökkentésében, a munkaerő és a kereslet összhangjának biztosításában, valamint az elvitel és a kiszállítás élményének javításában.",
            cn: "此运营功能支持周期性顾客订单的预测和库存规划，帮助减少浪费、协调人力与需求，并提升自提或配送体验。"
        },
        createdAt: "2026-09-01T10:35:00Z",
        modifiedAt: "2026-09-01T10:35:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["tervez\u00e9s", "forecasting", "pre-orders", "h\u00e1zhozsz\u00e1ll\u00edt\u00e1s", "elvitel"]
    },
    {
        id: "own-guest-app-feature",
        type: "whats-new",
        title_i18n: {
            en: "Own guest app",
            de: "Eigene Gast-App",
            hu: "Saját vendégapplikáció",
            cn: "自有顾客应用"
        },
        summary_i18n: {
            en: "Offer a branded restaurant app that lets guests order directly and stay connected to the venue beyond the website.",
            de: "Biete eine markenorientierte Restaurant-App an, mit der Gäste direkt bestellen und mit dem Restaurant auch außerhalb der Website in Verbindung bleiben können.",
            hu: "Ajánlj fel egy márkás éttermi applikációt, amelyen a vendégek közvetlenül tudnak rendelni, és a weboldalon kívül is kapcsolatban maradhatnak az étteremmel.",
            cn: "提供品牌化餐厅应用，让顾客可直接下单，并在网站之外继续与餐厅保持联系。"
        },
        details_i18n: {
            en: "The optional app extends the restaurant’s digital channel with a dedicated guest experience. It can support direct ordering, CRM engagement, and a more branded interaction around the restaurant.",
            de: "Die optionale App erweitert den digitalen Kanal des Restaurants um ein eigenes Gast-Erlebnis. Sie kann direkte Bestellungen, CRM-Engagement und eine stärker markenorientierte Interaktion unterstützen.",
            hu: "Az opcionális applikáció kibővíti az étterem digitális csatornáját egy külön vendégélményes megoldással. Támogatja a közvetlen rendelést, a CRM-hez kapcsolódó kommunikációt és a márkásabb vendégkapcsolat kialakítását.",
            cn: "该可选应用扩展餐厅的数字渠道，提供专属顾客体验。它支持直接下单、CRM 互动以及更强品牌化的餐厅交互。"
        },
        createdAt: "2026-09-01T10:40:00Z",
        modifiedAt: "2026-09-01T10:40:00Z",
        module: "sales",
        journey: "orders",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["app", "vend\u00e9g\u00e9lm\u00e9ny", "arculat", "rendez\u00e9s", "mobil"]
    },
    {
        id: "serving-display-feature",
        type: "whats-new",
        title_i18n: {
            en: "Serving display",
            de: "Ausgabebildschirm",
            hu: "Tálaló kijelző",
            cn: "出餐显示屏"
        },
        summary_i18n: {
            en: "Use a serving display to manage ready orders and hand them off to the guest or the service team in a structured way.",
            de: "Nutze einen Ausgabebildschirm, um fertige Bestellungen zu verwalten und sie strukturiert an Gäste oder das Service-Team zu übergeben.",
            hu: "A tálaló kijelző segítségével a kész rendelések kezelhetők és strukturált módon adhatók át a vendégnek vagy a kiszolgáló csapatnak.",
            cn: "使用出餐显示屏管理已准备好的订单，并以结构化方式交接给顾客或服务团队。"
        },
        details_i18n: {
            en: "This screen sits between kitchen production and guest handoff, helping the team release orders in the correct order and reduce confusion at service points.",
            de: "Dieser Bildschirm liegt zwischen Küchenproduktion und Gästeübergabe und hilft dem Team, Bestellungen in der richtigen Reihenfolge freizugeben und Verwirrung an Servicepunkten zu vermeiden.",
            hu: "Ez a kijelző a konyhai készítés és a vendégátadás között helyezkedik el, segít a rendelések helyes sorrendben történő kiadásában és csökkenti a zavart a kiszolgálási pontokon.",
            cn: "该屏幕位于厨房生产与顾客交接之间，帮助团队按正确顺序发出订单，并减少服务点的混乱。"
        },
        createdAt: "2026-09-01T10:45:00Z",
        modifiedAt: "2026-09-01T10:45:00Z",
        module: "kitchen",
        journey: "production",
        roles: ["admin", "manager", "kitchen"],
        permissions: [],
        tags: ["display", "kiszolg\u00e1l\u00e1s", "elvitel", "handoff", "konyha"]
    },
    {
        id: "kitchen-block-printing-feature",
        type: "whats-new",
        title_i18n: {
            en: "Kitchen block printing",
            de: "Küchenblockdruck",
            hu: "Konyhai blokknyomtatás",
            cn: "厨房订单块打印"
        },
        summary_i18n: {
            en: "Print kitchen blocks by production line so each station receives the right tasks and the order flow stays clear.",
            de: "Drucke Küchenblöcke nach Produktionslinie, damit jede Station die richtigen Aufgaben erhält und der Bestellfluss übersichtlich bleibt.",
            hu: "Nyomtass konyhai blokkokat a különböző elkészítési pályák szerint, hogy minden állomás a megfelelő feladatokat kapja és a rendelésfolyam átlátható maradjon.",
            cn: "按生产线打印厨房订单块，让每个工位接收正确任务，订单流程保持清晰。"
        },
        details_i18n: {
            en: "Kitchen staff can print task-specific blocks that help separate the work across lines or stations. This reduces confusion and improves speed without losing order clarity.",
            de: "Das Küchenpersonal kann aufgabenspezifische Blöcke drucken, die die Arbeit über Linien oder Stationen trennen. Das reduziert Verwirrung und erhöht die Geschwindigkeit ohne Qualitätsverlust.",
            hu: "A konyhai dolgozók feladatspecifikus blokkokat tudnak nyomtatni, amelyek segítenek a munka pályák vagy állomások közötti szétválasztásában. Ez csökkenti a zűrzavart és javítja a sebességet a rendelések átláthatósága megőrzése mellett.",
            cn: "厨房员工可打印按任务区分的订单块，帮助按生产线或工位分离工作，从而减少混乱并提高效率，同时保持订单清晰。"
        },
        createdAt: "2026-09-01T10:50:00Z",
        modifiedAt: "2026-09-01T10:50:00Z",
        module: "kitchen",
        journey: "production",
        roles: ["admin", "manager", "kitchen"],
        permissions: [],
        tags: ["konyha", "nyomtat\u00e1s", "blocks", "gy\u00e1rt\u00e1s", "munkafolyamat"]
    },
    {
        id: "multi-station-kitchen-feature",
        type: "whats-new",
        title_i18n: {
            en: "Multi-station kitchen support",
            de: "Mehrlinien-Küchenunterstützung",
            hu: "Több pályás konyha támogatása",
            cn: "多生产线厨房支持"
        },
        summary_i18n: {
            en: "Organize tasks by production line so each station gets the exact items it needs while the kitchen workflow stays synchronized.",
            de: "Organisiere Aufgaben nach Produktionslinien, damit jede Station genau die benötigten Artikel erhält und der Küchenfluss synchron bleibt.",
            hu: "A feladatokat konyhai pályák szerint lehet szervezni, így minden állomás csak a saját feladatait kapja meg, miközben a konyhai folyamat szinkronban marad.",
            cn: "按生产线组织任务，让每个工位接收其所需的具体商品，同时保持厨房流程同步。"
        },
        details_i18n: {
            en: "This is especially useful for larger kitchens with separate prep, grill, or plating areas. The system keeps line ownership visible and ensures each team sees only the items relevant to their station.",
            de: "Das ist besonders nützlich für größere Küchen mit separaten Vorbereitungs-, Grill- oder Anrichtenbereichen. Das System hält die Linienverantwortung sichtbar und stellt sicher, dass jedes Team nur die für seine Station relevanten Artikel sieht.",
            hu: "Ez különösen nagyobb konyhák esetén hasznos, ahol külön előkészítő, grillező vagy tálaló területek vannak. A rendszer láthatóvá teszi a pálya felelősségét, és biztosítja, hogy minden csapat csak a saját állomásához tartozó tételeket látja.",
            cn: "这对具有独立备餐、烧烤或摆盘区域的大型厨房尤为有用。系统保持生产线责任清晰，并确保每个团队仅看到与其工位相关的商品。"
        },
        createdAt: "2026-09-01T10:55:00Z",
        modifiedAt: "2026-09-01T10:55:00Z",
        module: "kitchen",
        journey: "production",
        roles: ["admin", "manager", "kitchen"],
        permissions: [],
        tags: ["konyha", "stations", "gy\u00e1rt\u00e1s", "munkafolyamat", "line-management"]
    },
    {
        id: "choice-qr-payment-feature",
        type: "whats-new",
        title_i18n: {
            en: "Choice QR payment",
            de: "Choice-QR-Zahlung",
            hu: "Choice QR-kódos fizetés",
            cn: "Choice 二维码支付"
        },
        summary_i18n: {
            en: "Use the Choice QR payment solution for on-site guest payments through the restaurant’s own checkout flow.",
            de: "Nutze die Choice-QR-Zahlungslösung für Vor-Ort-Zahlungen im eigenen Checkout-Fluss des Restaurants.",
            hu: "Használd a Choice QR-kódos fizetési megoldását a helyszíni vendégfizetésekhez az étterem saját fizetési folyamán belül.",
            cn: "在餐厅自有结账流程中，使用 Choice 二维码支付方案进行现场顾客付款。"
        },
        details_i18n: {
            en: "This option allows the restaurant to offer guest-facing QR payments using the Choice system while keeping the transaction flow compatible with the EatWithMe service layer.",
            de: "Diese Option ermöglicht es dem Restaurant, gastseitige QR-Zahlungen mit dem Choice-System anzubieten und dabei den Transaktionsfluss mit der EatWithMe-Dienstebene kompatibel zu halten.",
            hu: "Ez az opció lehetővé teszi, hogy az étterem a Choice rendszerrel QR-kódos, vendégfelőli fizetést kínáljon, miközben a tranzakciós folyamat kompatibilis marad az EatWithMe szolgáltatási réteggel.",
            cn: "此选项允许餐厅使用 Choice 系统提供面向顾客的二维码支付，同时保持交易流程与 EatWithMe 服务层兼容。"
        },
        createdAt: "2026-09-01T11:00:00Z",
        modifiedAt: "2026-09-01T11:00:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["choice", "qr", "fizet\u00e9s", "p\u00e9nzt\u00e1r", "vend\u00e9g\u00e9lm\u00e9ny"]
    },
    {
        id: "qerko-qr-payment-feature",
        type: "whats-new",
        title_i18n: {
            en: "Qerko QR payment",
            de: "Qerko-QR-Zahlung",
            hu: "Qerko QR-kódos fizetés",
            cn: "Qerko 二维码支付"
        },
        summary_i18n: {
            en: "Use the Qerko QR payment solution for on-site guest payments and sync the transaction flow with the restaurant system.",
            de: "Nutze die Qerko-QR-Zahlungslösung für Vor-Ort-Zahlungen und synchronisiere den Transaktionsfluss mit dem Restaurant-System.",
            hu: "A Qerko QR-kódos fizetési megoldását használhatod a helyszíni vendégfizetésekhez, miközben a tranzakciós folyamat szinkronban marad az éttermi rendszerrel.",
            cn: "使用 Qerko 二维码支付方案进行现场顾客付款，并与餐厅系统同步交易流程。"
        },
        details_i18n: {
            en: "This payment path supports digital checkout without keeping the guest waiting on a fixed terminal. The solution fits the on-site service flow and keeps the receipt and payment records aligned.",
            de: "Dieser Zahlungsweg unterstützt digitalen Checkout ohne die Gäste an einem festen Terminal warten zu lassen. Die Lösung passt zum Vor-Ort-Servicefluss und hält Beleg- und Zahlungsdatensätze konsistent.",
            hu: "Ez a fizetési útvonal digitális kasszázást támogat anélkül, hogy a vendég hosszasan egy rögzített terminálnál várakozna. A megoldás illeszkedik a helyszíni kiszolgálási folyamathoz és a nyugta- és fizetési nyilvántartásokat is összehangolja.",
            cn: "此支付路径支持数字化结账，而无需顾客长时间等待固定终端。该方案适用于现场服务流程，并保持收据与支付记录一致。"
        },
        createdAt: "2026-09-01T11:05:00Z",
        modifiedAt: "2026-09-01T11:05:00Z",
        module: "administration",
        journey: "administration",
        roles: ["admin", "manager"],
        permissions: [],
        tags: ["qerko", "qr", "fizet\u00e9s", "p\u00e9nzt\u00e1r", "vend\u00e9g\u00e9lm\u00e9ny"]
  }
];
